import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Nav top-level
    dashboard: 'Dashboard',
    myDashboard: 'My Dashboard',
    clockInOut: 'Clock In/Out',
    mobileApp: 'Mobile App',
    sales: 'Sales',
    ops: 'Ops',
    operations: 'Operations',
    finance: 'Finance',
    reports: 'Reports',
    data: 'Data',
    settings: 'Settings',

    // Sales items
    quoteManagement: 'Quote Management',
    quoteApprovalQueue: 'Quote Approval Queue',
    customerPricing: 'Customer Pricing',
    customerPortal: 'Customer Portal',
    previewCustomerPortal: 'Preview Customer Portal',
    staffPortalActivity: 'Staff Portal Activity',

    // Operations items
    supportRequestManagement: 'Support Request Management',
    technicianAssignment: 'Technician Assignment',
    ticketManagement: 'Ticket Management',
    pendingTicketsWorkflow: 'Pending Tickets Workflow',
    equipmentManagement: 'Equipment Management',
    technicianFieldLog: 'Technician Field Log',
    timeEntryApproval: 'Time Entry Approval',
    mobileTechnicianApp: 'Mobile Technician App',
    jobCompletionReport: 'Job Completion Report',

    // Finance items
    invoiceManagement: 'Invoice Management',
    arAgingDashboard: 'AR Aging Dashboard',
    invoiceReconciliation: 'Invoice Reconciliation',
    paymentReminders: 'Payment Reminders',
    rentalRevenue: 'Rental Revenue',
    accountingSync: 'Accounting Sync',

    // Reports items
    allReports: 'All Reports',
    performanceReports: 'Performance Reports',
    savedTemplates: 'Saved Templates',
    analytics: 'Analytics',

    // Data Management items
    dataManagement: 'Data Management',
    organizationManagement: 'Organization Management',
    inventoryTracking: 'Inventory Tracking',
    inventoryAnalytics: 'Inventory Analytics',
    securityAccessControl: 'Security & Access Control',
    emailAnalytics: 'Email Analytics',

    // Settings items
    organizationSettings: 'Organization Settings',
    companyUsers: 'Company Users',
    subscriptionBilling: 'Subscription & Billing',
    subscriptionManagement: 'Subscription Management',
    customDashboardBuilder: 'Custom Dashboard Builder',
    emailTemplates: 'Email Templates',
    taxRateSettings: 'Tax Rate Settings',
    accountSecurity: 'Account Security',
    adminDashboard: 'Admin Dashboard',
    orgDashboard: 'Org Dashboard',
    auditLog: 'Audit Log',
    activityLog: 'Activity Log',
    permissionAudit: 'Permission Audit',
    userRoleManagement: 'User & Role Management',

    // Profile / user menu
    signOut: 'Sign Out',
    logOff: 'Log Off',
    changePassword: 'Change Password',
    language: 'Language',
    languageEnglish: 'English',
    languageSpanish: 'Español',

    // Mobile section labels
    myTimeEntry: 'My Time Entry',
    clockInOutDesc: 'Log your time & mileage',

    // Session
    sessionExpiringSoon: 'Session expiring soon',
    autoSignOut: 'Auto sign-out in {n}s due to inactivity',
    stay: 'Stay',

    // ── Ticket Management – shared labels ──
    tm_createTicket: 'Create New Ticket',
    tm_createTicketDesc: 'Fill in all ticket details for the customer',
    tm_editTicket: 'Edit Service Request',
    tm_filterTickets: 'Filter Tickets',
    tm_reset: 'Reset',
    tm_cancel: 'Cancel',
    tm_save: 'Save Changes',
    tm_saving: 'Saving...',
    tm_creating: 'Creating Ticket...',
    tm_draftSaved: 'Draft auto-saved',
    tm_loadingForm: 'Loading form data...',

    // Create / Edit form fields
    tm_customer: 'Customer',
    tm_selectCustomer: 'Select a customer',
    tm_location: 'Location',
    tm_selectLocation: 'Select a location (optional)',
    tm_locationDesc: 'Link this ticket to a specific customer location',
    tm_addLocation: 'Add Location',
    tm_noLocation: 'No specific location',
    tm_noLocationsFound: 'No locations found for this customer. Use "Add Location" to create one.',
    tm_serviceType: 'Service Type',
    tm_selectServiceType: 'Select service type',
    tm_priority: 'Priority',
    tm_selectPriority: 'Select priority',
    tm_status: 'Status',
    tm_selectStatus: 'Select status',
    tm_scheduledDate: 'Scheduled Date',
    tm_scheduledTime: 'Scheduled Time',
    tm_timezone: 'Timezone',
    tm_estimatedDuration: 'Estimated Duration',
    tm_selectDuration: 'Select estimated duration',
    tm_description: 'Description',
    tm_descriptionPlaceholder: 'Describe the service request, issue, or work to be performed...',
    tm_internalNotes: 'Internal Notes',
    tm_internalNotesPlaceholder: 'Any additional internal notes or instructions...',
    tm_internalNotesHint: 'Optional — visible to staff only',
    tm_assignTechnicians: 'Assign Technicians',
    tm_selectTechnicians: 'Select one or more technicians',
    tm_techniciansDesc: 'Multiple technicians can be assigned',
    tm_salesperson: 'Salesperson',
    tm_selectSalesperson: 'Select salesperson (optional)',
    tm_productsServices: 'Products & Services',
    tm_addProduct: 'Add Product',
    tm_searchProducts: 'Search products...',
    tm_noProductsFound: 'No products found',
    tm_noProductsAdded: 'No products added. Click "Add Product" to search and add items.',
    tm_capitalEquipment: 'Capital Equipment',
    tm_assignEquipment: 'Assign Equipment',
    tm_searchEquipment: 'Search equipment...',
    tm_noEquipmentFound: 'No equipment found',
    tm_noEquipmentAdded: 'No equipment assigned. Click "Assign Equipment" to search and select items.',
    tm_customFields: 'Custom Fields',
    tm_customFieldsDesc: 'Optional fields for tracking additional ticket data.',
    tm_customField1: 'Custom Field 1',
    tm_customField2: 'Custom Field 2',
    tm_customField3: 'Custom Field 3',
    tm_customField4: 'Custom Field 4',
    tm_customField5: 'Custom Field 5',
    tm_enterValue: 'Enter value',
    tm_createTicketBtn: 'Create Ticket',

    // Duration options
    tm_dur1h: '1 hour',
    tm_dur2h: '2 hours',
    tm_dur3h: '3 hours',
    tm_dur4h: '4 hours',
    tm_dur6h: '6 hours',
    tm_dur8h: 'Full day (8 hours)',

    // Ticket Detail Panel
    tm_edit: 'Edit',
    tm_updateStatus: 'Update Status',
    tm_currentStatus: 'Current Status',
    tm_statusMgmt: 'Status Management',
    tm_readyToBegin: 'Ready to Begin?',
    tm_startJobDesc: 'Tap the button below when you begin working on this job. This will change the status to In Progress.',
    tm_startJob: 'Start Job — Mark In Progress',
    tm_jobInProgress: 'Job In Progress',
    tm_completeJobDesc: 'When the work is done, tap below to mark this job complete and send completion alerts.',
    tm_completeJob: 'Complete Job — Send Alerts',
    tm_ticketSummary: 'Ticket Summary',
    tm_customerInfo: 'Customer Information',
    tm_serviceDetails: 'Service Details',
    tm_assignedTechnicians: 'Assigned Technicians',
    tm_noTechniciansAssigned: 'No technicians assigned yet.',
    tm_mileageTracking: 'Mileage Tracking',
    tm_startMileage: 'Start Mileage',
    tm_endMileage: 'End Mileage',
    tm_totalMileage: 'Total Mileage',
    tm_saveMileage: 'Save Mileage',
    tm_saved: 'Saved!',
    tm_mileageError: 'End mileage must be greater than or equal to start mileage.',
    tm_locationAssignment: 'Location Assignment',
    tm_assignLocationLabel: 'Assign Location',
    tm_selectServiceLocation: 'Select a Service Location',
    tm_noLocationAssigned: 'No location assigned',
    tm_saveLocation: 'Save Location',
    tm_addFirstLocation: 'Add First Location',
    tm_noLocationsCustomer: 'No locations found for this customer.',
    tm_serviceHistoryAtLocation: 'Service History at this Location',
    tm_loadingHistory: 'Loading history...',
    tm_noServiceHistory: 'No prior service tickets at this location.',
    tm_scheduleWork: 'Schedule Work',
    tm_date: 'Date',
    tm_time: 'Time',
    tm_statusWillChange: 'Status will automatically change to Scheduled when saved.',
    tm_assignTechniciansLabel: 'Assign Technicians',
    tm_selectTechnicianPlaceholder: 'Select technician',
    tm_name: 'Name',
    tm_email: 'Email',
    tm_phone: 'Phone',
    tm_address: 'Address',
    tm_serviceTypeLabel: 'Service Type',
    tm_serviceRequestedDate: 'Service Requested Date',
    tm_scheduledDateLabel: 'Scheduled Date',
    tm_duration: 'Duration',
    tm_noDate: 'No Date',
    tm_customer_label: 'Customer',
    tm_removeLocation: 'Remove',
    tm_describeRequest: 'Describe the service request...',
    tm_additionalNotes: 'Any additional internal notes...',

    // Tabs
    tm_tabDetails: 'Details',
    tm_tabCommunication: 'Communication',
    tm_tabAttachments: 'Attachments',
    tm_tabHistory: 'History',
    tm_tabSurvey: 'Survey',
    tm_tabSource: 'Source',

    // Convert to Invoice Modal
    tm_convertToInvoice: 'Convert to Invoice',
    tm_taxRate: 'Tax Rate',
    tm_selectTaxRate: 'Select tax rate',
    tm_customRate: 'Enter custom rate %',
    tm_useSavedRate: 'Use saved tax rate',
    tm_enterCustomRate: 'Enter custom rate',
    tm_discountAmount: 'Discount Amount ($)',
    tm_dueDate: 'Due Date',
    tm_notes: 'Notes',
    tm_invoiceNotes: 'Invoice notes...',
    tm_subtotal: 'Subtotal:',
    tm_rentalSubtotal: 'Rental subtotal:',
    tm_discount: 'Discount:',
    tm_tax: 'Tax ({rate}%):',
    tm_totalAmount: 'Total Amount:',
    tm_createInvoice: 'Create Invoice',
    tm_rentalValidationIssues: 'Rental Validation Issues',
    tm_rentalReturnNote: 'Return to the ticket and correct rental item pricing before converting.',
    tm_equipmentRentalItems: 'Equipment Rental Items',
    tm_rentalItem: 'Rental Item',
    tm_days: 'Days',
    tm_ratePerDay: 'Rate/Day',
    tm_total: 'Total',
    tm_rentalTransferNote: 'Rental items transfer to the invoice with their full rental total. Rate/Day × Days = Total.',
    tm_otherTicketItems: 'Other Ticket Items',
    tm_ticketItems: 'Ticket Items',
    tm_item: 'Item',
    tm_qty: 'Qty',
    tm_price: 'Price',
    tm_noProductsWarning: 'This ticket has no products. The invoice will be created with $0 subtotal. You can add items after creation.',
    tm_selectDueDateError: 'Please select a due date.',

    // Job Completion Modal
    tm_completeJobTitle: 'Complete Job',
    tm_completionSummary: 'Completion Summary',
    tm_completionSummaryRequired: 'Completion Summary *',
    tm_completionSummaryPlaceholder: 'Briefly describe what was done to complete this job...',
    tm_additionalNotesOptional: 'Additional Notes',
    tm_additionalNotesOptionalLabel: '(optional)',
    tm_additionalNotesPlaceholder: 'Any follow-up items, parts used, or observations...',
    tm_reportPromptNotice: "After marking complete, you'll be prompted to fill out the Job Completion Report — a detailed record with checklist, photos, and customer signature.",
    tm_sendCompletionAlert: 'Send Completion Alert',
    tm_sendAlertDesc: 'Notify the customer and management team that this job has been completed.',
    tm_markJobComplete: 'Mark Job Complete',
    tm_completing: 'Completing...',
    tm_completionSummaryError: 'Please provide a completion summary before marking the job complete.',

    // Filters
    tm_filterTitle: 'Filter Tickets',
    tm_searchPlaceholder: 'Search by ticket ID or customer...',
    tm_allStatuses: 'All Statuses',
    tm_allPriorities: 'All Priorities',
    tm_allTechnicians: 'All Technicians',
    tm_allLocations: 'All Locations',
    tm_fromDate: 'From Date',
    tm_toDate: 'To Date',
    tm_filterByLocation: 'Filter by location',
    tm_statusPending: 'Pending',
    tm_statusScheduled: 'Scheduled',
    tm_statusInProgress: 'In Progress',
    tm_statusOnHold: 'On Hold',
    tm_statusCompleted: 'Completed',
    tm_statusInvoiced: 'Invoiced',
    tm_statusClosed: 'Closed',
    tm_statusCancelled: 'Cancelled',
    tm_priorityCritical: 'Critical',
    tm_priorityHigh: 'High',
    tm_priorityMedium: 'Medium',
    tm_priorityLow: 'Low',

    // Bulk Assign Modal
    tm_assignTechnicianTitle: 'Assign Technician',
    tm_assignToTickets: 'Assign to {count} selected ticket{plural}',
    tm_assigning: 'Assigning...',
    tm_assign: 'Assign',

    // Add Location Modal
    tm_addNewLocation: 'Add New Location',
    tm_locationName: 'Location Name *',
    tm_locationNamePlaceholder: 'e.g. Well #14A',
    tm_locationId: 'LocationID',
    tm_locationIdPlaceholder: 'e.g. 42-123-45678',
    tm_company: 'Company',
    tm_companyPlaceholder: 'Operating company',
    tm_locationAddress: 'Location / Address',
    tm_locationAddressPlaceholder: 'Enter address or location details',
    tm_locationAddressDesc: 'Type the address or location details',
    tm_contacts: 'Contacts',
    tm_contactName: 'Contact Name',
    tm_contactEmail: 'Email',
    tm_contactPhone: 'Phone',
    tm_addContact: 'Add Contact',
    tm_removeContact: 'Remove',
    tm_saveLocation_btn: 'Save Location',
    tm_saving_location: 'Saving...',
    tm_locationNameRequired: 'Location Name is required.',

    // Custom Fields Modal
    tm_manageServiceTypes: 'Manage Service Types',
    tm_manageStatuses: 'Manage Statuses',
    tm_managePriorities: 'Manage Priorities',
    tm_manageServiceTypesDesc: 'Manage custom service type options for tickets',
    tm_manageStatusesDesc: 'Manage custom status options for tickets',
    tm_managePrioritiesDesc: 'Manage custom priority levels for tickets',
    tm_addNew: 'Add New',
    tm_fieldName: 'Name',
    tm_fieldDescription: 'Description (Optional)',
    tm_fieldDescriptionPlaceholder: 'Enter description',
    tm_existing: 'Existing',
    tm_loading: 'Loading...',
    tm_noFieldsAdded: 'No {type} added yet',
    tm_deleteConfirm: 'Are you sure you want to delete this field?',

    // ── Support Request Management ──
    srm_title: 'Support Request Management',
    srm_createRequest: 'Create Support Request',
    srm_editRequest: 'Edit Support Request',
    srm_manageRequests: 'Manage Requests',
    srm_newRequest: 'New Request',
    srm_requestId: 'Request ID',
    srm_customer: 'Customer',
    srm_selectCustomer: 'Select a customer',
    srm_newCustomer: 'New Customer',
    srm_newCustomerName: 'Customer Name',
    srm_newCustomerEmail: 'Email',
    srm_newCustomerPhone: 'Phone',
    srm_newCustomerAddress: 'Address',
    srm_location: 'Location',
    srm_selectLocation: 'Select a location (optional)',
    srm_locationContact: 'Location Contact',
    srm_selectContact: 'Select a contact (optional)',
    srm_addLocation: 'Add Location',
    srm_serviceType: 'Service Type',
    srm_selectServiceType: 'Select service type',
    srm_priority: 'Priority',
    srm_selectPriority: 'Select priority',
    srm_description: 'Description',
    srm_descriptionPlaceholder: 'Describe the support request in detail...',
    srm_urgentRequest: 'Urgent Request',
    srm_serviceRequestedDate: 'Service Requested Date',
    srm_scheduledDate: 'Scheduled Date',
    srm_timezone: 'Timezone',
    srm_products: 'Products / Services',
    srm_addProduct: 'Add Product',
    srm_searchProducts: 'Search products...',
    srm_noProductsFound: 'No products found',
    srm_noProductsAdded: 'No products added yet.',
    srm_rentalEquipment: 'Rental Equipment',
    srm_addRental: 'Add Rental',
    srm_rentalPeriod: 'Rental Period',
    srm_rentalDays: 'Number of Days',
    srm_daily: 'Daily',
    srm_hourly: 'Hourly',
    srm_monthly: 'Monthly',
    srm_submit: 'Submit Request',
    srm_submitting: 'Submitting...',
    srm_update: 'Update Request',
    srm_cancel: 'Cancel',
    srm_draftSaved: 'Draft auto-saved',
    srm_loadingForm: 'Loading form...',
    srm_taxRate: 'Tax Rate',
    srm_subtotal: 'Subtotal',
    srm_tax: 'Tax',
    srm_total: 'Total',
    // Status options
    srm_statusPending: 'Pending Review',
    srm_statusApproved: 'Approved',
    srm_statusInProgress: 'In Progress',
    srm_statusConverted: 'Converted to Ticket',
    srm_statusRejected: 'Rejected',
    // Priority options
    srm_priorityLow: 'Low',
    srm_priorityMedium: 'Medium',
    srm_priorityHigh: 'High',
    srm_priorityCritical: 'Critical',
    // Table headers
    srm_colRequestId: 'Request ID',
    srm_colCustomer: 'Customer',
    srm_colServiceType: 'Service Type',
    srm_colPriority: 'Priority',
    srm_colStatus: 'Status',
    srm_colServiceDate: 'Service Date',
    srm_colCreated: 'Created',
    srm_colActions: 'Actions',
    srm_showing: 'Showing {count} of {total} requests',
    srm_noRequests: 'No support requests found',
    srm_clearFilters: 'Clear All Filters',
    // Actions
    srm_viewDetails: 'View Details',
    srm_edit: 'Edit',
    srm_convertToTicket: 'Convert to Ticket',
    srm_assignManager: 'Assign Manager',
    // Details modal
    srm_requestDetails: 'Request Details',
    srm_customerInfo: 'Customer Information',
    srm_serviceDetails: 'Service Details',
    srm_managerAssignment: 'Manager Assignment',
    srm_noManagerAssigned: 'No manager assigned',
    srm_assignManagerBtn: 'Assign Manager',
    srm_siteName: 'Site Name',
    srm_locationId: 'Location ID (API)',
    srm_company: 'Company',
    srm_addressLabel: 'Address / Location',
    srm_name: 'Name',
    srm_email: 'Email',
    srm_phone: 'Phone',
    srm_customerId: 'Customer ID',
    srm_productsRequested: 'Products/Services Requested',
    srm_noProducts: 'No products added',
    srm_qty: 'Qty',
    srm_serviceRequested: 'Service Requested',
    srm_priority_label: 'Priority',
    // Conversion modal
    srm_convertTitle: 'Convert to Ticket',
    srm_preConversionChecklist: 'Pre-Conversion Checklist',
    srm_requiredFields: 'Required Fields',
    srm_optionalFields: 'Optional Fields',
    srm_assignTechnicians: 'Assign Technicians',
    srm_selectTechnicians: 'Select technicians',
    srm_scheduledDate_label: 'Scheduled Date',
    srm_scheduledTime: 'Scheduled Time',
    srm_estimatedDuration: 'Estimated Duration',
    srm_notes: 'Notes',
    srm_notesPlaceholder: 'Additional notes for the ticket...',
    srm_expectedServiceDate: 'Service Requested Date',
    srm_converting: 'Converting...',
    srm_confirmConvert: 'Confirm Conversion',
    srm_missingRequired: 'Missing required fields: {fields}',
    // Manager assignment modal
    srm_assignManagerTitle: 'Assign Manager',
    srm_selectManager: 'Select Manager',
    srm_selectManagerPlaceholder: 'Choose a manager to assign',
    srm_managerDescription: 'Manager will oversee this support request',
    srm_managerRole: 'Manager Role',
    srm_managerRoleDesc: 'The assigned manager will be responsible for overseeing this request, assigning technicians, and ensuring timely completion.',
    srm_assigning: 'Assigning...',
    srm_assignManagerConfirm: 'Assign Manager',
    srm_requestSummary: 'Request Summary',
    srm_loadingManagers: 'Loading managers...',
    // Validation messages
    srm_customerRequired: 'Customer is required',
    srm_serviceTypeRequired: 'Service type is required',
    srm_descriptionRequired: 'Description is required',
    srm_priorityRequired: 'Priority is required',

    // ── Quote Management ──
    qm_title: 'Quote Management',
    qm_createQuote: 'Create Quote',
    qm_editQuote: 'Edit Quote',
    qm_customer: 'Customer',
    qm_selectCustomer: 'Select customer',
    qm_addNewCustomer: 'Add New Customer',
    qm_location: 'Location',
    qm_selectLocation: 'Select location (optional)',
    qm_addLocation: 'Add Location',
    qm_locationContact: 'Location Contact',
    qm_selectContact: 'Select contact (optional)',
    qm_salesperson: 'Salesperson',
    qm_selectSalesperson: 'Select salesperson',
    qm_serviceType: 'Service Type',
    qm_selectServiceType: 'Select service type (optional)',
    qm_expectedServiceDate: 'Service Request Date',
    qm_timezone: 'Timezone',
    qm_paymentTerms: 'Payment Terms',
    qm_paymentTermsPlaceholder: 'e.g. Net 30',
    qm_paymentDueDays: 'Payment Due Days',
    qm_depositRequired: 'Deposit Required',
    qm_depositPercentage: 'Deposit Percentage (%)',
    qm_taxRate: 'Tax Rate (%)',
    qm_notes: 'Notes',
    qm_notesPlaceholder: 'Add any notes or special instructions...',
    qm_products: 'Products & Services',
    qm_addProduct: 'Add Product / Kit',
    qm_selectProduct: 'Select product or kit',
    qm_productName: 'Product Name',
    qm_quantity: 'Quantity',
    qm_price: 'Price',
    qm_addItem: 'Add Item',
    qm_removeItem: 'Remove',
    qm_rentalEquipment: 'Rental Equipment',
    qm_addRental: 'Add Rental to Quote',
    qm_rentalPeriod: 'Rental Period',
    qm_rentalQty: 'Number of Days',
    qm_daily: 'Daily',
    qm_hourly: 'Hourly',
    qm_monthly: 'Monthly',
    qm_subtotal: 'Subtotal:',
    qm_tax: 'Tax ({rate}%):',
    qm_total: 'Total:',
    qm_createQuoteBtn: 'Create Quote',
    qm_creating: 'Creating...',
    qm_cancel: 'Cancel',
    qm_loadingForm: 'Loading form...',
    qm_errorLoadingForm: 'Error Loading Form',
    qm_retry: 'Retry',
    qm_templateApplied: 'Template applied',
    qm_documentTemplate: 'Document Template',
    qm_selectTemplate: 'Select template',
    qm_orgDefault: 'Org Default',
    qm_switchToTemplates: 'Browse Templates',
    qm_inventoryWarning: 'Inventory Warning',
    qm_stockAvailable: 'In Stock',
    qm_stockLow: 'Low Stock',
    qm_stockOut: 'Out of Stock',
    qm_notTracked: 'Not Tracked',
    qm_priceListApplied: 'Custom price list applied',
    qm_noProductsAdded: 'No products added. Add at least one product to create a quote.',
    qm_productRequired: 'Product name is required',
    qm_quantityRequired: 'Quantity must be at least 1',
    qm_priceNegative: 'Price cannot be negative',
    qm_fillRequired: 'Please fill in all required fields and add at least one product',
    // Quote table / list
    qm_quoteNumber: 'Quote #',
    qm_customer_col: 'Customer',
    qm_status_col: 'Status',
    qm_amount_col: 'Amount',
    qm_date_col: 'Date',
    qm_actions_col: 'Actions',
    qm_viewDetails: 'View Details',
    qm_edit: 'Edit',
    qm_convert: 'Convert to Invoice',
    qm_delete: 'Delete',
    qm_noQuotes: 'No quotes found',
    // Status options
    qm_statusDraft: 'Draft',
    qm_statusSent: 'Sent',
    qm_statusApproved: 'Approved',
    qm_statusRejected: 'Rejected',
    qm_statusExpired: 'Expired',
    qm_statusConverted: 'Converted',
    // Validation
    qm_customerRequired: 'Customer is required',
    qm_salespersonRequired: 'Salesperson is required',
    qm_productsRequired: 'At least one product is required',

    // ── Invoice Management ──
    im_title: 'Invoice Management',
    im_createInvoice: 'Create Invoice',
    im_customer: 'Customer',
    im_selectCustomer: 'Select customer',
    im_relatedTicket: 'Related Ticket (Optional)',
    im_selectTicket: 'Select ticket',
    im_relatedQuote: 'Related Quote (Optional)',
    im_selectQuote: 'Select quote',
    im_dueDate: 'Due Date',
    im_taxRate: 'Tax Rate (%)',
    im_discountAmount: 'Discount Amount',
    im_notes: 'Notes',
    im_notesPlaceholder: 'Add any notes or special instructions...',
    im_invoiceItems: 'Invoice Items',
    im_product: 'Product',
    im_selectProduct: 'Select product',
    im_description: 'Description',
    im_descriptionPlaceholder: 'Enter description',
    im_quantity: 'Quantity',
    im_unitPrice: 'Unit Price',
    im_discountPercent: 'Discount %',
    im_addItem: 'Add Item',
    im_subtotal: 'Subtotal:',
    im_discount: 'Discount:',
    im_tax: 'Tax ({rate}%):',
    im_totalAmount: 'Total Amount:',
    im_createInvoiceBtn: 'Create Invoice',
    im_creating: 'Creating...',
    im_cancel: 'Cancel',
    im_loadingForm: 'Loading form...',
    im_draftSaved: 'Draft auto-saved',
    im_fixErrors: 'Please fix the following errors:',
    // Invoice detail modal
    im_invoiceDetails: 'Invoice Details',
    im_sendReminder: 'Send Reminder',
    im_sending: 'Sending...',
    im_sendInvoice: 'Send Invoice',
    im_recordPayment: 'Record Payment',
    im_invoiceStatus: 'Invoice Status',
    im_updateStatus: 'Update Status',
    im_statusDraft: 'Draft',
    im_statusSent: 'Sent',
    im_statusPending: 'Pending',
    im_statusPartial: 'Partial',
    im_statusPaid: 'Paid',
    im_statusOverdue: 'Overdue',
    im_statusCancelled: 'Cancelled',
    im_daysOverdue: 'This invoice is {days} days overdue',
    im_customerPortal: 'Customer Portal',
    im_copyLink: 'Copy Link',
    im_portalLinkCopied: 'Portal link copied!',
    im_sourceTicket: 'Source Ticket',
    im_customerInfo: 'Customer Information',
    im_billingAddress: 'Billing Address',
    im_invoiceDate: 'Invoice Date',
    im_dueDateLabel: 'Due Date',
    im_paymentStatus: 'Payment Status',
    im_lineItems: 'Line Items',
    im_item: 'Item',
    im_qty: 'Qty',
    im_price: 'Price',
    im_disc: 'Disc%',
    im_total: 'Total',
    im_paymentHistory: 'Payment History',
    im_noPayments: 'No payments recorded',
    im_reminderHistory: 'Reminder History',
    im_noReminders: 'No reminders sent yet',
    im_downloadPDF: 'Download PDF',
    im_reminderSent: 'Reminder sent successfully!',
    im_reminderFailed: 'Failed to send reminder. Please try again.',
    im_noCustomerEmail: 'No customer email found for this invoice.',
    // Invoice table
    im_invoiceNumber: 'Invoice #',
    im_customer_col: 'Customer',
    im_amount_col: 'Amount',
    im_status_col: 'Status',
    im_date_col: 'Date',
    im_actions_col: 'Actions',
    im_noInvoices: 'No invoices found',
    // Validation
    im_customerRequired: 'Customer is required',
    im_dueDateRequired: 'Due date is required',
    im_itemsRequired: 'At least one item is required',

    // ── Customer Database ──
    cd_title: 'Customer Database',
    cd_addCustomer: 'Add Customer',
    cd_addNewCustomer: 'Add New Customer',
    cd_searchPlaceholder: 'Search by name, company, email, or phone...',
    cd_filters: 'Filters',
    cd_clearFilters: 'Clear All Filters',
    cd_activeFilters: 'Active filters applied',
    cd_noFilters: 'No filters applied',
    cd_status: 'Status',
    cd_allStatus: 'All Status',
    cd_statusActive: 'Active',
    cd_statusInactive: 'Inactive',
    cd_statusPending: 'Pending',
    cd_location: 'Location',
    cd_allLocations: 'All Locations',
    cd_serviceType: 'Service Type',
    cd_allServiceTypes: 'All Service Types',
    // Add customer form
    cd_basicInfo: 'Basic Information',
    cd_fullName: 'Full Name',
    cd_fullNamePlaceholder: 'John Doe',
    cd_emailAddress: 'Email Address',
    cd_emailPlaceholder: 'john.doe@example.com',
    cd_phoneNumber: 'Phone Number',
    cd_phonePlaceholder: '+1 (555) 123-4567',
    cd_company: 'Company',
    cd_companyPlaceholder: 'Acme Corporation',
    cd_locationDetails: 'Location Details',
    cd_cityState: 'City, State',
    cd_cityStatePlaceholder: 'New York, NY',
    cd_accountStatus: 'Account Status',
    cd_serviceAddress: 'Service Address',
    cd_serviceAddressPlaceholder: '123 Main Street, Suite 100',
    cd_profileSetup: 'Customer Profile Setup',
    cd_profileSetupDesc: 'After creating the customer profile, you can add equipment details and service history from the customer details page.',
    cd_customFields: 'Custom Fields',
    cd_customFieldsDesc: 'Optional fields for tracking additional customer data.',
    cd_customField1: 'Custom Field 1',
    cd_customField2: 'Custom Field 2',
    cd_customField3: 'Custom Field 3',
    cd_customField4: 'Custom Field 4',
    cd_customField5: 'Custom Field 5',
    cd_enterValue: 'Enter value',
    cd_cancel: 'Cancel',
    cd_createCustomer: 'Create Customer',
    cd_saving: 'Saving...',
    // Customer profile modal
    cd_customerInfo: 'Customer Information',
    cd_edit: 'Edit',
    cd_save: 'Save',
    cd_tabDetails: 'Details',
    cd_tabLocations: 'Locations',
    cd_tabEquipment: 'Equipment',
    cd_tabHistory: 'Service History',
    cd_billingAddress: 'Billing Address',
    cd_accountStats: 'Account Statistics',
    cd_totalServices: 'Total Services',
    cd_totalRevenue: 'Total Revenue',
    cd_equipmentCount: 'Equipment',
    cd_lastService: 'Last Service',
    cd_noEquipment: 'No equipment on record',
    cd_noServiceHistory: 'No service history available',
    // Table columns
    cd_colName: 'Name',
    cd_colCompany: 'Company',
    cd_colEmail: 'Email',
    cd_colPhone: 'Phone',
    cd_colLocation: 'Location',
    cd_colStatus: 'Status',
    cd_colLastService: 'Last Service',
    cd_colActions: 'Actions',
    cd_viewProfile: 'View Profile',
    cd_editCustomer: 'Edit Customer',
    cd_archiveCustomer: 'Archive Customer',
    cd_noCustomers: 'No customers found',
    cd_showing: 'Showing {count} of {total} customers',
    // Validation messages
    cd_nameRequired: 'Full name is required',
    cd_nameTooShort: 'Name must be at least 2 characters',
    cd_emailRequired: 'Email address is required',
    cd_emailInvalid: 'Please enter a valid email address (e.g., user@example.com)',
    cd_phoneRequired: 'Phone number is required',
    cd_phoneInvalid: 'Please enter a valid phone number',
    cd_locationRequired: 'Location is required',
    cd_addressRequired: 'Service address is required',
    cd_addressTooShort: 'Please enter a complete address',
    cd_saveError: 'Failed to save customer. Please try again.',

    // ── Technician Field Log ──
    tfl_title: 'Field Log',
    tfl_clockIn: 'Clock In',
    tfl_clockOut: 'Clock Out',
    tfl_clockedIn: 'Currently Clocked In',
    tfl_notClockedIn: 'Not Clocked In',
    tfl_startedAt: 'Started at',
    tfl_savedOffline: 'saved offline',
    tfl_offline: 'Offline',
    tfl_offlineTitle: 'Working offline',
    tfl_offlineDesc: 'Your entries are saved locally and will sync automatically when you reconnect.',
    tfl_entrySavedLocally: 'Entry saved locally — syncs automatically on reconnect',
    tfl_tabToday: 'Today',
    tfl_tabHistory: 'History',
    tfl_todayLog: "Today\'s Log",
    tfl_totalTime: 'Total Time',
    tfl_totalMileage: 'Total Mileage',
    tfl_noEntriesToday: 'No entries for today yet',
    tfl_active: 'Active',
    tfl_pendingSync: 'Pending sync',
    tfl_savedOfflineSyncOnReconnect: 'Saved offline · will sync on reconnect',
    tfl_clockedInAt: 'Clocked in at',
    tfl_clockOutQueued: 'Clock-out queued',
    tfl_ticketMileage: 'Ticket & Mileage',
    tfl_clockInToAssociate: 'Clock in to associate a ticket and log mileage.',
    tfl_associatedTicket: 'Associated Ticket',
    tfl_searchTickets: 'Search tickets...',
    tfl_noTicketSelected: 'No ticket selected',
    tfl_ticketSelectionOffline: 'Ticket selection unavailable offline',
    tfl_startMileage: 'Start Mileage',
    tfl_endMileage: 'End Mileage',
    tfl_total: 'Total',
    tfl_notes: 'Notes',
    tfl_addJobNotes: 'Add job notes...',
    tfl_saveDetails: 'Save Details',
    tfl_saveOffline: 'Save Offline',
    tfl_equipment: 'Equipment',
    tfl_active_checkouts: 'active',
    tfl_checkOut: 'Check Out',
    tfl_newCheckOut: 'New Equipment Check-Out',
    tfl_selectEquipment: 'Select Equipment',
    tfl_loadingEquipment: 'Loading equipment...',
    tfl_noEquipmentAvailable: 'No rental equipment available',
    tfl_rentalPeriod: 'Rental Period',
    tfl_hourly: 'Hourly',
    tfl_daily: 'Daily',
    tfl_monthly: 'Monthly',
    tfl_rate: 'Rate',
    tfl_notSet: 'Not set',
    tfl_confirmCheckout: 'Confirm Check-Out',
    tfl_cancel: 'Cancel',
    tfl_checkingOut: 'Checking out...',
    tfl_checkedOut: 'Checked out',
    tfl_elapsed: 'Elapsed',
    tfl_estimatedCost: 'Est. Cost',
    tfl_checkIn: 'Check In',
    tfl_checkingIn: 'Checking in...',
    tfl_noActiveCheckouts: 'No active equipment checkouts',
    tfl_checkoutOfflineNote: 'Equipment checked out (offline — will sync when connected)',
    tfl_checkinOfflineNote: 'Equipment checked in (offline — will sync when connected)',

    // ── Capital Equipment Management ──
    cem_title: 'Capital Equipment Management',
    cem_addEquipment: 'Add Equipment',
    cem_editEquipment: 'Edit Equipment',
    cem_addCapitalEquipment: 'Add Capital Equipment',
    cem_updateEquipmentDetails: 'Update equipment details',
    cem_registerNewAsset: 'Register new capital asset',
    cem_draftAutoSaved: 'Draft auto-saved',
    cem_equipmentName: 'Equipment Name',
    cem_equipmentNamePlaceholder: 'e.g. Hydraulic Pump Unit A',
    cem_serialLicense: 'Serial / License Number',
    cem_serialLicensePlaceholder: 'e.g. SN-2024-00123 or LIC-ABC-456',
    cem_datePurchased: 'Date Purchased',
    cem_purchasePrice: 'Purchase Price',
    cem_fullyAmortizedDate: 'Fully Amortized Date',
    cem_amortizationNote: 'Auto-calculated as 7 years from purchase date. You can override this.',
    cem_rentalEquipment: 'Rental Equipment',
    cem_rentalEquipmentDesc: 'Is this equipment available for rental?',
    cem_rentalRates: 'Rental Rates',
    cem_rentalRatesNote: '(set at least one)',
    cem_perHour: 'Per Hour',
    cem_perDay: 'Per Day',
    cem_perMonth: 'Per Month',
    cem_customFields: 'Custom Fields',
    cem_customField1: 'Custom Field 1',
    cem_customField2: 'Custom Field 2',
    cem_customField3: 'Custom Field 3',
    cem_customField4: 'Custom Field 4',
    cem_customField5: 'Custom Field 5',
    cem_enterValue: 'Enter value',
    cem_cancel: 'Cancel',
    cem_save: 'Save Equipment',
    cem_saving: 'Saving...',
    cem_searchPlaceholder: 'Search by name, ID, or serial/license...',
    cem_allRentalStatus: 'All Rental Status',
    cem_rentalOnly: 'Rental Only',
    cem_nonRentalOnly: 'Non-Rental Only',
    cem_allAmortization: 'All Amortization',
    cem_notYetAmortized: 'Not Yet Amortized',
    cem_fullyAmortized: 'Fully Amortized',
    cem_approaching: 'Approaching (≤2 yrs)',
    cem_conditionGood: 'Good',
    cem_conditionFair: 'Fair',
    cem_conditionPoor: 'Poor',
    cem_conditionOutOfService: 'Out of Service',
    cem_statusRental: 'Rental',
    cem_statusNonRental: 'Non-Rental',
    cem_tabDetails: 'Details',
    cem_tabMaintenance: 'Maintenance',
    cem_tabRentalHistory: 'Rental History',
    cem_deleteConfirm: 'Are you sure you want to delete this equipment? This action cannot be undone.',
    cem_delete: 'Delete',
    cem_deleting: 'Deleting...',
    cem_edit: 'Edit',
    // Validation
    cem_nameRequired: 'Equipment name is required',
    cem_datePurchasedRequired: 'Purchase date is required',
    cem_purchasePriceRequired: 'Purchase price is required',
    cem_amortizationDateRequired: 'Amortization end date is required',
    cem_rentalRateRequired: 'At least one rental rate is required for rental equipment',

    // ── Inventory Tracking ──
    inv_title: 'Inventory Tracking',
    inv_searchPlaceholder: 'Search products...',
    inv_allCategories: 'All Categories',
    inv_allStatuses: 'All Statuses',
    inv_allLocations: 'All Locations',
    inv_statusInStock: 'In Stock',
    inv_statusLowStock: 'Low Stock',
    inv_statusOutOfStock: 'Out of Stock',
    inv_colProduct: 'Product',
    inv_colSku: 'SKU',
    inv_colCategory: 'Category',
    inv_colQuantity: 'Quantity',
    inv_colReorderLevel: 'Reorder Level',
    inv_colStatus: 'Status',
    inv_colLastRestocked: 'Last Restocked',
    inv_colActions: 'Actions',
    inv_adjust: 'Adjust',
    inv_noProducts: 'No trackable products found',
    inv_never: 'Never',
    // Stock Adjustment Modal
    inv_stockAdjustment: 'Stock Adjustment',
    inv_transactionType: 'Transaction Type',
    inv_stockIn: 'Stock In (Add)',
    inv_stockOut: 'Stock Out (Remove)',
    inv_adjustment: 'Adjustment',
    inv_transfer: 'Transfer',
    inv_reorder: 'Reorder',
    inv_quantity: 'Quantity',
    inv_quantityPlaceholder: 'Enter quantity',
    inv_quantityAddDesc: 'Enter quantity to add',
    inv_quantityRemoveDesc: 'Enter positive number to remove from stock',
    inv_serviceLocation: 'Service Location',
    inv_selectLocation: 'Select a service location',
    inv_noLocationsOnFile: 'No service locations on file.',
    inv_addViaCustomers: 'Add via Customers',
    inv_loadingLocations: 'Loading locations...',
    inv_assignedTechnician: 'Assigned Technician',
    inv_technicianPlaceholder: 'Technician name (optional)',
    inv_notes: 'Notes',
    inv_notesPlaceholder: 'Add notes about this adjustment...',
    inv_currentStock: 'Current Stock',
    inv_reorderLevel: 'Reorder Level',
    inv_status: 'Status',
    inv_cancel: 'Cancel',
    inv_saveAdjustment: 'Save Adjustment',
    inv_saving: 'Saving...',
    // Validation
    inv_quantityRequired: 'Quantity change is required and must not be zero',
    inv_quantityInvalid: 'Quantity must be a valid number',
    inv_locationRequired: 'Service Location is required',
    inv_cannotRemove: 'Cannot remove {qty} items. Only {available} available.',

    // ── Personnel Management ──
    pm_title: 'Personnel Management',
    pm_addPersonnel: 'Add Personnel',
    pm_searchPlaceholder: 'Search by name, email, or phone...',
    pm_filterByStatus: 'Filter by Status',
    pm_filterBySkill: 'Filter by Skill',
    pm_allStatus: 'All Status',
    pm_statusAvailable: 'Available',
    pm_statusOnJob: 'On Job',
    pm_statusOffDuty: 'Off Duty',
    pm_allSkills: 'All Skills',
    pm_selectAll: 'Select All',
    pm_name: 'Full Name',
    pm_email: 'Email Address',
    pm_phone: 'Phone Number',
    pm_role: 'Role',
    pm_status: 'Status',
    pm_location: 'Location',
    pm_emergencyContact: 'Emergency Contact',
    pm_emergencyPhone: 'Emergency Phone',
    pm_skills: 'Skills',
    pm_skillsPlaceholder: 'e.g. HVAC, Electrical (comma-separated)',
    pm_equipment: 'Equipment',
    pm_equipmentPlaceholder: 'e.g. Drill, Multimeter (comma-separated)',
    pm_cancel: 'Cancel',
    pm_addMember: 'Add Member',
    pm_adding: 'Adding...',
    pm_edit: 'Edit',
    pm_save: 'Save',
    pm_saving: 'Saving...',
    pm_delete: 'Delete',
    pm_deleting: 'Deleting...',
    pm_tabInfo: 'Info',
    pm_tabTimeLog: 'Time Log',
    pm_contactInfo: 'Contact Information',
    pm_jobsCompleted: 'Jobs Completed',
    pm_rating: 'Rating',
    pm_addSkill: 'Add Skill',
    pm_addEquipment: 'Add Equipment',
    pm_noSkills: 'No skills listed',
    pm_noEquipment: 'No equipment listed',
    pm_deleteConfirm: 'Are you sure you want to remove this person from the team?',
    // Validation
    pm_nameRequired: 'Full name is required',
    pm_nameTooShort: 'Name must be at least 2 characters',
    pm_emailRequired: 'Email address is required',
    pm_emailInvalid: 'Please enter a valid email address',
    pm_phoneRequired: 'Phone number is required',
    pm_phoneInvalid: 'Please enter a valid phone number',
    pm_roleRequired: 'Role is required',
    pm_emergencyPhoneInvalid: 'Please enter a valid phone number',
    pm_fixErrors: 'Please fix the errors below before submitting.',

    // ── Technician Assignment ──
    ta_title: 'Technician Assignment',
    ta_subtitle: 'Assign qualified technicians to service tickets and manage schedules',
    ta_assignments: 'Assignments',
    ta_analytics: 'Analytics',
    ta_integrityCheck: 'Integrity Check',
    ta_technicians: 'Technicians',
    ta_calendar: 'Calendar',
    ta_filters: 'Filters',
    ta_resetAll: 'Reset All',
    ta_searchTechnicians: 'Search technicians...',
    ta_status: 'Status',
    ta_allStatus: 'All Status',
    ta_available: 'Available',
    ta_busy: 'Busy',
    ta_offline: 'Offline',
    ta_sortBy: 'Sort By',
    ta_sortName: 'Name (A-Z)',
    ta_sortRating: 'Rating (Highest)',
    ta_locationFilters: 'Location Filters',
    ta_customer: 'Customer',
    ta_allCustomers: 'All Customers',
    ta_locationName: 'Location Name',
    ta_allLocationNames: 'All Location Names',
    ta_showAvailableToday: 'Show only technicians available today',
    ta_availableTechnicians: 'Available Technicians',
    ta_loading: 'Loading...',
    ta_noTechniciansMatch: 'No technicians match the filters',
    ta_reset: 'Reset',
    ta_todaysAssignments: 'Today\'s Assignments',
    ta_createAssignment: 'Create Assignment',
    ta_createAssignmentCount: 'Create Assignment ({count})',
    ta_selectedTechnicians: '{count} technician{plural} selected{plural}',
    ta_primary: 'Primary',
    ta_setPrimary: 'Set as Primary',
    ta_clearSelection: 'Clear Selection',
    ta_filteringByLocation: 'Filtering by location:',
    ta_activeSkillFilters: 'Active Skill Filters',
    ta_clearAllSkills: 'Clear All Skills',
    ta_incompleteTickets: 'Incomplete Tickets',
    ta_noTickets: 'No incomplete tickets',
    ta_assignTicket: 'Assign Ticket',
    ta_viewLocation: 'View Location',
    // Assignment Form
    ta_formTitle: 'Create Assignment',
    ta_draftAutoSaved: 'Draft auto-saved',
    ta_selectedTechniciansLabel: 'Selected Technicians',
    ta_selectTicket: 'Select Ticket',
    ta_searchSelectTicket: 'Search and select ticket...',
    ta_scheduledDate: 'Scheduled Date',
    ta_scheduledTime: 'Scheduled Time',
    ta_timezone: 'Timezone',
    ta_estimatedDuration: 'Estimated Duration',
    ta_priorityLevel: 'Priority Level',
    ta_priorityLow: 'Low Priority',
    ta_priorityMedium: 'Medium Priority',
    ta_priorityHigh: 'High Priority',
    ta_priorityUrgent: 'Urgent',
    ta_dur1h: '1 hour',
    ta_dur2h: '2 hours',
    ta_dur3h: '3 hours',
    ta_dur4h: '4 hours',
    ta_dur6h: '6 hours',
    ta_dur8h: '8 hours',
    ta_assignmentNotes: 'Assignment Notes',
    ta_notesPlaceholder: 'Add special instructions or notes...',
    ta_notesDesc: 'Optional notes for the technician team',
    ta_cancel: 'Cancel',
    ta_createAssignmentBtn: 'Create Assignment',
    // Assignment Details
    ta_assignmentDetails: 'Assignment Details',
    ta_scheduleInfo: 'Schedule Info',
    ta_date: 'Date',
    ta_time: 'Time',
    ta_duration: 'Duration',
    ta_location: 'Location',
    ta_hours: 'hours',
    ta_assignedTechnicians: 'Assigned Technicians',
    ta_assignmentNotesSec: 'Assignment Notes',
    ta_editAssignment: 'Edit Assignment',
    ta_cancelAssignment: 'Cancel Assignment',
    ta_markCompleted: 'Mark as Completed',
    ta_ticketId: 'Ticket ID',
    ta_priority: 'Priority',
    // Bulk Assign Modal
    ta_bulkTitle: 'Bulk Technician Assignment',
    ta_bulkSubtitle: 'Assigning {count} ticket{plural} to a technician',
    ta_selectedTickets: 'Selected Tickets',
    ta_schedule: 'Schedule (Optional)',
    ta_bulkDate: 'Date',
    ta_bulkTime: 'Time',
    ta_bulkDuration: 'Duration (hrs)',
    ta_conflictDetected: 'Schedule Conflict Detected',
    ta_conflictDesc: '{name} already has {count} assignment{plural} that overlaps with this schedule:',
    ta_conflictNote: 'You can continue, but this may cause a double booking.',
    ta_selectTechnician: 'Select Technician',
    ta_searchTechniciansPlaceholder: 'Search technicians...',
    ta_noTechniciansFound: 'No technicians found',
    ta_conflict: 'Conflict',
    ta_activeJobs: 'active jobs',
    ta_assignBtn: 'Assign',
    ta_assigning: 'Assigning...',
    // Analytics
    ta_totalAssignments: 'Total Assignments',
    ta_allTime: 'All Time',
    ta_completed: 'Completed',
    ta_finishedJobs: 'Jobs Finished',
    ta_active: 'Active',
    ta_inProgress: 'In Progress',
    ta_avgRating: 'Average Rating',
    ta_technicianWorkload: 'Technician Workload',
    ta_statusBreakdown: 'Status Breakdown',
    ta_assignmentsByLocation: 'Assignments by Well Location',
    ta_technicianPerformance: 'Technician Performance',
    ta_technicianCol: 'Technician',
    ta_totalCol: 'Total',
    ta_activeCol: 'Active',
    ta_completedCol: 'Completed',
    ta_urgentCol: 'Urgent',
    ta_ratingCol: 'Rating',
    ta_statusCol: 'Status',
    ta_noAssignmentData: 'No assignment data yet',
    ta_noData: 'No data yet',

    // ── Quote Approval Queue ──
    qaq_title: 'Customer Quote Responses',
    qaq_subtitle: 'Track customer acceptance and rejection responses from the Quote Portal',
    qaq_refresh: 'Refresh',
    // Stats bar
    qaq_total: 'Total Sent',
    qaq_accepted: 'Accepted',
    qaq_rejected: 'Rejected',
    qaq_pending: 'Pending',
    qaq_signed: 'Signed',
    qaq_respondedToday: 'Responded Today',
    qaq_acceptedValue: 'Accepted Value',
    // Filter panel
    qaq_search: 'Search',
    qaq_searchPlaceholder: 'Quote #, name, or customer email...',
    qaq_response: 'Response',
    qaq_allResponses: 'All Responses',
    qaq_awaitingResponse: 'Awaiting Response',
    qaq_acceptedFilter: 'Accepted',
    qaq_rejectedFilter: 'Rejected',
    qaq_salesperson: 'Salesperson',
    qaq_allSalespersons: 'All',
    qaq_dateFrom: 'From',
    qaq_dateTo: 'To',
    qaq_clear: 'Clear',
    // Table headers
    qaq_quoteNum: 'Quote #',
    qaq_customer: 'Customer',
    qaq_value: 'Value',
    qaq_salespersonCol: 'Salesperson',
    qaq_customerResponse: 'Customer Response',
    qaq_responded: 'Responded',
    qaq_signedBy: 'Signed By',
    qaq_sent: 'Sent',
    qaq_expires: 'Expires',
    qaq_details: 'Details',
    // Response badges
    qaq_badgeAccepted: 'Accepted',
    qaq_badgeRejected: 'Rejected',
    qaq_badgePending: 'Awaiting Response',
    qaq_quoteExpired: 'Quote expired',
    // Expanded row
    qaq_customerNotes: 'Customer Notes',
    qaq_noNotes: 'No notes provided',
    qaq_signature: 'Signature',
    qaq_signedByLabel: 'Signed by:',
    qaq_method: 'Method:',
    qaq_signedAt: 'Signed at:',
    qaq_notYetSigned: 'Not yet signed',
    qaq_quoteItems: 'Quote Items',
    qaq_moreItems: '+{count} more items',
    qaq_noItems: 'No items',
    qaq_noResponses: 'No responses found',
    qaq_noResponsesDesc: 'No quotes with links to the customer portal match your current filters.',
    // Detail modal
    qaq_quoteLabel: 'Quote',
    qaq_acceptedByCustomer: 'Accepted by Customer',
    qaq_rejectedByCustomer: 'Rejected by Customer',
    qaq_awaitingCustomer: 'Awaiting Customer Response',
    qaq_quoteValue: 'Quote Value',
    qaq_responseDate: 'Response Date',
    qaq_signatureCaptured: 'Customer Signature Captured',
    qaq_signatureMethod: 'Signature Method',
    qaq_quoteDetails: 'Quote Details',
    qaq_salespersonLabel: 'Salesperson',
    qaq_created: 'Created',
    qaq_expiresLabel: 'Expires',
    qaq_portalLink: 'Portal Link',
    qaq_viewPortal: 'View portal',
    qaq_lineItems: 'Line Items',
    qaq_item: 'Item',
    qaq_qty: 'Qty',
    qaq_unit: 'Unit',
    qaq_total: 'Total',
    qaq_close: 'Close',
    // Approval action modal
    qaq_approveTitle: 'Approve Quote',
    qaq_rejectTitle: 'Reject Quote',
    qaq_changesTitle: 'Request Changes',
    qaq_approveBtn: 'Approve Quote',
    qaq_rejectBtn: 'Reject Quote',
    qaq_changesBtn: 'Send Change Request',
    qaq_approveDesc: 'Approve quote {num} for {customer}. The salesperson will be notified and the quote will be marked as Approved.',
    qaq_rejectDesc: 'Reject quote {num}. The salesperson will be notified with the reason.',
    qaq_changesDesc: 'Request changes to quote {num}. The salesperson will be notified and the quote will return to Draft.',
    qaq_commentRequired: 'Comment (Required)',
    qaq_commentOptional: 'Comment (Optional)',
    qaq_approvePlaceholder: 'Optional approval notes...',
    qaq_rejectPlaceholder: 'Reason for rejection (required)...',
    qaq_changesPlaceholder: 'Describe necessary changes (required)...',
    qaq_cancelBtn: 'Cancel',
    qaq_customerLabel: 'Customer',
    qaq_totalValue: 'Total Value',
    qaq_provideComment: 'Please provide a comment.',
    // Approval queue table
    qaq_queueClear: 'Queue is clear!',
    qaq_queueClearDesc: 'No quotes pending approval.',
    qaq_stage: 'Stage',
    qaq_approvalChain: 'Approval Chain',
    qaq_waiting: 'Waiting',
    qaq_actions: 'Actions',
    qaq_stagePending: 'Pending',
    qaq_stageApproved: 'Approved',
    qaq_stageRejected: 'Rejected',
    qaq_stageChanges: 'Changes Sol.',
    qaq_stageEscalated: 'Escalated',
    qaq_stageExpired: 'Expired',
    qaq_quoteDetails2: 'Quote Details',
    qaq_serviceRequestedDate: 'Service Requested Date:',
    qaq_notes: 'Notes:',
    qaq_currentApprover: 'Current Approver:',
    qaq_approvalHistory: 'Approval History',
    qaq_noHistory: 'No history yet',
    // Filter panel (approval)
    qaq_filterStage: 'Approval Stage',
    qaq_allStages: 'All Stages',
    qaq_filterSalesperson: 'Salesperson',
    qaq_allSalespersonsFilter: 'All Salespersons',
    qaq_quoteValue2: 'Quote Value',
    qaq_anyValue: 'Any Value',
    qaq_under1k: 'Less than $1,000',
    qaq_1kTo10k: '$1,000 – $10,000',
    qaq_10kTo50k: '$10,000 – $50,000',
    qaq_over50k: 'More than $50,000',
    qaq_activeFilters: 'Active',
    qaq_clearFilters: 'Clear',

    // ── Reports & Export Center ──
    rep_title: 'Reports',
    rep_subtitle: 'Manage report templates and view performance analytics',
    rep_exportExcel: 'Export Excel',
    rep_exporting: 'Exporting...',
    rep_exportCSV: 'Export CSV',
    rep_downloadPDF: 'Download PDF',
    rep_savedTemplates: 'Saved Templates',
    rep_newTemplate: 'New Template',
    rep_cancelNewTemplate: 'Cancel',
    rep_createNewTemplate: 'Create New Template',
    rep_templateName: 'Template Name *',
    rep_templateDesc: 'Description (optional)',
    rep_saveTemplate: 'Save Template',
    rep_noSavedTemplates: 'No saved templates yet',
    rep_noSavedTemplatesHint: 'Click "New Template" to create one',
    rep_run: 'Run',
    rep_confirm: 'Confirm',
    rep_cancel: 'Cancel',
    rep_reportTemplates: 'Report Templates',
    rep_categoryAll: 'All',
    rep_categoryPerformance: 'Performance',
    rep_categoryOperations: 'Operations',
    rep_categoryFinance: 'Finance',
    rep_categoryInventory: 'Inventory',
    rep_categorySupport: 'Support',
    rep_categorySales: 'Sales',
    rep_downloadReport: 'Download Report',
    rep_saveToTemplates: 'Save to Templates',
    rep_performanceAnalytics: 'Performance Analytics',
    rep_tabPerformance: 'Performance',
    rep_tabTickets: 'Tickets',
    rep_tabRevenue: 'Revenue',
    rep_totalTicketsClosed: 'Total Tickets Closed',
    rep_avgResolutionTime: 'Average Resolution Time',
    rep_openInvoices: 'Open Invoices',
    rep_activeTechnicians: 'Active Technicians',
    rep_accessRestricted: 'Access Restricted',
    rep_noPermission: 'You do not have permission to view Reports.',
    rep_quoteConversionAnalytics: 'Quote Conversion Analytics',
    rep_equipmentUtilization: 'Equipment Utilization',

    // ── Superadmin Dashboard ──
    sad_badge: 'SUPERADMIN',
    sad_title: 'System Overview',
    sad_subtitle: 'Cross-organization platform management',
    sad_manageOrgs: 'Manage Organizations',
    sad_totalOrgs: 'Total Organizations',
    sad_activeOrgs: 'Active Organizations',
    sad_totalUsers: 'Total Users',
    sad_newOrgsMonth: 'New Orgs This Month',
    sad_recentOrgs: 'Recent Organizations',
    sad_viewAll: 'View all',
    sad_noOrgs: 'No organizations found',
    sad_quickActions: 'Quick Actions',
    sad_addOrg: 'Add Organization',
    sad_userRoleMgmt: 'User & Role Management',
    sad_auditLog: 'Audit Log',
    sad_orgSettings: 'Organization Settings',
    sad_orgIsolationAudit: 'Org Isolation Audit',
    sad_systemHealth: 'System Health',
    sad_database: 'Database',
    sad_authService: 'Auth Service',
    sad_storage: 'Storage',
    sad_edgeFunctions: 'Edge Functions',
    sad_operational: 'Operational',
    sad_allTime: 'all time',
    sad_currentlyActive: 'currently active',
    sad_acrossAllOrgs: 'across all orgs',
    sad_thisMonth: 'this month',

    // ── Org Admin Dashboard ──
    oad_badge: 'ORG ADMIN',
    oad_title: 'Organization Dashboard',
    oad_subtitle: 'Team performance and organizational oversight',
    oad_quoteMgmt: 'Quote Management',
    oad_ticketMgmt: 'Ticket Management',
    oad_addQuote: 'Add Quote',
    oad_createTicket: 'Create New Ticket',
    oad_openTickets: 'Open Tickets',
    oad_needAttention: 'need attention',
    oad_pendingApprovals: 'Pending Approvals',
    oad_awaitingReview: 'awaiting review',
    oad_sales: 'Sales',
    oad_viewAllQuotes: 'View all quotes',
    oad_quotesAwaiting: 'Quotes Awaiting Response',
    oad_quotesAccepted: 'Quotes Accepted',
    oad_valueAccepted: 'Value of Accepted Quotes',
    oad_quotesRejected: 'Rejected Quotes',
    oad_ops: 'Ops',
    oad_viewAllTickets: 'View all tickets',
    oad_openSupportReqs: 'Open Support Requests',
    oad_ticketsPending: 'Tickets Pending',
    oad_ticketsOpen: 'Tickets Open',
    oad_upcomingSchedule: 'Upcoming Schedule',
    oad_ticketNum: 'Ticket #',
    oad_company: 'Company',
    oad_location: 'Location',
    oad_scheduledDateTime: 'Scheduled Date & Time',
    oad_technicians: 'Technicians',
    oad_status: 'Status',
    oad_noUpcoming: 'No upcoming scheduled, pending, or active jobs from today forward.',
    oad_pendingApprovalsBanner: 'Pending Approvals',
    oad_quotesAwaitingApproval: '{n} quote{s} awaiting your approval',
    oad_reviewNow: 'Review now',

    // ── Technician Dashboard ──
    tdb_badge: 'TECHNICIAN',
    tdb_title: 'My Dashboard',
    tdb_viewAllTickets: 'View All Tickets',
    tdb_totalAssigned: 'Total Assigned',
    tdb_completed: 'Completed',
    tdb_completionPct: 'Completion %',
    tdb_avgRating: 'Avg Rating',
    tdb_todayAssignments: "Today\'s Assignments",
    tdb_viewAll: 'View all',
    tdb_noTickets: 'No tickets assigned today',
    tdb_noTicketsHint: 'Check back later or view all tickets',
    tdb_startJob: 'Start Job',
    tdb_starting: 'Starting…',
    tdb_completeJob: 'Complete Job',
    tdb_quickActions: 'Quick Actions',
    tdb_myTickets: 'My Tickets',
    tdb_mobileApp: 'Mobile App',
    tdb_supportRequests: 'Support Requests',
    tdb_inventory: 'Inventory',
    tdb_performance: 'Performance',
    tdb_completionRate: 'Completion Rate',
    tdb_customerRating: 'Customer Rating',

    // ── Organization Onboarding Wizard ──
    oow_superadminRequired: 'Superadmin Access Required',
    oow_superadminDesc: 'Organization onboarding is restricted to superadmins only.',
    oow_backToDashboard: 'Back to Dashboard',
    oow_pageTitle: 'New Organization Setup',
    oow_stepOf: 'Step {current} of {total}',
    oow_back: 'Back',
    oow_next: 'Next',
    oow_launching: 'Launching...',
    oow_launchOrg: 'Launch Organization',
    oow_createdTitle: 'Organization Created!',
    oow_createdDesc: 'has been successfully provisioned.',
    oow_createdEmailSent: 'A welcome email has been sent to',
    oow_createdEmailSentSuffix: 'with login instructions.',
    oow_viewOrgs: 'View Organizations',
    oow_onboardAnother: 'Onboard Another',
    // WizardProgress
    oow_stepCompleted: 'completed',
    oow_minRemaining: '~{n} min remaining',
    oow_stepOrgDetails: 'Org Details',
    oow_stepOrgDetailsDesc: 'Company name, address & contact info',
    oow_stepSubscription: 'Subscription',
    oow_stepSubscriptionDesc: 'Choose plan and billing cycle',
    oow_stepBranding: 'Branding',
    oow_stepBrandingDesc: 'Logo, colors and visual identity',
    oow_stepAdminUser: 'Admin User',
    oow_stepAdminUserDesc: 'Designate the organization admin',
    oow_stepSettings: 'Settings',
    oow_stepSettingsDesc: 'Defaults, SLA and notifications',
    oow_stepReview: 'Review',
    oow_stepReviewDesc: 'Confirm and launch the organization',
    // OrgDetailsStep
    oow_orgDetailsTitle: 'Organization Details',
    oow_orgDetailsSubtitle: 'Enter the basic information for the new organization.',
    oow_prefilledStripe: 'Pre-filled from Stripe Checkout',
    oow_prefilledStripeDesc: 'Organization name and contact email were carried over from the trial signup. Review and update as needed.',
    oow_companyName: 'Company Name',
    oow_industry: 'Industry',
    oow_selectIndustry: 'Select industry...',
    oow_timezone: 'Timezone',
    oow_address: 'Address',
    oow_city: 'City',
    oow_state: 'State',
    oow_zipCode: 'ZIP Code',
    oow_country: 'Country',
    oow_contactEmail: 'Primary Contact Email',
    oow_phone: 'Phone',
    oow_website: 'Website',
    oow_required: 'required',
    // SubscriptionStep
    oow_subscriptionTitle: 'Subscription Plan',
    oow_subscriptionSubtitle: 'Select the subscription tier for this organization.',
    oow_prefilledPlan: 'Plan Pre-selected from Stripe Checkout',
    oow_prefilledPlanDesc: 'The plan was carried over from the trial signup. You can change it below if needed.',
    oow_monthly: 'Monthly',
    oow_annual: 'Annual',
    oow_save20: 'Save 20%',
    oow_perMonth: '/mo',
    oow_mostPopular: 'Most Popular',
    oow_enterprise: 'Enterprise',
    // BrandingStep
    oow_brandingTitle: 'Branding',
    oow_brandingSubtitle: "Customize the organization\'s visual identity.",
    oow_orgLogo: 'Organization Logo',
    oow_dropLogo: 'Drop logo here or click to upload',
    oow_logoFormats: 'PNG, JPG, SVG up to 2MB',
    oow_clickChangeLogo: 'Click to change logo',
    oow_primaryColor: 'Primary Brand Color',
    oow_preview: 'Preview',
    oow_primaryButton: 'Primary Button',
    oow_secondary: 'Secondary',
    // AdminUserStep
    oow_adminTitle: 'Initial Admin User',
    oow_adminSubtitle: 'Create the first administrator for this organization. A welcome email will be sent automatically.',
    oow_prefilledEmail: 'Email Pre-filled from Stripe Checkout',
    oow_prefilledEmailDesc: 'The admin email was carried over from the trial signup. Review and update if needed.',
    oow_autoRoleAssignment: 'Automatic Role Assignment',
    oow_autoRoleDesc: 'This user will be assigned the org_admin role with full access to the organization\'s features.',
    oow_firstName: 'First Name',
    oow_lastName: 'Last Name',
    oow_emailAddress: 'Email Address',
    oow_role: 'Role',
    oow_orgAdminRole: 'Organization Admin',
    oow_orgAdminRoleDesc: 'Full access to all org features',
    oow_sendWelcomeEmail: 'Send welcome email with login instructions',
    // DefaultSettingsStep
    oow_defaultsTitle: 'Default Settings',
    oow_defaultsSubtitle: 'Configure operational defaults for this organization.',
    oow_slaConfig: 'SLA Configuration',
    oow_defaultSlaHours: 'Default SLA Hours',
    oow_slaHoursHint: 'Hours before ticket escalation',
    oow_escalationWarning: 'Escalation Warning (hours)',
    oow_escalationHint: 'Notify before SLA breach',
    oow_ticketDefaults: 'Ticket Defaults',
    oow_defaultPriority: 'Default Priority',
    oow_autoAssignment: 'Auto-Assignment',
    oow_priorityLow: 'Low',
    oow_priorityMedium: 'Medium',
    oow_priorityHigh: 'High',
    oow_priorityCritical: 'Critical',
    oow_disabled: 'Disabled',
    oow_enabled: 'Enabled',
    oow_notifications: 'Notifications',
    oow_emailAlerts: 'Email Alerts',
    oow_emailAlertsDesc: 'Send email notifications for ticket updates',
    oow_dailySummary: 'Daily Summary',
    oow_dailySummaryDesc: 'Send daily digest of activity',
    // ReviewStep
    oow_reviewTitle: 'Review & Launch',
    oow_reviewSubtitle: 'Review your configuration before creating the organization.',
    oow_readyToLaunch: 'Ready to Launch',
    oow_readyToLaunchDesc: 'Clicking "Launch Organization" will create the organization, provision the database, and send the admin welcome email.',
    oow_reviewOrg: 'Organization',
    oow_reviewSubscription: 'Subscription',
    oow_reviewBranding: 'Branding',
    oow_reviewAdminUser: 'Admin User',
    oow_reviewSettings: 'Settings',
    oow_reviewCompanyName: 'Company Name',
    oow_reviewIndustry: 'Industry',
    oow_reviewTimezone: 'Timezone',
    oow_reviewAddress: 'Address',
    oow_reviewContactEmail: 'Contact Email',
    oow_reviewPhone: 'Phone',
    oow_reviewPlan: 'Plan',
    oow_reviewBillingCycle: 'Billing Cycle',
    oow_reviewAnnual: 'Annual (20% off)',
    oow_reviewMonthly: 'Monthly',
    oow_reviewPrimaryColor: 'Primary Color',
    oow_reviewLogo: 'Logo',
    oow_reviewLogoNotUploaded: 'Not uploaded',
    oow_reviewName: 'Name',
    oow_reviewEmail: 'Email',
    oow_reviewRole: 'Role',
    oow_reviewWelcomeEmail: 'Welcome Email',
    oow_reviewYes: 'Yes',
    oow_reviewNo: 'No',
    oow_reviewSlaHours: 'SLA Hours',
    oow_reviewEscalation: 'Escalation Warning',
    oow_reviewDefaultPriority: 'Default Priority',
    oow_reviewAutoAssignment: 'Auto-Assignment',
    oow_reviewEmailAlerts: 'Email Alerts',
    oow_reviewDailySummary: 'Daily Summary',
    oow_reviewHours: 'hours',

    // ── Interactive Demo Onboarding Guide ──
    idg_title: 'Getting Started',
    idg_stepOf: 'Step {current} of {total}',
    idg_skipGuide: 'Skip guide',
    idg_allSteps: 'All Steps',
    idg_previous: 'Previous',
    idg_next: 'Next',
    idg_finish: 'Finish',
    idg_completedTitle: "You\'re all set!",
    idg_completedDesc: "You\'ve completed the onboarding guide. You\'re ready to start using SERVICEJOCKEY to its full potential.",
    idg_goToDashboard: 'Go to Dashboard',
    idg_replayGuide: 'Replay Guide',
    // Step titles & descriptions
    idg_step1Title: 'Welcome to SERVICEJOCKEY',
    idg_step1Desc: 'Your all-in-one field service management platform. This guide will walk you through the key features.',
    idg_step2Title: 'Dashboard Overview',
    idg_step2Desc: 'The dashboard gives you a real-time snapshot of your operations — open tickets, pending quotes, revenue, and team activity.',
    idg_step2Action: 'View Dashboard',
    idg_step3Title: 'Create a Support Request',
    idg_step3Desc: 'Support requests are the starting point for customer issues. Navigate to Sales → Support Requests to log a new request.',
    idg_step3Action: 'Go to Support Requests',
    idg_step4Title: 'Convert to a Ticket',
    idg_step4Desc: 'Once a support request is reviewed, convert it to a work ticket. Open any request and click "Convert to Ticket".',
    idg_step4Action: 'View Tickets',
    idg_step5Title: 'Assign a Technician',
    idg_step5Desc: 'Assign the right technician to each ticket based on skills and availability using the Technician Assignment screen.',
    idg_step5Action: 'Technician Assignment',
    idg_step6Title: 'Generate a Quote',
    idg_step6Desc: 'Create professional quotes for customers. Add line items, apply pricing, and send for approval or directly to the customer.',
    idg_step6Action: 'Quote Management',
    idg_step7Title: 'Create an Invoice',
    idg_step7Desc: 'Convert approved quotes or completed tickets into invoices. Track payment status and send reminders automatically.',
    idg_step7Action: 'Invoice Management',
    idg_step8Title: 'Run a Report',
    idg_step8Desc: 'Gain insights into your business with built-in reports covering revenue, technician performance, and equipment utilization.',
    idg_step8Action: 'View Reports',

    // ── Account Security Center ──
    asc_title: 'Account Security Center',
    asc_subtitle: 'Manage your account security, sessions, and activity',
    asc_tabOverview: 'Overview',
    asc_tabPassword: 'Password',
    asc_tabSessions: 'Sessions',
    asc_tabHistory: 'Login History',
    asc_tab2FA: '2FA',
    asc_tabAdmin: 'Admin Overview',
    asc_statActiveSessions: 'Active Sessions',
    asc_statSuccessfulLogins: 'Successful Logins',
    asc_statFailedAttempts: 'Failed Attempts',
    asc_statSecurityStatus: 'Security Status',
    asc_statusAtRisk: 'At Risk',
    asc_statusGood: 'Good',
    asc_accountInfo: 'Account Information',
    asc_email: 'Email',
    asc_name: 'Name',
    asc_role: 'Role',
    asc_lastSignIn: 'Last Sign In',
    asc_quickActions: 'Quick Actions',
    asc_changePassword: 'Change Password',
    asc_manageSessions: 'Manage Sessions',
    asc_viewLoginHistory: 'View Login History',
    asc_twoFactorAuth: 'Two-Factor Auth',
    asc_changePasswordTitle: 'Change Password',
    asc_changePasswordSubtitle: 'Update your account password',
    asc_currentPassword: 'Current Password',
    asc_currentPasswordPlaceholder: 'Enter current password',
    asc_newPassword: 'New Password',
    asc_newPasswordPlaceholder: 'Enter new password',
    asc_confirmNewPassword: 'Confirm New Password',
    asc_confirmPasswordPlaceholder: 'Confirm new password',
    asc_passwordStrength: 'Strength',
    asc_passwordsNoMatch: 'Passwords do not match',
    asc_updatePassword: 'Update Password',
    asc_updating: 'Updating...',
    asc_strengthVeryWeak: 'Very Weak',
    asc_strengthWeak: 'Weak',
    asc_strengthFair: 'Fair',
    asc_strengthStrong: 'Strong',
    asc_strengthVeryStrong: 'Very Strong',
    asc_errPasswordMismatch: 'New passwords do not match.',
    asc_errPasswordTooShort: 'Password must be at least 8 characters.',
    asc_errPasswordSame: 'New password must be different from current password.',
    asc_errCurrentIncorrect: 'Current password is incorrect.',
    asc_successPasswordChanged: 'Password changed successfully.',
    asc_activeSessions: 'Active Sessions',
    asc_activeSessionsCount: '{n} active session',
    asc_activeSessionsCountPlural: '{n} active sessions',
    asc_revokeAllOthers: 'Revoke All Others',
    asc_noActiveSessions: 'No active sessions found',
    asc_sessionCurrent: 'Current',
    asc_sessionSignOut: 'Sign Out',
    asc_sessionRevoke: 'Revoke',
    asc_sessionRevokedSuccess: 'Session revoked successfully.',
    asc_allOthersRevoked: 'All other sessions revoked.',
    asc_errLoadSessions: 'Failed to load sessions.',
    asc_errRevokeSessions: 'Failed to revoke sessions.',
    asc_loginHistory: 'Login History',
    asc_loginHistorySubtitle: 'Last 10 authentication attempts',
    asc_noLoginHistory: 'No login history available',
    asc_loginHistoryHint: 'Login activity will appear here after your next sign-in',
    asc_successfulLogin: 'Successful Login',
    asc_failedLoginAttempt: 'Failed Login Attempt',
    asc_failureReason: 'Reason',
    asc_errLoadHistory: 'Failed to load login history.',
    asc_2faTitle: 'Two-Factor Authentication',
    asc_2faSubtitle: 'Add an extra layer of security to your account',
    asc_2faComingSoon: 'Coming Soon',
    asc_2faDesc: 'Two-factor authentication adds an additional layer of security to your account by requiring a verification code in addition to your password when signing in.',
    asc_2faAuthApp: 'Authenticator App',
    asc_2faAuthAppDesc: 'Use Google Authenticator, Authy, or similar apps',
    asc_2faEmail: 'Email Verification',
    asc_2faEmailDesc: 'Receive a code to your registered email address',
    asc_2faSMS: 'SMS Verification',
    asc_2faSMSDesc: 'Get a code sent to your mobile phone number',
    asc_2faRecovery: 'Recovery Options',
    asc_2faRecoveryDesc: 'When 2FA is enabled, you will receive backup recovery codes that can be used to access your account if you lose access to your authentication device. Store these codes securely.',
    asc_2faEnable: 'Enable 2FA — Coming Soon',
    asc_securityRecs: 'Security Recommendations',
    asc_securityRecsSubtitle: 'Personalized security insights',
    asc_accountSecure: 'Your account looks secure',
    asc_noIssues: 'No security issues detected at this time.',
    asc_recCount: '{n} recommendation for your account',
    asc_recCountPlural: '{n} recommendations for your account',
    asc_severityTip: 'Tip',
    asc_severityWarning: 'Warning',
    asc_severityAlert: 'Alert',
    asc_recFailedLoginsTitle: 'Multiple Failed Login Attempts',
    asc_recManySessionsTitle: 'Multiple Active Sessions',
    asc_recMultipleLocationsTitle: 'Logins from Multiple Countries',
    asc_rec2FATitle: 'Enable Two-Factor Authentication',
    asc_recChangePassword: 'Change Password',
    asc_recReviewSessions: 'Review Sessions',
    asc_recViewHistory: 'View History',
    asc_recLearnMore: 'Learn More',
    asc_adminOverviewTitle: 'Platform-Wide Security Overview',
    asc_adminOrgOverviewTitle: 'Organization Security Overview',
    asc_adminOverviewSubtitle: 'Monitor user login activity and suspicious patterns',
    asc_filterAll: 'All',
    asc_filterFailed: 'Failed',
    asc_filterSuspicious: 'Suspicious',
    asc_statTotalEvents: 'Total Events',
    asc_statFailedAttemptsStat: 'Failed Attempts',
    asc_statAffectedUsers: 'Affected Users',
    asc_userActivity: 'User Activity',
    asc_events: 'events',
    asc_failed: 'failed',
    asc_noActivity: 'No activity found',
    asc_errLoadOverview: 'Failed to load security overview.',

    // ── Organization Settings ──
    os_title: 'Organization Settings',
    os_subtitle: 'Configure your organization profile, branding, and operational preferences',
    os_accessRestricted: 'Access Restricted',
    os_accessRestrictedDesc: 'Organization Settings is only accessible to Org Admins and Superadmins.',
    os_saveChanges: 'Save Changes',
    os_saving: 'Saving...',
    os_saved: 'Saved',
    os_saveError: 'Failed to save settings.',
    os_tabProfile: 'Organization Profile',
    os_tabBranding: 'Branding',
    os_tabInvitations: 'Invitations',
    os_tabNotifications: 'Notifications',
    os_tabCommunication: 'Communication',
    os_tabTicketDefaults: 'Ticket Defaults',
    os_tabServiceTypes: 'Service Types',
    os_tabSubscription: 'Subscription',
    os_tabBilling: 'Billing Contact',
    os_tabDataRetention: 'Data Retention',
    os_tabDemoSettings: 'Demo Settings',
    os_profileTitle: 'Organization Profile',
    os_profileSubtitle: 'Core company information and contact details',
    os_companyLogo: 'Company Logo',
    os_logoUploadHint: 'Drop logo here or click to upload',
    os_logoFormatHint: 'PNG, JPG, WebP — max 5MB',
    os_uploading: 'Uploading...',
    os_removeLogo: 'Remove logo',
    os_companyName: 'Company Name *',
    os_phone: 'Phone',
    os_email: 'Email',
    os_website: 'Website',
    os_address: 'Address',
    os_addressLine1: 'Address Line 1',
    os_city: 'City',
    os_state: 'State',
    os_zip: 'ZIP',
    os_country: 'Country',
    os_timezone: 'Timezone',
    os_industry: 'Industry',
    os_selectIndustry: 'Select industry...',
    os_errLogoType: 'Please select an image file.',
    os_errLogoSize: 'Image must be smaller than 5MB.',
    os_errLogoUpload: 'Failed to upload logo.',
    os_brandingTitle: 'Branding Settings',
    os_brandingSubtitle: 'Customize your brand colors and visual identity across all screens',
    os_primaryColor: 'Primary Brand Color',
    os_livePreview: 'Live Preview',
    os_documentPreview: 'Document Header Preview',
    os_notifTitle: 'Notification Preferences',
    os_notifSubtitle: 'Configure email alerts, escalation rules, and automated notifications',
    os_emailAlertSettings: 'Email Alert Settings',
    os_emailAlertsEnabled: 'Email Alerts Enabled',
    os_emailAlertsDesc: 'Send email notifications for ticket updates and assignments',
    os_dailySummaryEmail: 'Daily Summary Email',
    os_dailySummaryDesc: 'Receive a daily digest of all activity and pending items',
    os_newTicketNotif: 'New Ticket Notifications',
    os_newTicketNotifDesc: 'Alert when new support requests or tickets are created',
    os_assignmentNotif: 'Assignment Notifications',
    os_assignmentNotifDesc: 'Notify technicians when tickets are assigned to them',
    os_escalationThresholds: 'Escalation Thresholds',
    os_criticalEscalation: 'Critical Priority Escalation (hours)',
    os_highEscalation: 'High Priority Escalation (hours)',
    os_standardEscalation: 'Standard Priority Escalation (hours)',
    os_hoursAfterScheduled: 'hours after scheduled time',
    os_emailTemplatesInfo: 'Email Templates',
    os_emailTemplatesDesc: 'Customize email notification templates in the Email Templates section under Settings.',
    os_paymentReminders: 'Invoice Payment Reminders',
    os_enableReminders: 'Enable Payment Reminders',
    os_enableRemindersDesc: 'Automatically send reminder emails to customers for unpaid invoices',
    os_reminderOnDueDate: 'Reminder on Due Date',
    os_reminderOnDueDateDesc: 'Send a reminder email on the invoice due date',
    os_daysBeforeDue: 'Days Before Due Date',
    os_daysBeforeDueDesc: 'Send reminders this many days before the due date (comma-separated, e.g. 7,3,1)',
    os_overdueReminders: 'Days Overdue Reminders',
    os_overdueRemindersDesc: 'Send overdue reminders at these intervals after the due date (e.g. 3,7,14)',
    os_billingContactEmail: 'Billing Contact Email (Internal)',
    os_billingContactEmailDesc: 'Receive internal notifications when payments are received',
    os_reminderPortalInfo: 'Reminder emails include a direct link to the customer payment portal. Customers can pay online without logging in.',
    os_billingTitle: 'Billing Contact',
    os_billingSubtitle: 'Separate financial contact information for payment processing and invoicing',
    os_billingWarning: 'Billing contact details are used for payment processing and financial communications. Keep this information accurate and up to date.',
    os_billingContactName: 'Billing Contact Name',
    os_billingEmail: 'Billing Email',
    os_billingPhone: 'Billing Phone',
    os_billingAddress: 'Billing Address',
    os_billingAddressPlaceholder: 'Same as company address if blank',
    os_paymentInfo: 'Payment Information',
    os_paymentInstructions: 'Payment Instructions',
    os_paymentInstructionsPlaceholder: 'Please make checks payable to... ACH payments accepted...',
    os_bankName: 'Bank Name',
    os_accountLast4: 'Account (last 4)',
    os_routingLast4: 'Routing (last 4)',
    os_defaultPaymentTerms: 'Default Payment Terms',
    os_ticketDefaultsTitle: 'Default Ticket Settings',
    os_ticketDefaultsSubtitle: 'Configure SLA hours, auto-assignment rules, and default ticket behavior',
    os_slaConfig: 'SLA Configuration',
    os_slaCritical: 'Critical Priority SLA (hours)',
    os_slaHigh: 'High Priority SLA (hours)',
    os_slaMedium: 'Medium Priority SLA (hours)',
    os_slaLow: 'Low Priority SLA (hours)',
    os_autoAssignment: 'Auto-Assignment Rules',
    os_autoAssignEnabled: 'Enabled',
    os_addNewRule: 'Add New Rule',
    os_conditionPlaceholder: 'Condition (e.g., priority=critical)',
    os_assignToPlaceholder: 'Assign to (e.g., team lead)',
    os_addRule: 'Add Rule',
    os_errRuleRequired: 'Both condition and assignment target are required.',
    os_when: 'When',
    os_assignTo: 'Assign to',
    os_defaultValues: 'Default Values',
    os_defaultPriority: 'Default Priority',
    os_priorityLow: 'Low',
    os_priorityMedium: 'Medium',
    os_priorityHigh: 'High',
    os_priorityCritical: 'Critical',
    os_defaultDuration: 'Default Estimated Duration (hours)',
    os_serviceTypesTitle: 'Service Types',
    os_serviceTypesSubtitle: 'Define and categorize the types of services your organization offers. Categories help large teams organize and filter hundreds of custom services.',
    os_addServiceType: 'Add New Service Type',
    os_serviceTypeName: 'Name',
    os_serviceTypeNameRequired: 'Name *',
    os_serviceTypeCategory: 'Category',
    os_serviceTypeDescription: 'Description',
    os_serviceTypeAdd: 'Add',
    os_serviceTypeAdding: 'Adding...',
    os_serviceTypeSave: 'Save',
    os_serviceTypeSaving: 'Saving...',
    os_serviceTypeCancel: 'Cancel',
    os_serviceTypeDelete: 'Delete',
    os_serviceTypeEdit: 'Edit',
    os_serviceTypeActive: 'Active',
    os_serviceTypeInactive: 'Inactive',
    os_serviceTypeSearch: 'Search service types...',
    os_serviceTypeAllCategories: 'All Categories',
    os_serviceTypeNewCategory: '+ New category…',
    os_serviceTypeEnterCategory: 'Enter category name',
    os_serviceTypeNoResults: 'No service types found.',
    os_serviceTypeErrNameRequired: 'Service type name is required.',
    os_invitationsTitle: 'Invitation Management',
    os_invitationsSubtitle: 'Invite users to this organization and manage pending invitations.',
    os_totalMembers: 'Total Members',
    os_pendingInvites: 'Pending Invites',
    os_activeMembers: 'Active Members',
    os_sendInvitation: 'Send New Invitation',
    os_inviteEmailPlaceholder: 'user@example.com',
    os_inviteRoleOrgAdmin: 'Org Admin',
    os_inviteRoleManager: 'Manager',
    os_invitationRoleTechnician: 'Technician',
    os_inviteRoleSalesperson: 'Salesperson',
    os_inviteRoleViewer: 'Viewer',
    os_invite: 'Invite',
    os_inviting: 'Inviting...',
    os_filterAll: 'all',
    os_filterPending: 'pending',
    os_filterAccepted: 'accepted',
    os_filterExpired: 'expired',
    os_filterRevoked: 'revoked',
    os_memberColumn: 'Member',
    os_roleColumn: 'Role',
    os_invitedColumn: 'Invited',
    os_statusColumn: 'Status',
    os_actionsColumn: 'Actions',
    os_resend: 'Resend',
    os_revoke: 'Revoke',
    os_noMembersFound: 'No members found.',
    os_errDuplicateInvite: 'This email already has an active invitation or membership.',
    os_errInviteFailed: 'Failed to send invitation.',

    // ── Permission Audit Dashboard ──
    pad_title: 'Permission Audit Dashboard',
    pad_subtitlePlatform: 'Platform-wide permission monitoring across all organizations',
    pad_subtitleOrg: 'Organization permission monitoring and security analysis',
    pad_accessRestricted: 'Access Restricted',
    pad_accessRestrictedDesc: 'Permission Audit Dashboard is accessible to Org Admins and Superadmins only.',
    pad_filters: 'Filters',
    pad_refresh: 'Refresh',
    pad_export: 'Export',
    pad_clearAll: 'Clear all',
    pad_metricDenials: 'Permission Denials',
    pad_metricFlagged: 'Flagged Events',
    pad_metricCritical: 'Critical Alerts',
    pad_metricRoleChanges: 'Role Changes',
    pad_metricTotalEvents: 'Total Events',
    pad_metricUsersAffected: 'Users Affected',
    pad_tabTimeline: 'Audit Timeline',
    pad_tabDenials: 'Permission Denials',
    pad_tabMatrix: 'Access Matrix',
    pad_tabAnalytics: 'Analytics',
    pad_filterOrg: 'Organization',
    pad_filterAllOrgs: 'All Organizations',
    pad_filterUser: 'User',
    pad_filterAllUsers: 'All Users',
    pad_filterRole: 'Role',
    pad_filterAllRoles: 'All Roles',
    pad_filterScreen: 'Screen',
    pad_filterAllScreens: 'All Screens',
    pad_filterEventType: 'Event Type',
    pad_filterAllEventTypes: 'All Event Types',
    pad_filterSeverity: 'Severity',
    pad_filterAll: 'All',
    pad_filterCritical: 'Critical',
    pad_filterWarning: 'Warning',
    pad_filterInfo: 'Info',
    pad_filterDateRange: 'Date Range',
    pad_filterFlaggedOnly: 'Flagged Only',
    pad_eventRoleChange: 'Role Change',
    pad_eventPermissionGrant: 'Permission Granted',
    pad_eventPermissionRevoke: 'Permission Revoked',
    pad_eventAccessAttempt: 'Access Attempt',
    pad_eventLoginAnomaly: 'Login Anomaly',
    pad_auditTimeline: 'Audit Timeline',
    pad_events: 'events',
    pad_noEvents: 'No audit events found',
    pad_noEventsHint: 'Try adjusting your filters',
    pad_previous: 'Previous',
    pad_next: 'Next',
    pad_pageOf: 'Page {page} of {total}',
    pad_flagged: 'Flagged',
    pad_performedBy: 'Performed By',
    pad_action: 'Action',
    pad_ipAddress: 'IP Address',
    pad_before: 'Before',
    pad_after: 'After',
    pad_details: 'Details',
    pad_severityCritical: 'Critical',
    pad_severityWarning: 'Warning',
    pad_severityInfo: 'Info',
    pad_permissionDenials: 'Permission Denials',
    pad_records: 'records',
    pad_noDenials: 'No permission denials',
    pad_allAuthorized: 'All access attempts were authorized',
    pad_userAccessMatrix: 'User Access Matrix',
    pad_anomalyDetection: 'Anomaly Detection',
    pad_noAnomalies: 'No anomalies detected',
    pad_normalPatterns: 'All access patterns look normal',
    pad_recentRoleChanges: 'Recent Role Changes',
    pad_noRoleChanges: 'No role changes in this period',

    // ── Security & Access Control ──
    sac_title: 'Security & Access Control',
    sac_subtitle: 'Configure role-based menu access permissions',
    sac_accessRestricted: 'Access Restricted',
    sac_accessRestrictedDesc: 'Security & Access Control requires Org Admin access.',
    sac_advancedConfig: 'Advanced Configuration',
    sac_roles: 'Roles',
    sac_newRole: 'New Role',
    sac_roleNamePlaceholder: 'Role name *',
    sac_roleDescPlaceholder: 'Description (optional)',
    sac_create: 'Create',
    sac_cancel: 'Cancel',
    sac_creating: 'Creating...',
    sac_noRoles: 'No roles configured yet.',
    sac_createFirstRole: 'Create your first role',
    sac_selectRolePrompt: 'Select or create a role to configure permissions',
    sac_colScreen: 'Screen',
    sac_colView: 'View',
    sac_colCreate: 'Create',
    sac_colEdit: 'Edit',
    sac_colDelete: 'Delete',
    sac_errLoadFailed: 'Failed to load',
    sac_errSaveFailed: 'Save failed',
    sac_roleCreatedSuccess: 'Role "{name}" created successfully',
    sac_errCreateRole: 'Failed to create role',
    sac_errOrgNotFound: 'Organization not found. Please refresh and try again.',
    sac_orgRoles: 'Organization Roles',
    sac_rolesConfigured: '{n} roles configured',
    sac_access: 'access',
    sac_noRolesFound: 'No roles found. Create roles in User & Role Management.',
    sac_fullAccess: 'Full Access',
    sac_broadAccess: 'Broad Access',
    sac_moderateAccess: 'Moderate Access',
    sac_limitedAccess: 'Limited Access',
    sac_noAccess: 'No Access',
    sac_ofMenus: 'of {total} menus',
    sac_customRole: 'Custom role',
    sac_accessible: 'accessible',
    sac_allOn: 'All On',
    sac_allOff: 'All Off',
    sac_partial: 'Partial',
    sac_allowed: 'Allowed',
    sac_denied: 'Denied',
    sac_saved: 'Saved',
    sac_catSales: 'Sales',
    sac_catOperations: 'Operations',
    sac_catFinance: 'Finance',
    sac_catDataManagement: 'Data Management',
    sac_catSettings: 'Settings',

    // ── Audit Log ──
    al_title: 'Audit Log',
    al_subtitle: 'Complete history of all system activities, changes, and user actions.',
    al_accessRestricted: 'Access Restricted',
    al_accessRestrictedDesc: "You don't have permission to view the Audit Log.",
    al_compliance: 'Compliance',
    al_filters: 'Filters',
    al_exportPDF: 'PDF',
    al_exportCSV: 'Export CSV',
    al_activityTimeline: 'Activity Timeline',
    al_events: 'events',
    al_live: 'Live',
    al_loading: 'Loading audit logs...',
    al_noEvents: 'No audit events found',
    al_noEventsHint: 'Activity will appear here as actions are performed.',
    al_noEventsFiltered: 'Try adjusting your filters.',
    al_clearFilters: 'Clear all filters',
    al_newEntries: '{n} new entry — click to refresh',
    al_newEntriesPlural: '{n} new entries — click to refresh',
    al_refresh: 'Refresh',
    al_clear: 'Clear',
    al_statTotalEvents: 'Total Events',
    al_statErrors: 'Errors',
    al_statWarnings: 'Warnings',
    al_statFailures: 'Failures',
    al_searchPlaceholder: 'Search user, action, resource...',
    al_filterUser: 'User',
    al_filterAllUsers: 'All Users',
    al_filterAction: 'Action',
    al_filterAllActions: 'All Actions',
    al_filterEntityType: 'Entity Type',
    al_filterAllEntities: 'All Entities',
    al_filterSeverity: 'Severity',
    al_filterAllSeverity: 'All Severity',
    al_filterSeverityInfo: 'Info',
    al_filterSeverityWarning: 'Warning',
    al_filterSeverityError: 'Error',
    al_filterSeverityCritical: 'Critical',
    al_filterStatus: 'Status',
    al_filterAllStatus: 'All Status',
    al_filterStatusSuccess: 'Success',
    al_filterStatusFailure: 'Failure',
    al_filterStatusPending: 'Pending',
    al_filterDateFrom: 'Date From',
    al_filterDateTo: 'Date To',
    al_on: 'on',
    al_timestamp: 'Timestamp',
    al_user: 'User',
    al_resourceType: 'Resource Type',
    al_ipAddress: 'IP Address',
    al_changeDetails: 'Change Details',
    al_viewIn: 'View in',
    al_justNow: 'just now',
    al_minutesAgo: '{n}m ago',
    al_hoursAgo: '{n}h ago',
    al_daysAgo: '{n}d ago',
    al_showingOf: 'Showing {from}–{to} of {total}',
    al_pageOf: '{page} / {total}',

    // ── Login Page ──
    login_signIn: 'Sign in to your account',
    login_enterCredentials: 'Enter your credentials to access your account.',
    login_emailAddress: 'Email Address',
    login_emailPlaceholder: 'you@company.com',
    login_password: 'Password',
    login_forgotPassword: 'Forgot password?',
    login_signingIn: 'Signing in...',
    login_signInBtn: 'Sign In',
    login_requestAccess: 'Request Access',
    login_trialCta: 'Click here to get started with your own organization, a 30-day free trial, and test with your own data',
    login_copyright: '© {year} ServiceJockey. All rights reserved.',
    login_emailNotConfirmed: 'Your email address has not been confirmed. Please contact your administrator or try again shortly — the system will auto-confirm your account.',
    login_invalidCredentials: 'Invalid email or password.',
    login_unexpectedError: 'An unexpected error occurred. Please try again.',
    // Request Access Modal
    login_requestTitle: 'Request Access',
    login_requestSubtitle: "Fill out the form below and we\'ll get back to you shortly.",
    login_companyName: 'Company Name',
    login_companyPlaceholder: 'Acme Corp',
    login_numberOfUsers: 'Number of Users',
    login_numberOfUsersPlaceholder: 'e.g. 10',
    login_phoneNumber: 'Phone Number',
    login_phonePlaceholder: '+1 (555) 000-0000',
    login_sending: 'Sending...',
    login_submitRequest: 'Submit Request',
    login_requestSent: 'Request Sent!',
    login_requestSentDesc: "Thank you! We\'ve received your access request and will be in touch shortly.",
    login_close: 'Close',

    // ── Forgot Password Page ──
    fp_title: 'Forgot your password?',
    fp_subtitle: "Enter your email address and we\'ll send you a link to reset your password.",
    fp_emailAddress: 'Email Address',
    fp_emailPlaceholder: 'you@company.com',
    fp_sending: 'Sending...',
    fp_sendResetLink: 'Send Reset Link',
    fp_checkInbox: 'Check your inbox',
    fp_sentTo: 'We sent a password reset link to',
    fp_didntReceive: "Didn\'t receive it? Check your spam folder or",
    fp_tryAgain: 'try again',
    fp_backToSignIn: 'Back to Sign In',

    // ── Reset Password Page ──
    rp_setNewPassword: 'Set a new password',
    rp_chooseStrong: 'Choose a strong password for your account.',
    rp_newPassword: 'New Password',
    rp_minChars: 'Min. 8 characters',
    rp_confirmNewPassword: 'Confirm New Password',
    rp_reEnter: 'Re-enter your password',
    rp_updating: 'Updating...',
    rp_updatePassword: 'Update Password',
    rp_passwordUpdated: 'Password updated!',
    rp_passwordUpdatedDesc: 'Your password has been successfully reset. Redirecting you to sign in…',
    rp_invalidLink: 'Invalid or expired link',
    rp_invalidLinkDesc: 'This password reset link is invalid or has expired. Please request a new one.',
    rp_requestNewLink: 'Request New Link',
    rp_backToSignIn: 'Back to Sign In',
    rp_errTooShort: 'Password must be at least 8 characters long.',
    rp_errNoMatch: 'Passwords do not match.',
    rp_errUpdateFailed: 'Failed to update password. Please try again.',
    rp_errUnexpected: 'An unexpected error occurred. Please try again.',

    // ── Org Settings Branding Tab ──
    os_brandingSettings: 'Branding Settings',
    os_brandingSettingsDesc: 'Customize your brand colors and visual identity across all screens',
    os_primaryBrandColor: 'Primary Brand Color',
    os_livePreviewLabel: 'Live Preview',
    os_documentHeaderPreview: 'Document Header Preview',

    // ── Org Settings Service Types Tab ──
    os_stLoading: 'Loading service types...',
    os_stNoResults: 'No service types found.',
    os_stAddNew: 'Add New Service Type',
    os_stName: 'Name *',
    os_stCategory: 'Category',
    os_stDescription: 'Description',
    os_stAdd: 'Add',
    os_stAdding: 'Adding...',
    os_stSave: 'Save',
    os_stSaving: 'Saving...',
    os_stCancel: 'Cancel',
    os_stDelete: 'Delete',
    os_stEdit: 'Edit',
    os_stActive: 'Active',
    os_stInactive: 'Inactive',
    os_stSearch: 'Search service types...',
    os_stAllCategories: 'All Categories',
    os_stNewCategory: '+ New category…',
    os_stEnterCategory: 'Enter category name',
    os_stErrNameRequired: 'Service type name is required.',

    // ── Org Settings Invitations Tab ──
    os_invLoading: 'Loading members...',
    os_invSendNew: 'Send New Invitation',
    os_invEmailPlaceholder: 'user@example.com',
    os_invRoleOrgAdmin: 'Org Admin',
    os_invRoleManager: 'Manager',
    os_invRoleTechnician: 'Technician',
    os_invRoleSalesperson: 'Salesperson',
    os_invRoleViewer: 'Viewer',
    os_invInvite: 'Invite',
    os_invInviting: 'Inviting...',
    os_invFilterAll: 'all',
    os_invFilterPending: 'pending',
    os_invFilterAccepted: 'accepted',
    os_invFilterExpired: 'expired',
    os_invFilterRevoked: 'revoked',
    os_invMemberCol: 'Member',
    os_invRoleCol: 'Role',
    os_invInvitedCol: 'Invited',
    os_invStatusCol: 'Status',
    os_invActionsCol: 'Actions',
    os_invResend: 'Resend',
    os_invRevoke: 'Revoke',
    os_invNoMembers: 'No members found.',
    os_invErrDuplicate: 'This email already has an active invitation or membership.',
    os_invErrFailed: 'Failed to send invitation.',
    os_invSuccess: 'Invitation sent to {email}',
    os_invTotalMembers: 'Total Members',
    os_invPendingInvites: 'Pending Invites',
    os_invActiveMembers: 'Active Members',

    // ── Settings Hub ──
    sh_title: 'Settings Hub',
    sh_subtitle: 'Manage organization settings, users, billing, security, and administrative controls',
    sh_open: 'Open',

    // ── My Dashboard ──
    md_title: 'My Dashboard',
    md_welcome: 'Welcome, {name}',
    md_loading: 'Loading your dashboard...',
    md_noPersonnelTitle: 'No Personnel Record Found',
    md_noPersonnelDesc: 'Your account is not linked to a personnel record. Contact your manager to set up your profile.',
    md_goToMainDashboard: 'Go to Main Dashboard',
    md_myQuotes: 'My Quotes',
    md_totalQuotes: 'Total quotes',
    md_scheduled: 'Scheduled',
    md_upcomingTickets: 'Upcoming tickets',
    md_pending: 'Pending',
    md_awaitingResponse: 'Awaiting response',
    md_converted: 'Converted',
    md_toTickets: 'To tickets',
    md_totalTickets: 'Total Tickets',
    md_allTickets: 'All tickets',
    md_open: 'Open',
    md_activeTickets: 'Active tickets',
    md_today: 'Today',
    md_scheduledToday: 'Scheduled today',
    md_inProgress: 'In Progress',
    md_activeJobs: 'Active jobs',
    md_assigned: 'Assigned',
    md_sevenDay: '7-Day',
    md_upcomingJobs: 'Upcoming jobs',
    md_myAssignedTickets: 'My Assigned Tickets',
    md_viewAllTickets: 'View All Tickets',
    md_mySchedule: 'My Schedule (7 days)',
    md_calendar: 'Calendar',
    md_myScheduledJobs: 'My Scheduled Jobs',
    md_viewAll: 'View All',
    md_quickActions: 'Quick Actions',
    md_upcomingSchedule: 'Upcoming Schedule',
    md_escalationAlerts: 'Escalation Alerts',
    md_escalations: '{n} escalation',
    md_escalationsPlural: '{n} escalations',
    md_alerts: 'Alerts ({n})',
    md_notScheduled: 'Not scheduled',
    md_noUpcomingJobs: 'No upcoming scheduled jobs.',
    md_noQuotesFound: 'No quotes found. Create your first quote!',
    md_createQuote: 'Create Quote',
    md_noTicketsAssigned: 'No tickets currently assigned to you.',
    md_yourQuotesSchedule: 'Your quotes and scheduled jobs',
    md_allTicketsOverview: 'All tickets and team overview',
    md_yourAssignedTickets: 'Your assigned tickets and schedule',

    // ── Org Admin First Login Checklist ──
    flc_title: 'First Login Checklist',
    flc_subtitle: 'Complete these steps to get your organization ready',
    flc_progress: '{done} of {total} steps completed',
    flc_allDone: 'All done! Your organization is set up.',
    flc_dismiss: 'Dismiss Checklist',
    flc_goToDashboard: 'Go to Dashboard',
    flc_categoryDataVerification: 'Data Verification',
    flc_categoryDataVerificationDesc: 'Confirm your organization details are accurate',
    flc_categoryRoleConfiguration: 'Role Configuration',
    flc_categoryRoleConfigurationDesc: 'Set up your team structure and permissions',
    flc_categoryDemoReview: 'Demo Data Review',
    flc_categoryDemoReviewDesc: 'Explore sample data and plan your go-live',
    flc_priorityRequired: 'Required',
    flc_priorityRecommended: 'Recommended',
    flc_priorityOptional: 'Optional',
    flc_markComplete: 'Mark complete',
    flc_markIncomplete: 'Mark incomplete',
    flc_open: 'Open',

    // ── Subscription & Billing ──
    sb_title: 'Subscription & Billing',
    sb_subtitle: 'Manage your subscription plan, billing details, and payment methods',
    sb_tabPlan: 'Plan',
    sb_tabBilling: 'Billing',
    sb_tabMembers: 'Members',
    sb_tabWebhooks: 'Webhooks',
    sb_currentPlan: 'Current Plan',
    sb_trialDaysLeft: '{n} days left in trial',
    sb_trialExpired: 'Trial Expired',
    sb_suspended: 'Account Suspended',
    sb_active: 'Active',
    sb_trialing: 'Trial',
    sb_pastDue: 'Past Due',
    sb_cancelled: 'Cancelled',
    sb_selectPlan: 'Select Plan',
    sb_monthly: 'Monthly',
    sb_annual: 'Annual',
    sb_save20: 'Save 20%',
    sb_perUserMonth: '/user/mo',
    sb_mostPopular: 'Most Popular',
    sb_unlimited: 'Unlimited',
    sb_fullSuite: 'Full Suite',
    sb_users: 'users',
    sb_userCount: 'User Count',
    sb_monthlyTotal: 'Monthly Total',
    sb_savePlan: 'Save Plan',
    sb_saving: 'Saving...',
    sb_saved: 'Saved!',
    sb_checkout: 'Subscribe Now',
    sb_checkingOut: 'Loading...',
    sb_billingName: 'Billing Name',
    sb_billingEmail: 'Billing Email',
    sb_billingAddress: 'Billing Address',
    sb_saveChanges: 'Save Changes',
    sb_activeMembers: 'Active Members',
    sb_totalMembers: 'Total Members',
    sb_syncBilling: 'Sync Billing',
    sb_syncing: 'Syncing...',
    sb_checkoutSuccess: 'Subscription activated successfully!',
    sb_accessRestricted: 'Access Restricted',
    sb_accessRestrictedDesc: 'Subscription & Billing is only accessible to Org Admins.',

    // ── User & Role Management ──
    urm_title: 'User & Role Management',
    urm_subtitle: "Manage your organization's users, roles, and permissions",
    urm_inviteUser: 'Invite User',
    urm_totalUsers: 'Total Users',
    urm_active: 'Active',
    urm_inactive: 'Inactive',
    urm_customRoles: 'Custom Roles',
    urm_tabUsers: 'Users',
    urm_tabRoles: 'Roles & Permissions',
    urm_tabPreview: 'Role Preview',
    urm_tabInheritance: 'Inheritance',
    urm_tabAudit: 'Role Change Log',
    urm_tabPermissionAudit: 'Permission Changes',
    urm_searchUsers: 'Search users...',
    urm_allRoles: 'All Roles',
    urm_roleAdmin: 'Admin',
    urm_roleManager: 'Manager',
    urm_roleTechnician: 'Technician',
    urm_roleSalesperson: 'Salesperson',
    urm_roleViewer: 'Viewer',
    urm_allStatuses: 'All Statuses',
    urm_statusActive: 'Active',
    urm_statusInactive: 'Inactive',
    urm_statusPending: 'Invite Pending',
    urm_statusNotInvited: 'Not Invited',

    // ── Member Dashboard ──
    mdb_greeting_morning: 'Good morning',
    mdb_greeting_afternoon: 'Good afternoon',
    mdb_greeting_evening: 'Good evening',
    mdb_happeningToday: "Here\'s what\'s happening today",
    mdb_addQuote: 'Add Quote',
    mdb_createTicket: 'Create New Ticket',
    mdb_openTickets: 'Open Tickets',
    mdb_pendingRequests: 'Pending Requests',
    mdb_quickAccess: 'Quick Access',
    mdb_quickAccessDesc: 'Screens available based on your role permissions',
    mdb_noAccess: 'No screens available',
    mdb_noAccessDesc: 'Contact your administrator to request access to specific screens.',
  },
  es: {
    // Nav top-level
    dashboard: 'Panel',
    myDashboard: 'Mi Panel',
    clockInOut: 'Entrada/Salida',
    mobileApp: 'App Móvil',
    sales: 'Ventas',
    ops: 'Ops',
    operations: 'Operaciones',
    finance: 'Finanzas',
    reports: 'Informes',
    data: 'Datos',
    settings: 'Configuración',

    // Sales items
    quoteManagement: 'Gestión de Cotizaciones',
    quoteApprovalQueue: 'Cola de Aprobación de Cotizaciones',
    customerPricing: 'Precios para Clientes',
    customerPortal: 'Portal del Cliente',
    previewCustomerPortal: 'Vista Previa del Portal del Cliente',
    staffPortalActivity: 'Actividad del Portal del Personal',

    // Operations items
    supportRequestManagement: 'Gestión de Solicitudes de Soporte',
    technicianAssignment: 'Asignación de Técnicos',
    ticketManagement: 'Gestión de Tickets',
    pendingTicketsWorkflow: 'Flujo de Tickets Pendientes',
    equipmentManagement: 'Gestión de Equipos',
    technicianFieldLog: 'Registro de Campo del Técnico',
    timeEntryApproval: 'Aprobación de Registro de Tiempo',
    mobileTechnicianApp: 'App Móvil del Técnico',
    jobCompletionReport: 'Informe de Finalización de Trabajo',

    // Finance items
    invoiceManagement: 'Gestión de Facturas',
    arAgingDashboard: 'Panel de Antigüedad de Cuentas por Cobrar',
    invoiceReconciliation: 'Conciliación de Facturas',
    paymentReminders: 'Recordatorios de Pago',
    rentalRevenue: 'Ingresos por Alquiler',
    accountingSync: 'Sincronización Contable',

    // Reports items
    allReports: 'Todos los Informes',
    performanceReports: 'Informes de Rendimiento',
    savedTemplates: 'Plantillas Guardadas',
    analytics: 'Analíticas',

    // Data Management items
    dataManagement: 'Gestión de Datos',
    organizationManagement: 'Gestión de Organización',
    inventoryTracking: 'Seguimiento de Inventario',
    inventoryAnalytics: 'Analíticas de Inventario',
    securityAccessControl: 'Seguridad y Control de Acceso',
    emailAnalytics: 'Analíticas de Correo',

    // Settings items
    organizationSettings: 'Configuración de Organización',
    companyUsers: 'Usuarios de la Empresa',
    subscriptionBilling: 'Suscripción y Facturación',
    subscriptionManagement: 'Gestión de Suscripciones',
    customDashboardBuilder: 'Constructor de Panel Personalizado',
    emailTemplates: 'Plantillas de Correo',
    taxRateSettings: 'Configuración de Tasas de Impuesto',
    accountSecurity: 'Seguridad de Cuenta',
    adminDashboard: 'Panel de Administrador',
    orgDashboard: 'Panel de Organización',
    auditLog: 'Registro de Auditoría',
    activityLog: 'Registro de Actividad',
    permissionAudit: 'Auditoría de Permisos',
    userRoleManagement: 'Gestión de Usuarios y Roles',

    // Profile / user menu
    signOut: 'Cerrar Sesión',
    logOff: 'Cerrar Sesión',
    changePassword: 'Cambiar Contraseña',
    language: 'Idioma',
    languageEnglish: 'English',
    languageSpanish: 'Español',

    // Mobile section labels
    myTimeEntry: 'Mi Registro de Tiempo',
    clockInOutDesc: 'Registra tu tiempo y kilometraje',

    // Session
    sessionExpiringSoon: 'La sesión expirará pronto',
    autoSignOut: 'Cierre automático en {n}s por inactividad',
    stay: 'Continuar',

    // ── Ticket Management – shared labels ──
    tm_createTicket: 'Crear Nuevo Ticket',
    tm_createTicketDesc: 'Complete todos los detalles del ticket para el cliente',
    tm_editTicket: 'Editar Solicitud de Servicio',
    tm_filterTickets: 'Filtrar Tickets',
    tm_reset: 'Restablecer',
    tm_cancel: 'Cancelar',
    tm_save: 'Guardar Cambios',
    tm_saving: 'Guardando...',
    tm_creating: 'Creando Ticket...',
    tm_draftSaved: 'Borrador guardado automáticamente',
    tm_loadingForm: 'Cargando datos del formulario...',

    // Create / Edit form fields
    tm_customer: 'Cliente',
    tm_selectCustomer: 'Seleccionar un cliente',
    tm_location: 'Ubicación',
    tm_selectLocation: 'Seleccionar una ubicación (opcional)',
    tm_locationDesc: 'Vincular este ticket a una ubicación específica del cliente',
    tm_addLocation: 'Agregar Ubicación',
    tm_noLocation: 'Sin ubicación específica',
    tm_noLocationsFound: 'No se encontraron ubicaciones para este cliente. Use "Agregar Ubicación" para crear una.',
    tm_serviceType: 'Tipo de Servicio',
    tm_selectServiceType: 'Seleccionar tipo de servicio',
    tm_priority: 'Prioridad',
    tm_selectPriority: 'Seleccionar prioridad',
    tm_status: 'Estado',
    tm_selectStatus: 'Seleccionar estado',
    tm_scheduledDate: 'Fecha Programada',
    tm_scheduledTime: 'Hora Programada',
    tm_timezone: 'Zona Horaria',
    tm_estimatedDuration: 'Duración Estimada',
    tm_selectDuration: 'Seleccionar duración estimada',
    tm_description: 'Descripción',
    tm_descriptionPlaceholder: 'Describa la solicitud de servicio, el problema o el trabajo a realizar...',
    tm_internalNotes: 'Notas Internas',
    tm_internalNotesPlaceholder: 'Notas o instrucciones internas adicionales...',
    tm_internalNotesHint: 'Opcional — visible solo para el personal',
    tm_assignTechnicians: 'Asignar Técnicos',
    tm_selectTechnicians: 'Seleccionar uno o más técnicos',
    tm_techniciansDesc: 'Se pueden asignar múltiples técnicos',
    tm_salesperson: 'Vendedor',
    tm_selectSalesperson: 'Seleccionar vendedor (opcional)',
    tm_productsServices: 'Productos y Servicios',
    tm_addProduct: 'Agregar Producto',
    tm_searchProducts: 'Buscar productos...',
    tm_noProductsFound: 'No se encontraron productos',
    tm_noProductsAdded: 'No se agregaron productos. Haga clic en "Agregar Producto" para buscar y agregar artículos.',
    tm_capitalEquipment: 'Equipo de Capital',
    tm_assignEquipment: 'Asignar Equipo',
    tm_searchEquipment: 'Buscar equipo...',
    tm_noEquipmentFound: 'No se encontró equipo',
    tm_noEquipmentAdded: 'No se asignó equipo. Haga clic en "Asignar Equipo" para buscar y seleccionar artículos.',
    tm_customFields: 'Campos Personalizados',
    tm_customFieldsDesc: 'Campos opcionales para rastrear datos adicionales del ticket.',
    tm_customField1: 'Campo Personalizado 1',
    tm_customField2: 'Campo Personalizado 2',
    tm_customField3: 'Campo Personalizado 3',
    tm_customField4: 'Campo Personalizado 4',
    tm_customField5: 'Campo Personalizado 5',
    tm_enterValue: 'Ingresar valor',
    tm_createTicketBtn: 'Crear Ticket',

    // Duration options
    tm_dur1h: '1 hora',
    tm_dur2h: '2 horas',
    tm_dur3h: '3 horas',
    tm_dur4h: '4 horas',
    tm_dur6h: '6 horas',
    tm_dur8h: 'Día completo (8 horas)',

    // Ticket Detail Panel
    tm_edit: 'Editar',
    tm_updateStatus: 'Actualizar Estado',
    tm_currentStatus: 'Estado Actual',
    tm_statusMgmt: 'Gestión de Estado',
    tm_readyToBegin: '¿Listo para Comenzar?',
    tm_startJobDesc: 'Toque el botón de abajo cuando comience a trabajar en este trabajo. Esto cambiará el estado a En Progreso.',
    tm_startJob: 'Iniciar Trabajo — Marcar En Progreso',
    tm_jobInProgress: 'Trabajo En Progreso',
    tm_completeJobDesc: 'Cuando el trabajo esté terminado, toque abajo para marcar este trabajo como completo y enviar alertas de finalización.',
    tm_completeJob: 'Completar Trabajo — Enviar Alertas',
    tm_ticketSummary: 'Resumen del Ticket',
    tm_customerInfo: 'Información del Cliente',
    tm_serviceDetails: 'Detalles del Servicio',
    tm_assignedTechnicians: 'Técnicos Asignados',
    tm_noTechniciansAssigned: 'No hay técnicos asignados aún.',
    tm_mileageTracking: 'Seguimiento de Kilometraje',
    tm_startMileage: 'Kilometraje Inicial',
    tm_endMileage: 'Kilometraje Final',
    tm_totalMileage: 'Kilometraje Total',
    tm_saveMileage: 'Guardar Kilometraje',
    tm_saved: '¡Guardado!',
    tm_mileageError: 'El kilometraje final debe ser mayor o igual al kilometraje inicial.',
    tm_locationAssignment: 'Asignación de Ubicación',
    tm_assignLocationLabel: 'Asignar Ubicación',
    tm_selectServiceLocation: 'Seleccionar una Ubicación de Servicio',
    tm_noLocationAssigned: 'Sin ubicación asignada',
    tm_saveLocation: 'Guardar Ubicación',
    tm_addFirstLocation: 'Agregar Primera Ubicación',
    tm_noLocationsCustomer: 'No se encontraron ubicaciones para este cliente.',
    tm_serviceHistoryAtLocation: 'Historial de Servicio en esta Ubicación',
    tm_loadingHistory: 'Cargando historial...',
    tm_noServiceHistory: 'No hay tickets de servicio previos en esta ubicación.',
    tm_scheduleWork: 'Programar Trabajo',
    tm_date: 'Fecha',
    tm_time: 'Hora',
    tm_statusWillChange: 'El estado cambiará automáticamente a Programado al guardar.',
    tm_assignTechniciansLabel: 'Asignar Técnicos',
    tm_selectTechnicianPlaceholder: 'Seleccionar técnico',
    tm_name: 'Nombre',
    tm_email: 'Correo',
    tm_phone: 'Teléfono',
    tm_address: 'Dirección',
    tm_serviceTypeLabel: 'Tipo de Servicio',
    tm_serviceRequestedDate: 'Fecha de Servicio Solicitada',
    tm_scheduledDateLabel: 'Fecha Programada',
    tm_duration: 'Duración',
    tm_noDate: 'Sin Fecha',
    tm_customer_label: 'Cliente',
    tm_removeLocation: 'Eliminar',
    tm_describeRequest: 'Describa la solicitud de servicio...',
    tm_additionalNotes: 'Notas internas adicionales...',

    // Tabs
    tm_tabDetails: 'Detalles',
    tm_tabCommunication: 'Comunicación',
    tm_tabAttachments: 'Adjuntos',
    tm_tabHistory: 'Historial',
    tm_tabSurvey: 'Encuesta',
    tm_tabSource: 'Origen',

    // Convert to Invoice Modal
    tm_convertToInvoice: 'Convertir a Factura',
    tm_taxRate: 'Tasa de Impuesto',
    tm_selectTaxRate: 'Seleccionar tasa de impuesto',
    tm_customRate: 'Ingresar tasa personalizada %',
    tm_useSavedRate: 'Usar tasa de impuesto guardada',
    tm_enterCustomRate: 'Ingresar tasa personalizada',
    tm_discountAmount: 'Monto de Descuento ($)',
    tm_dueDate: 'Fecha de Vencimiento',
    tm_notes: 'Notas',
    tm_invoiceNotes: 'Notas de la factura...',
    tm_subtotal: 'Subtotal:',
    tm_rentalSubtotal: 'Subtotal de alquiler:',
    tm_discount: 'Descuento:',
    tm_tax: 'Impuesto ({rate}%):',
    tm_totalAmount: 'Monto Total:',
    tm_createInvoice: 'Crear Factura',
    tm_rentalValidationIssues: 'Problemas de Validación de Alquiler',
    tm_rentalReturnNote: 'Regrese al ticket y corrija los precios de los artículos de alquiler antes de convertir.',
    tm_equipmentRentalItems: 'Artículos de Alquiler de Equipo',
    tm_rentalItem: 'Artículo de Alquiler',
    tm_days: 'Días',
    tm_ratePerDay: 'Tarifa/Día',
    tm_total: 'Total',
    tm_rentalTransferNote: 'Los artículos de alquiler se transfieren a la factura con su total de alquiler completo. Tarifa/Día × Días = Total.',
    tm_otherTicketItems: 'Otros Artículos del Ticket',
    tm_ticketItems: 'Artículos del Ticket',
    tm_item: 'Artículo',
    tm_qty: 'Cant.',
    tm_price: 'Precio',
    tm_noProductsWarning: 'Este ticket no tiene productos. La factura se creará con subtotal de $0. Puede agregar artículos después de la creación.',
    tm_selectDueDateError: 'Por favor seleccione una fecha de vencimiento.',

    // Job Completion Modal
    tm_completeJobTitle: 'Completar Trabajo',
    tm_completionSummary: 'Resumen de Finalización',
    tm_completionSummaryRequired: 'Resumen de Finalización *',
    tm_completionSummaryPlaceholder: 'Describa brevemente lo que se hizo para completar este trabajo...',
    tm_additionalNotesOptional: 'Notas Adicionales',
    tm_additionalNotesOptionalLabel: '(opcional)',
    tm_additionalNotesPlaceholder: 'Elementos de seguimiento, piezas usadas u observaciones...',
    tm_reportPromptNotice: 'Después de marcar como completo, se le pedirá que complete el Informe de Finalización de Trabajo — un registro detallado con lista de verificación, fotos y firma del cliente.',
    tm_sendCompletionAlert: 'Enviar Alerta de Finalización',
    tm_sendAlertDesc: 'Notificar al cliente y al equipo de gestión que este trabajo ha sido completado.',
    tm_markJobComplete: 'Marcar Trabajo Completo',
    tm_completing: 'Completando...',
    tm_completionSummaryError: 'Por favor proporcione un resumen de finalización antes de marcar el trabajo como completo.',

    // Filters
    tm_filterTitle: 'Filtrar Tickets',
    tm_searchPlaceholder: 'Buscar por ID de ticket o cliente...',
    tm_allStatuses: 'Todos los Estados',
    tm_allPriorities: 'Todas las Prioridades',
    tm_allTechnicians: 'Todos los Técnicos',
    tm_allLocations: 'Todos las Ubicaciones',
    tm_fromDate: 'Desde Fecha',
    tm_toDate: 'Hasta Fecha',
    tm_filterByLocation: 'Filtrar por ubicación',
    tm_statusPending: 'Pendiente',
    tm_statusScheduled: 'Programado',
    tm_statusInProgress: 'En Progreso',
    tm_statusOnHold: 'En Espera',
    tm_statusCompleted: 'Completado',
    tm_statusInvoiced: 'Facturado',
    tm_statusClosed: 'Cerrado',
    tm_statusCancelled: 'Cancelado',
    tm_priorityCritical: 'Crítico',
    tm_priorityHigh: 'Alto',
    tm_priorityMedium: 'Medio',
    tm_priorityLow: 'Bajo',

    // Bulk Assign Modal
    tm_assignTechnicianTitle: 'Asignar Técnico',
    tm_assignToTickets: 'Asignar a {count} ticket{plural} seleccionado{plural}',
    tm_assigning: 'Asignando...',
    tm_assign: 'Asignar',

    // Add Location Modal
    tm_addNewLocation: 'Agregar Nueva Ubicación',
    tm_locationName: 'Nombre de Ubicación *',
    tm_locationNamePlaceholder: 'ej. Pozo #14A',
    tm_locationId: 'ID de Ubicación',
    tm_locationIdPlaceholder: 'ej. 42-123-45678',
    tm_company: 'Empresa',
    tm_companyPlaceholder: 'Empresa operadora',
    tm_locationAddress: 'Ubicación / Dirección',
    tm_locationAddressPlaceholder: 'Ingrese la dirección o detalles de ubicación',
    tm_locationAddressDesc: 'Escriba la dirección o detalles de ubicación',
    tm_contacts: 'Contactos',
    tm_contactName: 'Nombre del Contacto',
    tm_contactEmail: 'Correo',
    tm_contactPhone: 'Teléfono',
    tm_addContact: 'Agregar Contacto',
    tm_removeContact: 'Eliminar',
    tm_saveLocation_btn: 'Guardar Ubicación',
    tm_saving_location: 'Guardando...',
    tm_locationNameRequired: 'El nombre de la ubicación es obligatorio.',

    // Custom Fields Modal
    tm_manageServiceTypes: 'Gestionar Tipos de Servicio',
    tm_manageStatuses: 'Gestionar Estados',
    tm_managePriorities: 'Gestionar Prioridades',
    tm_manageServiceTypesDesc: 'Gestionar opciones de tipo de servicio personalizadas para tickets',
    tm_manageStatusesDesc: 'Gestionar opciones de estado personalizadas para tickets',
    tm_managePrioritiesDesc: 'Gestionar niveles de prioridad personalizados para tickets',
    tm_addNew: 'Agregar Nuevo',
    tm_fieldName: 'Nombre',
    tm_fieldDescription: 'Descripción (Opcional)',
    tm_fieldDescriptionPlaceholder: 'Ingresar descripción',
    tm_existing: 'Existente',
    tm_loading: 'Cargando...',
    tm_noFieldsAdded: 'No se han agregado {type} aún',
    tm_deleteConfirm: '¿Está seguro de que desea eliminar este campo?',

    // ── Support Request Management ──
    srm_title: 'Gestión de Solicitudes de Soporte',
    srm_createRequest: 'Crear Solicitud de Soporte',
    srm_editRequest: 'Editar Solicitud de Soporte',
    srm_manageRequests: 'Gestionar Solicitudes',
    srm_newRequest: 'Nueva Solicitud',
    srm_requestId: 'ID de Solicitud',
    srm_customer: 'Cliente',
    srm_selectCustomer: 'Seleccionar un cliente',
    srm_newCustomer: 'Nuevo Cliente',
    srm_newCustomerName: 'Nombre del Cliente',
    srm_newCustomerEmail: 'Correo',
    srm_newCustomerPhone: 'Teléfono',
    srm_newCustomerAddress: 'Dirección',
    srm_location: 'Ubicación',
    srm_selectLocation: 'Seleccionar una ubicación (opcional)',
    srm_locationContact: 'Contacto de Ubicación',
    srm_selectContact: 'Seleccionar un contacto (opcional)',
    srm_addLocation: 'Agregar Ubicación',
    srm_serviceType: 'Tipo de Servicio',
    srm_selectServiceType: 'Seleccionar tipo de servicio',
    srm_priority: 'Prioridad',
    srm_selectPriority: 'Seleccionar prioridad',
    srm_description: 'Descripción',
    srm_descriptionPlaceholder: 'Describa la solicitud de soporte en detalle...',
    srm_urgentRequest: 'Solicitud Urgente',
    srm_serviceRequestedDate: 'Fecha de Servicio Solicitada',
    srm_scheduledDate: 'Fecha Programada',
    srm_timezone: 'Zona Horaria',
    srm_products: 'Productos / Servicios',
    srm_addProduct: 'Agregar Producto',
    srm_searchProducts: 'Buscar productos...',
    srm_noProductsFound: 'No se encontraron productos',
    srm_noProductsAdded: 'No se han agregado productos aún.',
    srm_rentalEquipment: 'Equipo de Alquiler',
    srm_addRental: 'Agregar Alquiler',
    srm_rentalPeriod: 'Período de Alquiler',
    srm_rentalDays: 'Número de Días',
    srm_daily: 'Diario',
    srm_hourly: 'Por Hora',
    srm_monthly: 'Mensual',
    srm_submit: 'Enviar Solicitud',
    srm_submitting: 'Enviando...',
    srm_update: 'Actualizar Solicitud',
    srm_cancel: 'Cancelar',
    srm_draftSaved: 'Borrador guardado automáticamente',
    srm_loadingForm: 'Cargando formulario...',
    srm_taxRate: 'Tasa de Impuesto',
    srm_subtotal: 'Subtotal',
    srm_tax: 'Impuesto',
    srm_total: 'Total',
    // Status options
    srm_statusPending: 'Pendiente de Revisión',
    srm_statusApproved: 'Aprobado',
    srm_statusInProgress: 'En Progreso',
    srm_statusConverted: 'Convertido a Ticket',
    srm_statusRejected: 'Rechazado',
    // Priority options
    srm_priorityLow: 'Bajo',
    srm_priorityMedium: 'Medio',
    srm_priorityHigh: 'Alto',
    srm_priorityCritical: 'Crítico',
    // Table headers
    srm_colRequestId: 'ID de Solicitud',
    srm_colCustomer: 'Cliente',
    srm_colServiceType: 'Tipo de Servicio',
    srm_colPriority: 'Prioridad',
    srm_colStatus: 'Estado',
    srm_colServiceDate: 'Fecha de Servicio',
    srm_colCreated: 'Creado',
    srm_colActions: 'Acciones',
    srm_showing: 'Mostrando {count} de {total} solicitudes',
    srm_noRequests: 'No se encontraron solicitudes de soporte',
    srm_clearFilters: 'Limpiar Todos los Filtros',
    // Actions
    srm_viewDetails: 'Ver Detalles',
    srm_edit: 'Editar',
    srm_convertToTicket: 'Convertir a Ticket',
    srm_assignManager: 'Asignar Gerente',
    // Details modal
    srm_requestDetails: 'Detalles de la Solicitud',
    srm_customerInfo: 'Información del Cliente',
    srm_serviceDetails: 'Detalles del Servicio',
    srm_managerAssignment: 'Asignación de Gerente',
    srm_noManagerAssigned: 'Sin gerente asignado',
    srm_assignManagerBtn: 'Asignar Gerente',
    srm_siteName: 'Nombre del Sitio',
    srm_locationId: 'ID de Ubicación (API)',
    srm_company: 'Empresa',
    srm_addressLabel: 'Dirección / Ubicación',
    srm_name: 'Nombre',
    srm_email: 'Correo',
    srm_phone: 'Teléfono',
    srm_customerId: 'ID del Cliente',
    srm_productsRequested: 'Productos/Servicios Solicitados',
    srm_noProducts: 'No se agregaron productos',
    srm_qty: 'Cant.',
    srm_serviceRequested: 'Servicio Solicitado',
    srm_priority_label: 'Prioridad',
    // Conversion modal
    srm_convertTitle: 'Convertir a Ticket',
    srm_preConversionChecklist: 'Lista de Verificación Previa a la Conversión',
    srm_requiredFields: 'Campos Requeridos',
    srm_optionalFields: 'Campos Opcionales',
    srm_assignTechnicians: 'Asignar Técnicos',
    srm_selectTechnicians: 'Seleccionar técnicos',
    srm_scheduledDate_label: 'Fecha Programada',
    srm_scheduledTime: 'Hora Programada',
    srm_estimatedDuration: 'Duración Estimada',
    srm_notes: 'Notas',
    srm_notesPlaceholder: 'Notas adicionales para el ticket...',
    srm_expectedServiceDate: 'Fecha de Servicio Solicitada',
    srm_converting: 'Convirtiendo...',
    srm_confirmConvert: 'Confirmar Conversión',
    srm_missingRequired: 'Campos requeridos faltantes: {fields}',
    // Manager assignment modal
    srm_assignManagerTitle: 'Asignar Gerente',
    srm_selectManager: 'Seleccionar Gerente',
    srm_selectManagerPlaceholder: 'Elegir un gerente para asignar',
    srm_managerDescription: 'El gerente supervisará esta solicitud de soporte',
    srm_managerRole: 'Rol del Gerente',
    srm_managerRoleDesc: 'El gerente asignado será responsable de supervisar esta solicitud, asignar técnicos y garantizar la finalización oportuna.',
    srm_assigning: 'Asignando...',
    srm_assignManagerConfirm: 'Asignar Gerente',
    srm_requestSummary: 'Resumen de la Solicitud',
    srm_loadingManagers: 'Cargando gerentes...',
    // Validation messages
    srm_customerRequired: 'El cliente es obligatorio',
    srm_serviceTypeRequired: 'El tipo de servicio es obligatorio',
    srm_descriptionRequired: 'La descripción es obligatoria',
    srm_priorityRequired: 'La prioridad es obligatoria',

    // ── Quote Management ──
    qm_title: 'Gestión de Cotizaciones',
    qm_createQuote: 'Crear Cotización',
    qm_editQuote: 'Editar Cotización',
    qm_customer: 'Cliente',
    qm_selectCustomer: 'Seleccionar cliente',
    qm_addNewCustomer: 'Agregar Nuevo Cliente',
    qm_location: 'Ubicación',
    qm_selectLocation: 'Seleccionar ubicación (opcional)',
    qm_addLocation: 'Agregar Ubicación',
    qm_locationContact: 'Contacto de Ubicación',
    qm_selectContact: 'Seleccionar contacto (opcional)',
    qm_salesperson: 'Vendedor',
    qm_selectSalesperson: 'Seleccionar vendedor',
    qm_serviceType: 'Tipo de Servicio',
    qm_selectServiceType: 'Seleccionar tipo de servicio (opcional)',
    qm_expectedServiceDate: 'Fecha de Solicitud de Servicio',
    qm_timezone: 'Zona Horaria',
    qm_paymentTerms: 'Términos de Pago',
    qm_paymentTermsPlaceholder: 'ej. Neto 30',
    qm_paymentDueDays: 'Días de Vencimiento de Pago',
    qm_depositRequired: 'Depósito Requerido',
    qm_depositPercentage: 'Porcentaje de Depósito (%)',
    qm_taxRate: 'Tasa de Impuesto (%)',
    qm_notes: 'Notas',
    qm_notesPlaceholder: 'Agregue notas o instrucciones especiales...',
    qm_products: 'Productos y Servicios',
    qm_addProduct: 'Agregar Producto / Kit',
    qm_selectProduct: 'Seleccionar producto o kit',
    qm_productName: 'Nombre del Producto',
    qm_quantity: 'Cantidad',
    qm_price: 'Precio',
    qm_addItem: 'Agregar Artículo',
    qm_removeItem: 'Eliminar',
    qm_rentalEquipment: 'Equipo de Alquiler',
    qm_addRental: 'Agregar Alquiler a Cotización',
    qm_rentalPeriod: 'Período de Alquiler',
    qm_rentalQty: 'Número de Días',
    qm_daily: 'Diario',
    qm_hourly: 'Por Hora',
    qm_monthly: 'Mensual',
    qm_subtotal: 'Subtotal:',
    qm_tax: 'Impuesto ({rate}%):',
    qm_total: 'Total:',
    qm_createQuoteBtn: 'Crear Cotización',
    qm_creating: 'Creando...',
    qm_cancel: 'Cancelar',
    qm_loadingForm: 'Cargando formulario...',
    qm_errorLoadingForm: 'Error al Cargar el Formulario',
    qm_retry: 'Reintentar',
    qm_templateApplied: 'Plantilla aplicada',
    qm_documentTemplate: 'Plantilla de Documento',
    qm_selectTemplate: 'Seleccionar plantilla',
    qm_orgDefault: 'Predeterminado de la Org.',
    qm_switchToTemplates: 'Explorar Plantillas',
    qm_inventoryWarning: 'Advertencia de Inventario',
    qm_stockAvailable: 'En Stock',
    qm_stockLow: 'Stock Bajo',
    qm_stockOut: 'Sin Stock',
    qm_notTracked: 'No Rastreado',
    qm_priceListApplied: 'Lista de precios personalizada aplicada',
    qm_noProductsAdded: 'No se han agregado productos. Agregue al menos un producto para crear una cotización.',
    qm_productRequired: 'El nombre del producto es obligatorio',
    qm_quantityRequired: 'La cantidad debe ser al menos 1',
    qm_priceNegative: 'El precio no puede ser negativo',
    qm_fillRequired: 'Por favor complete todos los campos requeridos y agregue al menos un producto',
    // Quote table / list
    qm_quoteNumber: 'Cotización #',
    qm_customer_col: 'Cliente',
    qm_status_col: 'Estado',
    qm_amount_col: 'Monto',
    qm_date_col: 'Fecha',
    qm_actions_col: 'Acciones',
    qm_viewDetails: 'Ver Detalles',
    qm_edit: 'Editar',
    qm_convert: 'Convertir a Factura',
    qm_delete: 'Eliminar',
    qm_noQuotes: 'No se encontraron cotizaciones',
    // Status options
    qm_statusDraft: 'Borrador',
    qm_statusSent: 'Enviado',
    qm_statusApproved: 'Aprobado',
    qm_statusRejected: 'Rechazado',
    qm_statusExpired: 'Vencido',
    qm_statusConverted: 'Convertido',
    // Validation
    qm_customerRequired: 'El cliente es obligatorio',
    qm_salespersonRequired: 'El vendedor es obligatorio',
    qm_productsRequired: 'Se requiere al menos un producto',

    // ── Invoice Management ──
    im_title: 'Gestión de Facturas',
    im_createInvoice: 'Crear Factura',
    im_customer: 'Cliente',
    im_selectCustomer: 'Seleccionar cliente',
    im_relatedTicket: 'Ticket Relacionado (Opcional)',
    im_selectTicket: 'Seleccionar ticket',
    im_relatedQuote: 'Cotización Relacionada (Opcional)',
    im_selectQuote: 'Seleccionar cotización',
    im_dueDate: 'Fecha de Vencimiento',
    im_taxRate: 'Tasa de Impuesto (%)',
    im_discountAmount: 'Monto de Descuento',
    im_notes: 'Notas',
    im_notesPlaceholder: 'Agregue notas o instrucciones especiales...',
    im_invoiceItems: 'Artículos de la Factura',
    im_product: 'Producto',
    im_selectProduct: 'Seleccionar producto',
    im_description: 'Descripción',
    im_descriptionPlaceholder: 'Ingresar descripción',
    im_quantity: 'Cantidad',
    im_unitPrice: 'Precio Unitario',
    im_discountPercent: 'Descuento %',
    im_addItem: 'Agregar Artículo',
    im_subtotal: 'Subtotal:',
    im_discount: 'Descuento:',
    im_tax: 'Impuesto ({rate}%):',
    im_totalAmount: 'Monto Total:',
    im_createInvoiceBtn: 'Crear Factura',
    im_creating: 'Creando...',
    im_cancel: 'Cancelar',
    im_loadingForm: 'Cargando formulario...',
    im_draftSaved: 'Borrador guardado automáticamente',
    im_fixErrors: 'Por favor corrija los siguientes errores:',
    // Invoice detail modal
    im_invoiceDetails: 'Detalles de la Factura',
    im_sendReminder: 'Enviar Recordatorio',
    im_sending: 'Enviando...',
    im_sendInvoice: 'Enviar Factura',
    im_recordPayment: 'Registrar Pago',
    im_invoiceStatus: 'Estado de la Factura',
    im_updateStatus: 'Actualizar Estado',
    im_statusDraft: 'Borrador',
    im_statusSent: 'Enviado',
    im_statusPending: 'Pendiente',
    im_statusPartial: 'Parcial',
    im_statusPaid: 'Pagado',
    im_statusOverdue: 'Vencido',
    im_statusCancelled: 'Cancelado',
    im_daysOverdue: 'Esta factura tiene {days} días de vencimiento',
    im_customerPortal: 'Portal del Cliente',
    im_copyLink: 'Copiar Enlace',
    im_portalLinkCopied: '¡Enlace del portal copiado!',
    im_sourceTicket: 'Ticket de Origen',
    im_customerInfo: 'Información del Cliente',
    im_billingAddress: 'Dirección de Facturación',
    im_invoiceDate: 'Fecha de Factura',
    im_dueDateLabel: 'Fecha de Vencimiento',
    im_paymentStatus: 'Estado de Pago',
    im_lineItems: 'Artículos',
    im_item: 'Artículo',
    im_qty: 'Cant.',
    im_price: 'Precio',
    im_disc: 'Desc%',
    im_total: 'Total',
    im_paymentHistory: 'Historial de Pagos',
    im_noPayments: 'No se han registrado pagos',
    im_reminderHistory: 'Historial de Recordatorios',
    im_noReminders: 'No se han enviado recordatorios aún',
    im_downloadPDF: 'Descargar PDF',
    im_reminderSent: '¡Recordatorio enviado exitosamente!',
    im_reminderFailed: 'Error al enviar el recordatorio. Por favor intente de nuevo.',
    im_noCustomerEmail: 'No se encontró correo del cliente para esta factura.',
    // Invoice table
    im_invoiceNumber: 'Factura #',
    im_customer_col: 'Cliente',
    im_amount_col: 'Monto',
    im_status_col: 'Estado',
    im_date_col: 'Fecha',
    im_actions_col: 'Acciones',
    im_noInvoices: 'No se encontraron facturas',
    // Validation
    im_customerRequired: 'El cliente es obligatorio',
    im_dueDateRequired: 'La fecha de vencimiento es obligatoria',
    im_itemsRequired: 'Se requiere al menos un artículo',

    // ── Customer Database ──
    cd_title: 'Base de Datos de Clientes',
    cd_addCustomer: 'Agregar Cliente',
    cd_addNewCustomer: 'Agregar Nuevo Cliente',
    cd_searchPlaceholder: 'Buscar por nombre, empresa, correo o teléfono...',
    cd_filters: 'Filtros',
    cd_clearFilters: 'Limpiar Todos los Filtros',
    cd_activeFilters: 'Filtros activos aplicados',
    cd_noFilters: 'Sin filtros aplicados',
    cd_status: 'Estado',
    cd_allStatus: 'Todos los Estados',
    cd_statusActive: 'Activo',
    cd_statusInactive: 'Inactivo',
    cd_statusPending: 'Pendiente',
    cd_location: 'Ubicación',
    cd_allLocations: 'Todas las Ubicaciones',
    cd_serviceType: 'Tipo de Servicio',
    cd_allServiceTypes: 'Todos los Tipos de Servicio',
    // Add customer form
    cd_basicInfo: 'Información Básica',
    cd_fullName: 'Nombre Completo',
    cd_fullNamePlaceholder: 'Juan García',
    cd_emailAddress: 'Dirección de Correo',
    cd_emailPlaceholder: 'juan.garcia@ejemplo.com',
    cd_phoneNumber: 'Número de Teléfono',
    cd_phonePlaceholder: '+1 (555) 123-4567',
    cd_company: 'Empresa',
    cd_companyPlaceholder: 'Empresa Ejemplo S.A.',
    cd_locationDetails: 'Detalles de Ubicación',
    cd_cityState: 'Ciudad, Estado',
    cd_cityStatePlaceholder: 'Ciudad de México, CDMX',
    cd_accountStatus: 'Estado de la Cuenta',
    cd_serviceAddress: 'Dirección de Servicio',
    cd_serviceAddressPlaceholder: 'Calle Principal 123, Suite 100',
    cd_profileSetup: 'Configuración del Perfil del Cliente',
    cd_profileSetupDesc: 'Después de crear el perfil del cliente, puede agregar detalles de equipos e historial de servicio desde la página de detalles del cliente.',
    cd_customFields: 'Campos Personalizados',
    cd_customFieldsDesc: 'Campos opcionales para rastrear datos adicionales del cliente.',
    cd_customField1: 'Campo Personalizado 1',
    cd_customField2: 'Campo Personalizado 2',
    cd_customField3: 'Campo Personalizado 3',
    cd_customField4: 'Campo Personalizado 4',
    cd_customField5: 'Campo Personalizado 5',
    cd_enterValue: 'Ingresar valor',
    cd_cancel: 'Cancelar',
    cd_createCustomer: 'Crear Cliente',
    cd_saving: 'Guardando...',
    // Customer profile modal
    cd_customerInfo: 'Información del Cliente',
    cd_edit: 'Editar',
    cd_save: 'Guardar',
    cd_tabDetails: 'Detalles',
    cd_tabLocations: 'Ubicaciones',
    cd_tabEquipment: 'Equipos',
    cd_tabHistory: 'Historial de Servicio',
    cd_billingAddress: 'Dirección de Facturación',
    cd_accountStats: 'Estadísticas de la Cuenta',
    cd_totalServices: 'Servicios Totales',
    cd_totalRevenue: 'Ingresos Totales',
    cd_equipmentCount: 'Equipos',
    cd_lastService: 'Último Servicio',
    cd_noEquipment: 'Sin equipos registrados',
    cd_noServiceHistory: 'Sin historial de servicio disponible',
    // Table columns
    cd_colName: 'Nombre',
    cd_colCompany: 'Empresa',
    cd_colEmail: 'Correo',
    cd_colPhone: 'Teléfono',
    cd_colLocation: 'Ubicación',
    cd_colStatus: 'Estado',
    cd_colLastService: 'Último Servicio',
    cd_colActions: 'Acciones',
    cd_viewProfile: 'Ver Perfil',
    cd_editCustomer: 'Editar Cliente',
    cd_archiveCustomer: 'Archivar Cliente',
    cd_noCustomers: 'No se encontraron clientes',
    cd_showing: 'Mostrando {count} de {total} clientes',
    // Validation messages
    cd_nameRequired: 'El nombre completo es obligatorio',
    cd_nameTooShort: 'El nombre debe tener al menos 2 caracteres',
    cd_emailRequired: 'La dirección de correo es obligatoria',
    cd_emailInvalid: 'Por favor ingrese una dirección de correo válida (ej. usuario@ejemplo.com)',
    cd_phoneRequired: 'El número de teléfono es obligatorio',
    cd_phoneInvalid: 'Por favor ingrese un número de teléfono válido',
    cd_locationRequired: 'La ubicación es obligatoria',
    cd_addressRequired: 'La dirección de servicio es obligatoria',
    cd_addressTooShort: 'Por favor ingrese una dirección completa',
    cd_saveError: 'Error al guardar el cliente. Por favor intente de nuevo.',

    // ── Technician Field Log ──
    tfl_title: 'Registro de Campo',
    tfl_clockIn: 'Registrar Entrada',
    tfl_clockOut: 'Registrar Salida',
    tfl_clockedIn: 'Actualmente Registrado',
    tfl_notClockedIn: 'Sin Registro Activo',
    tfl_startedAt: 'Iniciado a las',
    tfl_savedOffline: 'guardado sin conexión',
    tfl_offline: 'Sin Conexión',
    tfl_offlineTitle: 'Trabajando sin conexión',
    tfl_offlineDesc: 'Sus entradas se guardan localmente y se sincronizarán automáticamente cuando se reconecte.',
    tfl_entrySavedLocally: 'Entrada guardada localmente — se sincroniza automáticamente al reconectarse',
    tfl_tabToday: 'Hoy',
    tfl_tabHistory: 'Historial',
    tfl_todayLog: 'Registro de Hoy',
    tfl_totalTime: 'Tiempo Total',
    tfl_totalMileage: 'Kilometraje Total',
    tfl_noEntriesToday: 'Sin entradas por hoy',
    tfl_active: 'Activo',
    tfl_pendingSync: 'Pendiente de sincronización',
    tfl_savedOfflineSyncOnReconnect: 'Guardado sin conexión · se sincronizará al reconectarse',
    tfl_clockedInAt: 'Entrada registrada a las',
    tfl_clockOutQueued: 'Salida en cola',
    tfl_ticketMileage: 'Ticket y Kilometraje',
    tfl_clockInToAssociate: 'Registre entrada para asociar un ticket y registrar kilometraje.',
    tfl_associatedTicket: 'Ticket Asociado',
    tfl_searchTickets: 'Buscar tickets...',
    tfl_noTicketSelected: 'Sin ticket seleccionado',
    tfl_ticketSelectionOffline: 'Selección de ticket no disponible sin conexión',
    tfl_startMileage: 'Kilometraje Inicial',
    tfl_endMileage: 'Kilometraje Final',
    tfl_total: 'Total',
    tfl_notes: 'Notas',
    tfl_addJobNotes: 'Agregar notas del trabajo...',
    tfl_saveDetails: 'Guardar Detalles',
    tfl_saveOffline: 'Guardar Sin Conexión',
    tfl_equipment: 'Equipo',
    tfl_active_checkouts: 'activo(s)',
    tfl_checkOut: 'Registrar Salida de Equipo',
    tfl_newCheckOut: 'Nueva Salida de Equipo',
    tfl_selectEquipment: 'Seleccionar Equipo',
    tfl_loadingEquipment: 'Cargando equipo...',
    tfl_noEquipmentAvailable: 'No hay equipo de alquiler disponible',
    tfl_rentalPeriod: 'Período de Alquiler',
    tfl_hourly: 'Por Hora',
    tfl_daily: 'Diario',
    tfl_monthly: 'Mensual',
    tfl_rate: 'Tarifa',
    tfl_notSet: 'No establecida',
    tfl_confirmCheckout: 'Confirmar Salida',
    tfl_cancel: 'Cancelar',
    tfl_checkingOut: 'Registrando salida...',
    tfl_checkedOut: 'Salida registrada',
    tfl_elapsed: 'Transcurrido',
    tfl_estimatedCost: 'Costo Est.',
    tfl_checkIn: 'Registrar Entrada de Equipo',
    tfl_checkingIn: 'Registrando entrada...',
    tfl_noActiveCheckouts: 'Sin salidas de equipo activas',
    tfl_checkoutOfflineNote: 'Equipo registrado (sin conexión — se sincronizará al conectarse)',
    tfl_checkinOfflineNote: 'Entrada de equipo registrada (sin conexión — se sincronizará al conectarse)',

    // ── Capital Equipment Management ──
    cem_title: 'Gestión de Equipo de Capital',
    cem_addEquipment: 'Agregar Equipo',
    cem_editEquipment: 'Editar Equipo',
    cem_addCapitalEquipment: 'Agregar Equipo de Capital',
    cem_updateEquipmentDetails: 'Actualizar detalles del equipo',
    cem_registerNewAsset: 'Registrar nuevo activo de capital',
    cem_draftAutoSaved: 'Borrador guardado automáticamente',
    cem_equipmentName: 'Nombre del Equipo',
    cem_equipmentNamePlaceholder: 'ej. Unidad de Bomba Hidráulica A',
    cem_serialLicense: 'Número de Serie / Licencia',
    cem_serialLicensePlaceholder: 'ej. SN-2024-00123 o LIC-ABC-456',
    cem_datePurchased: 'Fecha de Compra',
    cem_purchasePrice: 'Precio de Compra',
    cem_fullyAmortizedDate: 'Fecha de Amortización Completa',
    cem_amortizationNote: 'Calculado automáticamente como 7 años desde la fecha de compra. Puede modificarlo.',
    cem_rentalEquipment: 'Equipo de Alquiler',
    cem_rentalEquipmentDesc: '¿Este equipo está disponible para alquiler?',
    cem_rentalRates: 'Tarifas de Alquiler',
    cem_rentalRatesNote: '(establezca al menos una)',
    cem_perHour: 'Por Hora',
    cem_perDay: 'Por Día',
    cem_perMonth: 'Por Mes',
    cem_customFields: 'Campos Personalizados',
    cem_customField1: 'Campo Personalizado 1',
    cem_customField2: 'Campo Personalizado 2',
    cem_customField3: 'Campo Personalizado 3',
    cem_customField4: 'Campo Personalizado 4',
    cem_customField5: 'Campo Personalizado 5',
    cem_enterValue: 'Ingresar valor',
    cem_cancel: 'Cancelar',
    cem_save: 'Guardar Equipo',
    cem_saving: 'Guardando...',
    cem_searchPlaceholder: 'Buscar por nombre, ID o serie/licencia...',
    cem_allRentalStatus: 'Todos los Estados de Alquiler',
    cem_rentalOnly: 'Solo Alquiler',
    cem_nonRentalOnly: 'Solo No Alquiler',
    cem_allAmortization: 'Toda la Amortización',
    cem_notYetAmortized: 'No Amortizado Aún',
    cem_fullyAmortized: 'Totalmente Amortizado',
    cem_approaching: 'Próximo (≤2 años)',
    cem_conditionGood: 'Bueno',
    cem_conditionFair: 'Regular',
    cem_conditionPoor: 'Malo',
    cem_conditionOutOfService: 'Fuera de Servicio',
    cem_statusRental: 'Alquiler',
    cem_statusNonRental: 'No Alquiler',
    cem_tabDetails: 'Detalles',
    cem_tabMaintenance: 'Mantenimiento',
    cem_tabRentalHistory: 'Historial de Alquiler',
    cem_deleteConfirm: '¿Está seguro de que desea eliminar este equipo? Esta acción no se puede deshacer.',
    cem_delete: 'Eliminar',
    cem_deleting: 'Eliminando...',
    cem_edit: 'Editar',
    // Validation
    cem_nameRequired: 'El nombre del equipo es obligatorio',
    cem_datePurchasedRequired: 'La fecha de compra es obligatoria',
    cem_purchasePriceRequired: 'El precio de compra es obligatorio',
    cem_amortizationDateRequired: 'La fecha de amortización es obligatoria',
    cem_rentalRateRequired: 'Se requiere al menos una tarifa de alquiler para equipo de alquiler',

    // ── Inventory Tracking ──
    inv_title: 'Seguimiento de Inventario',
    inv_searchPlaceholder: 'Buscar productos...',
    inv_allCategories: 'Todas las Categorías',
    inv_allStatuses: 'Todos los Estados',
    inv_allLocations: 'Todas las Ubicaciones',
    inv_statusInStock: 'En Stock',
    inv_statusLowStock: 'Stock Bajo',
    inv_statusOutOfStock: 'Sin Stock',
    inv_colProduct: 'Producto',
    inv_colSku: 'SKU',
    inv_colCategory: 'Categoría',
    inv_colQuantity: 'Cantidad',
    inv_colReorderLevel: 'Nivel de Reorden',
    inv_colStatus: 'Estado',
    inv_colLastRestocked: 'Último Reabastecimiento',
    inv_colActions: 'Acciones',
    inv_adjust: 'Ajustar',
    inv_noProducts: 'No se encontraron productos rastreables',
    inv_never: 'Nunca',
    // Stock Adjustment Modal
    inv_stockAdjustment: 'Ajuste de Stock',
    inv_transactionType: 'Tipo de Transacción',
    inv_stockIn: 'Entrada de Stock (Agregar)',
    inv_stockOut: 'Salida de Stock (Quitar)',
    inv_adjustment: 'Ajuste',
    inv_transfer: 'Transferencia',
    inv_reorder: 'Reorden',
    inv_quantity: 'Cantidad',
    inv_quantityPlaceholder: 'Ingresar cantidad',
    inv_quantityAddDesc: 'Ingresar cantidad a agregar',
    inv_quantityRemoveDesc: 'Ingresar número positivo para quitar del stock',
    inv_serviceLocation: 'Ubicación de Servicio',
    inv_selectLocation: 'Seleccionar una ubicación de servicio',
    inv_noLocationsOnFile: 'Sin ubicaciones de servicio registradas.',
    inv_addViaCustomers: 'Agregar vía Clientes',
    inv_loadingLocations: 'Cargando ubicaciones...',
    inv_assignedTechnician: 'Técnico Asignado',
    inv_technicianPlaceholder: 'Nombre del técnico (opcional)',
    inv_notes: 'Notas',
    inv_notesPlaceholder: 'Agregar notas sobre este ajuste...',
    inv_currentStock: 'Stock Actual',
    inv_reorderLevel: 'Nivel de Reorden',
    inv_status: 'Estado',
    inv_cancel: 'Cancelar',
    inv_saveAdjustment: 'Guardar Ajuste',
    inv_saving: 'Guardando...',
    // Validation
    inv_quantityRequired: 'La cantidad es obligatoria y no debe ser cero',
    inv_quantityInvalid: 'La cantidad debe ser un número válido',
    inv_locationRequired: 'La ubicación de servicio es obligatoria',
    inv_cannotRemove: 'No se pueden quitar {qty} artículos. Solo hay {available} disponibles.',

    // ── Personnel Management ──
    pm_title: 'Gestión de Personal',
    pm_addPersonnel: 'Agregar Personal',
    pm_searchPlaceholder: 'Buscar por nombre, correo o teléfono...',
    pm_filterByStatus: 'Filtrar por Estado',
    pm_filterBySkill: 'Filtrar por Habilidad',
    pm_allStatus: 'Todos los Estados',
    pm_statusAvailable: 'Disponible',
    pm_statusOnJob: 'En Trabajo',
    pm_statusOffDuty: 'Fuera de Servicio',
    pm_allSkills: 'Todas las Habilidades',
    pm_selectAll: 'Seleccionar Todo',
    pm_name: 'Nombre Completo',
    pm_email: 'Correo Electrónico',
    pm_phone: 'Número de Teléfono',
    pm_role: 'Rol',
    pm_status: 'Estado',
    pm_location: 'Ubicación',
    pm_emergencyContact: 'Contacto de Emergencia',
    pm_emergencyPhone: 'Teléfono de Emergencia',
    pm_skills: 'Habilidades',
    pm_skillsPlaceholder: 'ej. HVAC, Eléctrico (separados por coma)',
    pm_equipment: 'Equipo',
    pm_equipmentPlaceholder: 'ej. Taladro, Multímetro (separados por coma)',
    pm_cancel: 'Cancelar',
    pm_addMember: 'Agregar Miembro',
    pm_adding: 'Agregando...',
    pm_edit: 'Editar',
    pm_save: 'Guardar',
    pm_saving: 'Guardando...',
    pm_delete: 'Eliminar',
    pm_deleting: 'Eliminando...',
    pm_tabInfo: 'Información',
    pm_tabTimeLog: 'Registro de Tiempo',
    pm_contactInfo: 'Información de Contacto',
    pm_jobsCompleted: 'Trabajos Completados',
    pm_rating: 'Calificación',
    pm_addSkill: 'Agregar Habilidad',
    pm_addEquipment: 'Agregar Equipo',
    pm_noSkills: 'Sin habilidades registradas',
    pm_noEquipment: 'Sin equipo registrado',
    pm_deleteConfirm: '¿Está seguro de que desea eliminar a esta persona del equipo?',
    // Validation
    pm_nameRequired: 'El nombre completo es obligatorio',
    pm_nameTooShort: 'El nombre debe tener al menos 2 caracteres',
    pm_emailRequired: 'El correo electrónico es obligatorio',
    pm_emailInvalid: 'Por favor ingrese un correo electrónico válido',
    pm_phoneRequired: 'El número de teléfono es obligatorio',
    pm_phoneInvalid: 'Por favor ingrese un número de teléfono válido',
    pm_roleRequired: 'El rol es obligatorio',
    pm_emergencyPhoneInvalid: 'Por favor ingrese un número de teléfono válido',
    pm_fixErrors: 'Por favor corrija los errores antes de enviar.',

    // ── Technician Assignment ──
    ta_title: 'Asignación de Técnicos',
    ta_subtitle: 'Asignar técnicos calificados a tickets de servicio y gestionar horarios',
    ta_assignments: 'Asignaciones',
    ta_analytics: 'Analíticas',
    ta_integrityCheck: 'Verificación de Integridad',
    ta_technicians: 'Técnicos',
    ta_calendar: 'Calendario',
    ta_filters: 'Filtros',
    ta_resetAll: 'Restablecer Todo',
    ta_searchTechnicians: 'Buscar técnicos...',
    ta_status: 'Estado',
    ta_allStatus: 'Todos los Estados',
    ta_available: 'Disponible',
    ta_busy: 'Ocupado',
    ta_offline: 'Sin Conexión',
    ta_sortBy: 'Ordenar Por',
    ta_sortName: 'Nombre (A-Z)',
    ta_sortRating: 'Calificación (Mayor)',
    ta_locationFilters: 'Filtros de Ubicación',
    ta_customer: 'Cliente',
    ta_allCustomers: 'Todos los Clientes',
    ta_locationName: 'Nombre de Ubicación',
    ta_allLocationNames: 'Todos los Nombres de Ubicación',
    ta_showAvailableToday: 'Mostrar solo técnicos disponibles hoy',
    ta_availableTechnicians: 'Técnicos Disponibles',
    ta_loading: 'Cargando...',
    ta_noTechniciansMatch: 'Ningún técnico coincide con los filtros',
    ta_reset: 'Restablecer',
    ta_todaysAssignments: 'Asignaciones de Hoy',
    ta_createAssignment: 'Crear Asignación',
    ta_createAssignmentCount: 'Crear Asignación ({count})',
    ta_selectedTechnicians: '{count} técnico{plural} seleccionado{plural}',
    ta_primary: 'Principal',
    ta_setPrimary: 'Establecer como Principal',
    ta_clearSelection: 'Limpiar Selección',
    ta_filteringByLocation: 'Filtrando por ubicación:',
    ta_activeSkillFilters: 'Filtros de Habilidad Activos',
    ta_clearAllSkills: 'Limpiar Todas las Habilidades',
    ta_incompleteTickets: 'Tickets Incompletos',
    ta_noTickets: 'No hay tickets incompletos',
    ta_assignTicket: 'Asignar Ticket',
    ta_viewLocation: 'Ver Ubicación',
    // Assignment Form
    ta_formTitle: 'Crear Asignación',
    ta_draftAutoSaved: 'Borrador guardado automáticamente',
    ta_selectedTechniciansLabel: 'Técnicos Seleccionados',
    ta_selectTicket: 'Seleccionar Ticket',
    ta_searchSelectTicket: 'Buscar y seleccionar ticket...',
    ta_scheduledDate: 'Fecha Programada',
    ta_scheduledTime: 'Hora Programada',
    ta_timezone: 'Zona Horaria',
    ta_estimatedDuration: 'Duración Estimada',
    ta_priorityLevel: 'Nivel de Prioridad',
    ta_priorityLow: 'Prioridad Baja',
    ta_priorityMedium: 'Prioridad Media',
    ta_priorityHigh: 'Prioridad Alta',
    ta_priorityUrgent: 'Urgente',
    ta_dur1h: '1 hora',
    ta_dur2h: '2 horas',
    ta_dur3h: '3 horas',
    ta_dur4h: '4 horas',
    ta_dur6h: '6 horas',
    ta_dur8h: '8 horas',
    ta_assignmentNotes: 'Notas de Asignación',
    ta_notesPlaceholder: 'Agregar instrucciones especiales o notas...',
    ta_notesDesc: 'Notas opcionales para el equipo de técnicos',
    ta_cancel: 'Cancelar',
    ta_createAssignmentBtn: 'Crear Asignación',
    // Assignment Details
    ta_assignmentDetails: 'Detalles de la Asignación',
    ta_scheduleInfo: 'Información del Horario',
    ta_date: 'Fecha',
    ta_time: 'Hora',
    ta_duration: 'Duración',
    ta_location: 'Ubicación',
    ta_hours: 'horas',
    ta_assignedTechnicians: 'Técnicos Asignados',
    ta_assignmentNotesSec: 'Notas de Asignación',
    ta_editAssignment: 'Editar Asignación',
    ta_cancelAssignment: 'Cancelar Asignación',
    ta_markCompleted: 'Marcar como Completado',
    ta_ticketId: 'ID de Ticket',
    ta_priority: 'Prioridad',
    // Bulk Assign Modal
    ta_bulkTitle: 'Asignación Masiva de Técnicos',
    ta_bulkSubtitle: 'Asignando {count} ticket{plural} a un técnico',
    ta_selectedTickets: 'Tickets Seleccionados',
    ta_schedule: 'Horario (Opcional)',
    ta_bulkDate: 'Fecha',
    ta_bulkTime: 'Hora',
    ta_bulkDuration: 'Duración (hrs)',
    ta_conflictDetected: 'Conflicto de Horario Detectado',
    ta_conflictDesc: '{name} ya tiene {count} asignación{plural} que se superpone con este horario:',
    ta_conflictNote: 'Puede continuar, pero esto puede causar una doble reserva.',
    ta_selectTechnician: 'Seleccionar Técnico',
    ta_searchTechniciansPlaceholder: 'Buscar técnicos...',
    ta_noTechniciansFound: 'No se encontraron técnicos',
    ta_conflict: 'Conflicto',
    ta_activeJobs: 'trabajos activos',
    ta_assignBtn: 'Asignar',
    ta_assigning: 'Asignando...',
    // Analytics
    ta_totalAssignments: 'Total de Asignaciones',
    ta_allTime: 'Todo el tiempo',
    ta_completed: 'Completadas',
    ta_finishedJobs: 'Trabajos terminados',
    ta_active: 'Activas',
    ta_inProgress: 'En progreso',
    ta_avgRating: 'Calificación Promedio',
    ta_technicianWorkload: 'Carga de Trabajo del Técnico',
    ta_statusBreakdown: 'Desglose de Estado',
    ta_assignmentsByLocation: 'Asignaciones por Ubicación de Pozo',
    ta_technicianPerformance: 'Rendimiento del Técnico',
    ta_technicianCol: 'Técnico',
    ta_totalCol: 'Total',
    ta_activeCol: 'Activas',
    ta_completedCol: 'Completadas',
    ta_urgentCol: 'Urgentes',
    ta_ratingCol: 'Calificación',
    ta_statusCol: 'Estado',
    ta_noAssignmentData: 'Aún no hay datos de asignaciones',
    ta_noData: 'Aún no hay datos',

    // ── Quote Approval Queue ──
    qaq_title: 'Respuestas de Cotizaciones de Clientes',
    qaq_subtitle: 'Rastrear respuestas de aceptación y rechazo de clientes desde el Portal de Cotizaciones',
    qaq_refresh: 'Actualizar',
    // Stats bar
    qaq_total: 'Total Enviadas',
    qaq_accepted: 'Aceptadas',
    qaq_rejected: 'Rechazadas',
    qaq_pending: 'Pendientes',
    qaq_signed: 'Firmadas',
    qaq_respondedToday: 'Respondidas Hoy',
    qaq_acceptedValue: 'Valor Aceptado',
    // Filter panel
    qaq_search: 'Buscar',
    qaq_searchPlaceholder: 'Cotización #, nombre o correo del cliente...',
    qaq_response: 'Respuesta',
    qaq_allResponses: 'Todas las Respuestas',
    qaq_awaitingResponse: 'Esperando Respuesta',
    qaq_acceptedFilter: 'Aceptada',
    qaq_rejectedFilter: 'Rechazada',
    qaq_salesperson: 'Vendedor',
    qaq_allSalespersons: 'Todos',
    qaq_dateFrom: 'Desde',
    qaq_dateTo: 'Hasta',
    qaq_clear: 'Limpiar',
    // Table headers
    qaq_quoteNum: 'Cotización #',
    qaq_customer: 'Cliente',
    qaq_value: 'Valor',
    qaq_salespersonCol: 'Vendedor',
    qaq_customerResponse: 'Respuesta del Cliente',
    qaq_responded: 'Respondido',
    qaq_signedBy: 'Firmado Por',
    qaq_sent: 'Enviado',
    qaq_expires: 'Vence',
    qaq_details: 'Detalles',
    // Response badges
    qaq_badgeAccepted: 'Aceptada',
    qaq_badgeRejected: 'Rechazada',
    qaq_badgePending: 'Esperando Respuesta',
    qaq_quoteExpired: 'Cotización vencida',
    // Expanded row
    qaq_customerNotes: 'Notas del Cliente',
    qaq_noNotes: 'Sin notas proporcionadas',
    qaq_signature: 'Firma',
    qaq_signedByLabel: 'Firmado por:',
    qaq_method: 'Método:',
    qaq_signedAt: 'Firmado a las:',
    qaq_notYetSigned: 'Aún no firmado',
    qaq_quoteItems: 'Artículos de la Cotización',
    qaq_moreItems: '+{count} artículos más',
    qaq_noItems: 'Sin artículos',
    qaq_noResponses: 'No se encontraron respuestas',
    qaq_noResponsesDesc: 'Ninguna cotización con enlaces al portal del cliente coincide con sus filtros actuales.',
    // Detail modal
    qaq_quoteLabel: 'Cotización',
    qaq_acceptedByCustomer: 'Aceptada por el Cliente',
    qaq_rejectedByCustomer: 'Rechazada por el Cliente',
    qaq_awaitingCustomer: 'Esperando Respuesta del Cliente',
    qaq_quoteValue: 'Valor de la Cotización',
    qaq_responseDate: 'Fecha de Respuesta',
    qaq_signatureCaptured: 'Firma del Cliente Capturada',
    qaq_signatureMethod: 'Método de firma',
    qaq_quoteDetails: 'Detalles de la Cotización',
    qaq_salespersonLabel: 'Vendedor',
    qaq_created: 'Creado',
    qaq_expiresLabel: 'Vence',
    qaq_portalLink: 'Enlace del Portal',
    qaq_viewPortal: 'Ver portal',
    qaq_lineItems: 'Artículos',
    qaq_item: 'Artículo',
    qaq_qty: 'Cant.',
    qaq_unit: 'Unitario',
    qaq_total: 'Total',
    qaq_close: 'Cerrar',
    // Approval action modal
    qaq_approveTitle: 'Aprobar Cotización',
    qaq_rejectTitle: 'Rechazar Cotización',
    qaq_changesTitle: 'Solicitar Cambios',
    qaq_approveBtn: 'Aprobar Cotización',
    qaq_rejectBtn: 'Rechazar Cotización',
    qaq_changesBtn: 'Enviar Solicitud de Cambios',
    qaq_approveDesc: 'Aprobar cotización {num} para {customer}. El vendedor será notificado y la cotización se marcará como Aprobada.',
    qaq_rejectDesc: 'Rechazar cotización {num}. El vendedor será notificado con su motivo.',
    qaq_changesDesc: 'Solicitar cambios en la cotización {num}. El vendedor será notificado y la cotización volverá al estado Borrador.',
    qaq_commentRequired: 'Comentario (Requerido)',
    qaq_commentOptional: 'Comentario (Opcional)',
    qaq_approvePlaceholder: 'Notas de aprobación opcionales...',
    qaq_rejectPlaceholder: 'Motivo del rechazo (requerido)...',
    qaq_changesPlaceholder: 'Describa los cambios necesarios (requerido)...',
    qaq_cancelBtn: 'Cancelar',
    qaq_customerLabel: 'Cliente',
    qaq_totalValue: 'Valor Total',
    qaq_provideComment: 'Por favor proporcione un comentario.',
    // Approval queue table
    qaq_queueClear: '¡Cola limpia!',
    qaq_queueClearDesc: 'No hay cotizaciones pendientes de aprobación.',
    qaq_stage: 'Etapa',
    qaq_approvalChain: 'Cadena de Aprobación',
    qaq_waiting: 'Esperando',
    qaq_actions: 'Acciones',
    qaq_stagePending: 'Pendiente',
    qaq_stageApproved: 'Aprobado',
    qaq_stageRejected: 'Rechazado',
    qaq_stageChanges: 'Cambios Sol.',
    qaq_stageEscalated: 'Escalado',
    qaq_stageExpired: 'Vencido',
    qaq_quoteDetails2: 'Detalles de la Cotización',
    qaq_serviceRequestedDate: 'Fecha de Servicio Solicitada:',
    qaq_notes: 'Notas:',
    qaq_currentApprover: 'Aprobador Actual:',
    qaq_approvalHistory: 'Historial de Aprobación',
    qaq_noHistory: 'Sin historial aún',
    // Filter panel (approval)
    qaq_filterStage: 'Etapa de Aprobación',
    qaq_allStages: 'Todas las Etapas',
    qaq_filterSalesperson: 'Vendedor',
    qaq_allSalespersonsFilter: 'Todos los Vendedores',
    qaq_quoteValue2: 'Valor de Cotización',
    qaq_anyValue: 'Cualquier Valor',
    qaq_under1k: 'Menos de $1,000',
    qaq_1kTo10k: '$1,000 – $10,000',
    qaq_10kTo50k: '$10,000 – $50,000',
    qaq_over50k: 'Más de $50,000',
    qaq_activeFilters: 'Activos',
    qaq_clearFilters: 'Limpiar',

    // ── Reports & Export Center ──
    rep_title: 'Informes',
    rep_subtitle: 'Gestionar plantillas de informes y ver analíticas de rendimiento',
    rep_exportExcel: 'Exportar Excel',
    rep_exporting: 'Exportando...',
    rep_exportCSV: 'Exportar CSV',
    rep_downloadPDF: 'Descargar PDF',
    rep_savedTemplates: 'Plantillas Guardadas',
    rep_newTemplate: 'Nueva Plantilla',
    rep_cancelNewTemplate: 'Cancelar',
    rep_createNewTemplate: 'Crear Nueva Plantilla',
    rep_templateName: 'Nombre de la plantilla *',
    rep_templateDesc: 'Descripción (opcional)',
    rep_saveTemplate: 'Guardar Plantilla',
    rep_noSavedTemplates: 'Aún no hay plantillas guardadas',
    rep_noSavedTemplatesHint: 'Haga clic en "Nueva Plantilla" para crear una',
    rep_run: 'Ejecutar',
    rep_confirm: 'Confirmar',
    rep_cancel: 'Cancelar',
    rep_reportTemplates: 'Plantillas de Informes',
    rep_categoryAll: 'Todos',
    rep_categoryPerformance: 'Rendimiento',
    rep_categoryOperations: 'Operaciones',
    rep_categoryFinance: 'Finanzas',
    rep_categoryInventory: 'Inventario',
    rep_categorySupport: 'Soporte',
    rep_categorySales: 'Ventas',
    rep_downloadReport: 'Descargar Informe',
    rep_saveToTemplates: 'Guardar en Plantillas',
    rep_performanceAnalytics: 'Analíticas de Rendimiento',
    rep_tabPerformance: 'Rendimiento',
    rep_tabTickets: 'Tickets',
    rep_tabRevenue: 'Ingresos',
    rep_totalTicketsClosed: 'Total de Tickets Cerrados',
    rep_avgResolutionTime: 'Tiempo Promedio de Resolución',
    rep_openInvoices: 'Facturas Abiertas',
    rep_activeTechnicians: 'Técnicos Activos',
    rep_accessRestricted: 'Acceso Restringido',
    rep_noPermission: 'No tiene permiso para ver Informes.',
    rep_quoteConversionAnalytics: 'Analíticas de Conversión de Cotizaciones',
    rep_equipmentUtilization: 'Utilización de Equipos',

    // ── Superadmin Dashboard ──
    sad_badge: 'SUPERADMIN',
    sad_title: 'Resumen del Sistema',
    sad_subtitle: 'Gestión de plataforma entre organizaciones',
    sad_manageOrgs: 'Gestionar Organizaciones',
    sad_totalOrgs: 'Total de Organizaciones',
    sad_activeOrgs: 'Organizaciones Activas',
    sad_totalUsers: 'Total de Usuarios',
    sad_newOrgsMonth: 'Nuevas Orgs Este Mes',
    sad_recentOrgs: 'Organizaciones Recientes',
    sad_viewAll: 'Ver todas',
    sad_noOrgs: 'No se encontraron organizaciones',
    sad_quickActions: 'Acciones Rápidas',
    sad_addOrg: 'Agregar Organización',
    sad_userRoleMgmt: 'Gestión de Usuarios y Roles',
    sad_auditLog: 'Registro de Auditoría',
    sad_orgSettings: 'Configuración de Organización',
    sad_orgIsolationAudit: 'Auditoría de Aislamiento de Org',
    sad_systemHealth: 'Salud del Sistema',
    sad_database: 'Base de Datos',
    sad_authService: 'Servicio de Autenticación',
    sad_storage: 'Almacenamiento',
    sad_edgeFunctions: 'Funciones Edge',
    sad_operational: 'Operacional',
    sad_allTime: 'en total',
    sad_currentlyActive: 'actualmente activas',
    sad_acrossAllOrgs: 'en todas las orgs',
    sad_thisMonth: 'este mes',

    // ── Org Admin Dashboard ──
    oad_badge: 'ADMIN DE ORG',
    oad_title: 'Panel de Organización',
    oad_subtitle: 'Rendimiento del equipo y supervisión organizacional',
    oad_quoteMgmt: 'Gestión de Cotizaciones',
    oad_ticketMgmt: 'Gestión de Tickets',
    oad_addQuote: 'Agregar Cotización',
    oad_createTicket: 'Crear Nuevo Ticket',
    oad_openTickets: 'Tickets Abiertos',
    oad_needAttention: 'requieren atención',
    oad_pendingApprovals: 'Aprobaciones Pendientes',
    oad_awaitingReview: 'en espera de revisión',
    oad_sales: 'Ventas',
    oad_viewAllQuotes: 'Ver todas las cotizaciones',
    oad_quotesAwaiting: 'Cotizaciones en Espera de Respuesta',
    oad_quotesAccepted: 'Cotizaciones Aceptadas',
    oad_valueAccepted: 'Valor de Cotizaciones Aceptadas',
    oad_quotesRejected: 'Cotizaciones Rechazadas',
    oad_ops: 'Operaciones',
    oad_viewAllTickets: 'Ver todos los tickets',
    oad_openSupportReqs: 'Solicitudes de Soporte Abiertas',
    oad_ticketsPending: 'Tickets Pendientes',
    oad_ticketsOpen: 'Tickets Abiertos',
    oad_upcomingSchedule: 'Próximo Calendario',
    oad_ticketNum: 'Ticket #',
    oad_company: 'Empresa',
    oad_location: 'Ubicación',
    oad_scheduledDateTime: 'Fecha y Hora Programada',
    oad_technicians: 'Técnicos',
    oad_status: 'Estado',
    oad_noUpcoming: 'No hay trabajos programados, pendientes o activos a partir de hoy.',
    oad_pendingApprovalsBanner: 'Aprobaciones Pendientes',
    oad_quotesAwaitingApproval: '{n} cotización{s} en espera de su aprobación',
    oad_reviewNow: 'Revisar ahora',

    // ── Technician Dashboard ──
    tdb_badge: 'TÉCNICO',
    tdb_title: 'Mi Panel',
    tdb_viewAllTickets: 'Ver Todos los Tickets',
    tdb_totalAssigned: 'Total Asignados',
    tdb_completed: 'Completados',
    tdb_completionPct: '% de Completado',
    tdb_avgRating: 'Calificación Prom.',
    tdb_todayAssignments: 'Asignaciones de Hoy',
    tdb_viewAll: 'Ver todos',
    tdb_noTickets: 'No hay tickets asignados hoy',
    tdb_noTicketsHint: 'Vuelve más tarde o ve todos los tickets',
    tdb_startJob: 'Iniciar Trabajo',
    tdb_starting: 'Iniciando…',
    tdb_completeJob: 'Completar Trabajo',
    tdb_quickActions: 'Acciones Rápidas',
    tdb_myTickets: 'Mis Tickets',
    tdb_mobileApp: 'App Móvil',
    tdb_supportRequests: 'Solicitudes de Soporte',
    tdb_inventory: 'Inventario',
    tdb_performance: 'Rendimiento',
    tdb_completionRate: 'Tasa de Completado',
    tdb_customerRating: 'Calificación del Cliente',

    // ── Organization Onboarding Wizard ──
    oow_superadminRequired: 'Se Requiere Acceso de Superadmin',
    oow_superadminDesc: 'El proceso de incorporación de organizaciones está restringido solo a superadmins.',
    oow_backToDashboard: 'Volver al Panel',
    oow_pageTitle: 'Configuración de Nueva Organización',
    oow_stepOf: 'Paso {current} de {total}',
    oow_back: 'Atrás',
    oow_next: 'Siguiente',
    oow_launching: 'Lanzando...',
    oow_launchOrg: 'Lanzar Organización',
    oow_createdTitle: '¡Organización Creada!',
    oow_createdDesc: 'ha sido aprovisionada exitosamente.',
    oow_createdEmailSent: 'Se ha enviado un correo de bienvenida a',
    oow_createdEmailSentSuffix: 'con instrucciones de inicio de sesión.',
    oow_viewOrgs: 'Ver Organizaciones',
    oow_onboardAnother: 'Incorporar Otra',
    // WizardProgress
    oow_stepCompleted: 'completados',
    oow_minRemaining: '~{n} min restantes',
    oow_stepOrgDetails: 'Detalles de Org',
    oow_stepOrgDetailsDesc: 'Nombre de empresa, dirección e información de contacto',
    oow_stepSubscription: 'Suscripción',
    oow_stepSubscriptionDesc: 'Elegir plan y ciclo de facturación',
    oow_stepBranding: 'Marca',
    oow_stepBrandingDesc: 'Logo, colores e identidad visual',
    oow_stepAdminUser: 'Usuario Admin',
    oow_stepAdminUserDesc: 'Designar el administrador de la organización',
    oow_stepSettings: 'Configuración',
    oow_stepSettingsDesc: 'Valores predeterminados, SLA y notificaciones',
    oow_stepReview: 'Revisión',
    oow_stepReviewDesc: 'Confirmar y lanzar la organización',
    // OrgDetailsStep
    oow_orgDetailsTitle: 'Detalles de la Organización',
    oow_orgDetailsSubtitle: 'Ingrese la información básica para la nueva organización.',
    oow_prefilledStripe: 'Pre-completado desde Stripe Checkout',
    oow_prefilledStripeDesc: 'El nombre de la organización y el correo de contacto se tomaron del registro de prueba. Revise y actualice según sea necesario.',
    oow_companyName: 'Nombre de la Empresa',
    oow_industry: 'Industria',
    oow_selectIndustry: 'Seleccionar industria...',
    oow_timezone: 'Zona Horaria',
    oow_address: 'Dirección',
    oow_city: 'Ciudad',
    oow_state: 'Estado',
    oow_zipCode: 'Código Postal',
    oow_country: 'País',
    oow_contactEmail: 'Correo de Contacto Principal',
    oow_phone: 'Teléfono',
    oow_website: 'Sitio Web',
    oow_required: 'requerido',
    // SubscriptionStep
    oow_subscriptionTitle: 'Plan de Suscripción',
    oow_subscriptionSubtitle: 'Seleccione el nivel de suscripción para esta organización.',
    oow_prefilledPlan: 'Plan Pre-seleccionado desde Stripe Checkout',
    oow_prefilledPlanDesc: 'El plan se tomó del registro de prueba. Puede cambiarlo a continuación si es necesario.',
    oow_monthly: 'Mensual',
    oow_annual: 'Anual',
    oow_save20: 'Ahorra 20%',
    oow_perMonth: '/mes',
    oow_mostPopular: 'Más Popular',
    oow_enterprise: 'Empresarial',
    // BrandingStep
    oow_brandingTitle: 'Marca',
    oow_brandingSubtitle: 'Personalice la identidad visual de la organización.',
    oow_orgLogo: 'Logo de la Organización',
    oow_dropLogo: 'Suelte el logo aquí o haga clic para subir',
    oow_logoFormats: 'PNG, JPG, SVG hasta 2MB',
    oow_clickChangeLogo: 'Haga clic para cambiar el logo',
    oow_primaryColor: 'Color Principal de Marca',
    oow_preview: 'Vista Previa',
    oow_primaryButton: 'Botón Principal',
    oow_secondary: 'Secundario',
    // AdminUserStep
    oow_adminTitle: 'Usuario Admin Inicial',
    oow_adminSubtitle: 'Cree el primer administrador para esta organización. Se enviará un correo de bienvenida automáticamente.',
    oow_prefilledEmail: 'Correo Pre-completado desde Stripe Checkout',
    oow_prefilledEmailDesc: 'El correo del admin se tomó del registro de prueba. Revise y actualice si es necesario.',
    oow_autoRoleAssignment: 'Asignación Automática de Rol',
    oow_autoRoleDesc: 'Este usuario recibirá el rol org_admin con acceso completo a las funciones de la organización.',
    oow_firstName: 'Nombre',
    oow_lastName: 'Apellido',
    oow_emailAddress: 'Correo Electrónico',
    oow_role: 'Rol',
    oow_orgAdminRole: 'Administrador de Organización',
    oow_orgAdminRoleDesc: 'Acceso completo a todas las funciones de la org',
    oow_sendWelcomeEmail: 'Enviar correo de bienvenida con instrucciones de inicio de sesión',
    // DefaultSettingsStep
    oow_defaultsTitle: 'Configuración Predeterminada',
    oow_defaultsSubtitle: 'Configure los valores operativos predeterminados para esta organización.',
    oow_slaConfig: 'Configuración de SLA',
    oow_defaultSlaHours: 'Horas de SLA Predeterminadas',
    oow_slaHoursHint: 'Horas antes de la escalada del ticket',
    oow_escalationWarning: 'Advertencia de Escalada (horas)',
    oow_escalationHint: 'Notificar antes del incumplimiento del SLA',
    oow_ticketDefaults: 'Valores Predeterminados de Ticket',
    oow_defaultPriority: 'Prioridad Predeterminada',
    oow_autoAssignment: 'Asignación Automática',
    oow_priorityLow: 'Baja',
    oow_priorityMedium: 'Media',
    oow_priorityHigh: 'Alta',
    oow_priorityCritical: 'Crítica',
    oow_disabled: 'Deshabilitado',
    oow_enabled: 'Habilitado',
    oow_notifications: 'Notificaciones',
    oow_emailAlerts: 'Alertas por Correo',
    oow_emailAlertsDesc: 'Enviar notificaciones por correo para actualizaciones de tickets',
    oow_dailySummary: 'Resumen Diario',
    oow_dailySummaryDesc: 'Enviar resumen diario de actividad',
    // ReviewStep
    oow_reviewTitle: 'Revisar y Lanzar',
    oow_reviewSubtitle: 'Revise su configuración antes de crear la organización.',
    oow_readyToLaunch: 'Listo para Lanzar',
    oow_readyToLaunchDesc: 'Al hacer clic en "Lanzar Organización" se creará la organización, se aprovisionará la base de datos y se enviará el correo de bienvenida al admin.',
    oow_reviewOrg: 'Organización',
    oow_reviewSubscription: 'Suscripción',
    oow_reviewBranding: 'Marca',
    oow_reviewAdminUser: 'Usuario Admin',
    oow_reviewSettings: 'Configuración',
    oow_reviewCompanyName: 'Nombre de la Empresa',
    oow_reviewIndustry: 'Industria',
    oow_reviewTimezone: 'Zona Horaria',
    oow_reviewAddress: 'Dirección',
    oow_reviewContactEmail: 'Correo de Contacto',
    oow_reviewPhone: 'Teléfono',
    oow_reviewPlan: 'Plan',
    oow_reviewBillingCycle: 'Ciclo de Facturación',
    oow_reviewAnnual: 'Anual (20% de descuento)',
    oow_reviewMonthly: 'Mensual',
    oow_reviewPrimaryColor: 'Color Principal',
    oow_reviewLogo: 'Logo',
    oow_reviewLogoNotUploaded: 'No subido',
    oow_reviewName: 'Nombre',
    oow_reviewEmail: 'Correo',
    oow_reviewRole: 'Rol',
    oow_reviewWelcomeEmail: 'Correo de Bienvenida',
    oow_reviewYes: 'Sí',
    oow_reviewNo: 'No',
    oow_reviewSlaHours: 'Horas de SLA',
    oow_reviewEscalation: 'Advertencia de Escalada',
    oow_reviewDefaultPriority: 'Prioridad Predeterminada',
    oow_reviewAutoAssignment: 'Asignación Automática',
    oow_reviewEmailAlerts: 'Alertas por Correo',
    oow_reviewDailySummary: 'Resumen Diario',
    oow_reviewHours: 'horas',

    // ── Interactive Demo Onboarding Guide ──
    idg_title: 'Primeros Pasos',
    idg_stepOf: 'Paso {current} de {total}',
    idg_skipGuide: 'Omitir guía',
    idg_allSteps: 'Todos los Pasos',
    idg_previous: 'Anterior',
    idg_next: 'Siguiente',
    idg_finish: 'Finalizar',
    idg_completedTitle: '¡Todo listo!',
    idg_completedDesc: 'Has completado la guía de incorporación. Estás listo para usar SERVICEJOCKEY al máximo.',
    idg_goToDashboard: 'Ir al Panel',
    idg_replayGuide: 'Repetir Guía',
    // Step titles & descriptions
    idg_step1Title: 'Bienvenido a SERVICEJOCKEY',
    idg_step1Desc: 'Tu plataforma todo-en-uno para gestión de servicios de campo. Esta guía te mostrará las funciones principales.',
    idg_step2Title: 'Resumen del Panel',
    idg_step2Desc: 'El panel te da una vista en tiempo real de tus operaciones: tickets abiertos, cotizaciones pendientes, ingresos y actividad del equipo.',
    idg_step2Action: 'Ver Panel',
    idg_step3Title: 'Crear una Solicitud de Soporte',
    idg_step3Desc: 'Las solicitudes de soporte son el punto de partida para los problemas de clientes. Ve a Ventas → Solicitudes de Soporte para registrar una nueva.',
    idg_step3Action: 'Ir a Solicitudes de Soporte',
    idg_step4Title: 'Convertir a Ticket',
    idg_step4Desc: 'Una vez revisada la solicitud de soporte, conviértela en un ticket de trabajo. Abre cualquier solicitud y haz clic en "Convertir a Ticket".',
    idg_step4Action: 'Ver Tickets',
    idg_step5Title: 'Asignar un Técnico',
    idg_step5Desc: 'Asigna el técnico adecuado a cada ticket según habilidades y disponibilidad usando la pantalla de Asignación de Técnicos.',
    idg_step5Action: 'Asignación de Técnicos',
    idg_step6Title: 'Generar una Cotización',
    idg_step6Desc: 'Crea cotizaciones profesionales para clientes. Agrega líneas de artículos, aplica precios y envía para aprobación o directamente al cliente.',
    idg_step6Action: 'Gestión de Cotizaciones',
    idg_step7Title: 'Crear una Factura',
    idg_step7Desc: 'Convierte cotizaciones aprobadas o tickets completados en facturas. Rastrea el estado de pago y envía recordatorios automáticamente.',
    idg_step7Action: 'Gestión de Facturas',
    idg_step8Title: 'Ejecutar un Informe',
    idg_step8Desc: 'Obtén información sobre tu negocio con informes integrados que cubren ingresos, rendimiento de técnicos y utilización de equipos.',
    idg_step8Action: 'Ver Informes',

    // ── Account Security Center ──
    asc_title: 'Centro de Seguridad de Cuenta',
    asc_subtitle: 'Gestiona la seguridad de tu cuenta, sesiones y actividad',
    asc_tabOverview: 'Resumen',
    asc_tabPassword: 'Contraseña',
    asc_tabSessions: 'Sesiones',
    asc_tabHistory: 'Historial de Acceso',
    asc_tab2FA: '2FA',
    asc_tabAdmin: 'Resumen Admin',
    asc_statActiveSessions: 'Sesiones Activas',
    asc_statSuccessfulLogins: 'Accesos Exitosos',
    asc_statFailedAttempts: 'Intentos Fallidos',
    asc_statSecurityStatus: 'Estado de Seguridad',
    asc_statusAtRisk: 'En Riesgo',
    asc_statusGood: 'Bueno',
    asc_accountInfo: 'Información de Cuenta',
    asc_email: 'Correo',
    asc_name: 'Nombre',
    asc_role: 'Rol',
    asc_lastSignIn: 'Último Acceso',
    asc_quickActions: 'Acciones Rápidas',
    asc_changePassword: 'Cambiar Contraseña',
    asc_manageSessions: 'Gestionar Sesiones',
    asc_viewLoginHistory: 'Ver Historial de Acceso',
    asc_twoFactorAuth: 'Autenticación de Dos Factores',
    // Change Password
    asc_changePasswordTitle: 'Cambiar Contraseña',
    asc_changePasswordSubtitle: 'Actualiza la contraseña de tu cuenta',
    asc_currentPassword: 'Contraseña Actual',
    asc_currentPasswordPlaceholder: 'Ingresa la contraseña actual',
    asc_newPassword: 'Nueva Contraseña',
    asc_newPasswordPlaceholder: 'Ingresa la nueva contraseña',
    asc_confirmNewPassword: 'Confirmar Nueva Contraseña',
    asc_confirmPasswordPlaceholder: 'Confirma la nueva contraseña',
    asc_passwordStrength: 'Fortaleza',
    asc_passwordsNoMatch: 'Las contraseñas no coinciden',
    asc_updatePassword: 'Actualizar Contraseña',
    asc_updating: 'Actualizando...',
    asc_strengthVeryWeak: 'Muy Débil',
    asc_strengthWeak: 'Débil',
    asc_strengthFair: 'Regular',
    asc_strengthStrong: 'Fuerte',
    asc_strengthVeryStrong: 'Muy Fuerte',
    asc_errPasswordMismatch: 'Las contraseñas nuevas no coinciden.',
    asc_errPasswordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
    asc_errPasswordSame: 'La nueva contraseña debe ser diferente a la actual.',
    asc_errCurrentIncorrect: 'La contraseña actual es incorrecta.',
    asc_successPasswordChanged: 'Contraseña cambiada exitosamente.',
    // Active Sessions
    asc_activeSessions: 'Sesiones Activas',
    asc_activeSessionsCount: '{n} sesión activa',
    asc_activeSessionsCountPlural: '{n} sesiones activas',
    asc_revokeAllOthers: 'Revocar Todas las Demás',
    asc_noActiveSessions: 'No se encontraron sesiones activas',
    asc_sessionCurrent: 'Actual',
    asc_sessionSignOut: 'Cerrar Sesión',
    asc_sessionRevoke: 'Revocar',
    asc_sessionRevokedSuccess: 'Sesión revocada exitosamente.',
    asc_allOthersRevoked: 'Todas las demás sesiones revocadas.',
    asc_errLoadSessions: 'Error al cargar sesiones.',
    asc_errRevokeSessions: 'Error al revocar sesiones.',
    // Login History
    asc_loginHistory: 'Historial de Acceso',
    asc_loginHistorySubtitle: 'Últimos 10 intentos de autenticación',
    asc_noLoginHistory: 'No hay historial de acceso disponible',
    asc_loginHistoryHint: 'La actividad de acceso aparecerá aquí después de tu próximo inicio de sesión',
    asc_successfulLogin: 'Acceso Exitoso',
    asc_failedLoginAttempt: 'Intento de Acceso Fallido',
    asc_failureReason: 'Motivo',
    asc_errLoadHistory: 'Error al cargar el historial de acceso.',
    // 2FA
    asc_2faTitle: 'Autenticación de Dos Factores',
    asc_2faSubtitle: 'Agrega una capa adicional de seguridad a tu cuenta',
    asc_2faComingSoon: 'Próximamente',
    asc_2faDesc: 'La autenticación de dos factores agrega una capa adicional de seguridad a tu cuenta al requerir un código de verificación además de tu contraseña al iniciar sesión.',
    asc_2faAuthApp: 'Aplicación Autenticadora',
    asc_2faAuthAppDesc: 'Usa Google Authenticator, Authy u aplicaciones similares',
    asc_2faEmail: 'Verificación por Correo',
    asc_2faEmailDesc: 'Recibe un código en tu dirección de correo registrada',
    asc_2faSMS: 'Verificación por SMS',
    asc_2faSMSDesc: 'Recibe un código en tu teléfono móvil',
    asc_2faRecovery: 'Opciones de Recuperación',
    asc_2faRecoveryDesc: 'Cuando el 2FA esté habilitado, recibirás códigos de recuperación de respaldo para acceder a tu cuenta si pierdes acceso a tu dispositivo de autenticación. Guarda estos códigos de forma segura.',
    asc_2faEnable: 'Habilitar 2FA — Próximamente',
    // Security Recommendations
    asc_securityRecs: 'Recomendaciones de Seguridad',
    asc_securityRecsSubtitle: 'Información de seguridad personalizada',
    asc_accountSecure: 'Tu cuenta parece segura',
    asc_noIssues: 'No se detectaron problemas de seguridad en este momento.',
    asc_recCount: '{n} recomendación para tu cuenta',
    asc_recCountPlural: '{n} recomendaciones para tu cuenta',
    asc_severityTip: 'Consejo',
    asc_severityWarning: 'Advertencia',
    asc_severityAlert: 'Alerta',
    asc_recFailedLoginsTitle: 'Múltiples Intentos de Acceso Fallidos',
    asc_recManySessionsTitle: 'Múltiples Sesiones Activas',
    asc_recMultipleLocationsTitle: 'Accesos desde Múltiples Países',
    asc_rec2FATitle: 'Habilitar Autenticación de Dos Factores',
    asc_recChangePassword: 'Cambiar Contraseña',
    asc_recReviewSessions: 'Revisar Sesiones',
    asc_recViewHistory: 'Ver Historial',
    asc_recLearnMore: 'Más Información',
    // Admin Security Overview
    asc_adminOverviewTitle: 'Resumen de Seguridad de la Plataforma',
    asc_adminOrgOverviewTitle: 'Resumen de Seguridad de la Organización',
    asc_adminOverviewSubtitle: 'Monitorea la actividad de acceso y patrones sospechosos',
    asc_filterAll: 'Todos',
    asc_filterFailed: 'Fallidos',
    asc_filterSuspicious: 'Sospechosos',
    asc_statTotalEvents: 'Total de Eventos',
    asc_statFailedAttemptsStat: 'Intentos Fallidos',
    asc_statAffectedUsers: 'Usuarios Afectados',
    asc_userActivity: 'Actividad de Usuarios',
    asc_events: 'eventos',
    asc_failed: 'fallido(s)',
    asc_noActivity: 'No se encontró actividad',
    asc_errLoadOverview: 'Error al cargar el resumen de seguridad.',

    // ── Organization Settings ──
    os_title: 'Configuración de la Organización',
    os_subtitle: 'Configura el perfil, marca y preferencias operativas de tu organización',
    os_accessRestricted: 'Acceso Restringido',
    os_accessRestrictedDesc: 'La Configuración de la Organización solo es accesible para Administradores de Org y Superadministradores.',
    os_saveChanges: 'Guardar Cambios',
    os_saving: 'Guardando...',
    os_saved: 'Guardado',
    os_saveError: 'Error al guardar la configuración.',
    // Tab labels
    os_tabProfile: 'Perfil de la Organización',
    os_tabBranding: 'Marca',
    os_tabInvitations: 'Invitaciones',
    os_tabNotifications: 'Notificaciones',
    os_tabCommunication: 'Comunicación',
    os_tabTicketDefaults: 'Valores Predeterminados de Tickets',
    os_tabServiceTypes: 'Tipos de Servicio',
    os_tabSubscription: 'Suscripción',
    os_tabBilling: 'Contacto de Facturación',
    os_tabDataRetention: 'Retención de Datos',
    os_tabDemoSettings: 'Configuración Demo',
    // Org Profile Tab
    os_profileTitle: 'Perfil de la Organización',
    os_profileSubtitle: 'Información principal de la empresa y datos de contacto',
    os_companyLogo: 'Logo de la Empresa',
    os_logoUploadHint: 'Arrastra el logo aquí o haz clic para subir',
    os_logoFormatHint: 'PNG, JPG, WebP — máx. 5MB',
    os_uploading: 'Subiendo...',
    os_removeLogo: 'Eliminar logo',
    os_companyName: 'Nombre de la Empresa *',
    os_phone: 'Teléfono',
    os_email: 'Correo',
    os_website: 'Sitio Web',
    os_address: 'Dirección',
    os_addressLine1: 'Línea de Dirección 1',
    os_city: 'Ciudad',
    os_state: 'Estado',
    os_zip: 'Código Postal',
    os_country: 'País',
    os_timezone: 'Zona Horaria',
    os_industry: 'Industria',
    os_selectIndustry: 'Seleccionar industria...',
    os_errLogoType: 'Por favor selecciona un archivo de imagen.',
    os_errLogoSize: 'La imagen debe ser menor a 5MB.',
    os_errLogoUpload: 'Error al subir el logo.',
    // Branding Tab
    os_brandingTitle: 'Configuración de Marca',
    os_brandingSubtitle: 'Personaliza los colores de tu marca e identidad visual en todas las pantallas',
    os_primaryColor: 'Color Principal de Marca',
    os_livePreview: 'Vista Previa en Vivo',
    os_documentPreview: 'Vista Previa del Encabezado del Documento',
    // Notifications Tab
    os_notifTitle: 'Preferencias de Notificaciones',
    os_notifSubtitle: 'Configura alertas de correo, reglas de escalada y notificaciones automáticas',
    os_emailAlertSettings: 'Configuración de Alertas por Correo',
    os_emailAlertsEnabled: 'Alertas por Correo Habilitadas',
    os_emailAlertsDesc: 'Enviar notificaciones por correo para actualizaciones y asignaciones de tickets',
    os_dailySummaryEmail: 'Correo de Resumen Diario',
    os_dailySummaryDesc: 'Recibe un resumen diario de toda la actividad y elementos pendientes',
    os_newTicketNotif: 'Notificaciones de Nuevos Tickets',
    os_newTicketNotifDesc: 'Alertar cuando se creen nuevas solicitudes de soporte o tickets',
    os_assignmentNotif: 'Notificaciones de Asignación',
    os_assignmentNotifDesc: 'Notificar a los técnicos cuando se les asignen tickets',
    os_escalationThresholds: 'Umbrales de Escalada',
    os_criticalEscalation: 'Escalada de Prioridad Crítica (horas)',
    os_highEscalation: 'Escalada de Prioridad Alta (horas)',
    os_standardEscalation: 'Escalada de Prioridad Estándar (horas)',
    os_hoursAfterScheduled: 'horas después del tiempo programado',
    os_emailTemplatesInfo: 'Plantillas de Correo',
    os_emailTemplatesDesc: 'Personaliza las plantillas de notificación por correo en la sección de Plantillas de Correo en Configuración.',
    os_paymentReminders: 'Recordatorios de Pago de Facturas',
    os_enableReminders: 'Habilitar Recordatorios de Pago',
    os_enableRemindersDesc: 'Enviar automáticamente correos de recordatorio a clientes por facturas no pagadas',
    os_reminderOnDueDate: 'Recordatorio en Fecha de Vencimiento',
    os_reminderOnDueDateDesc: 'Enviar un correo de recordatorio en la fecha de vencimiento de la factura',
    os_daysBeforeDue: 'Días Antes del Vencimiento',
    os_daysBeforeDueDesc: 'Enviar recordatorios esta cantidad de días antes del vencimiento (separados por comas, ej. 7,3,1)',
    os_overdueReminders: 'Recordatorios de Vencimiento',
    os_overdueRemindersDesc: 'Enviar recordatorios de vencimiento en estos intervalos después de la fecha de vencimiento (ej. 3,7,14)',
    os_billingContactEmail: 'Correo de Contacto de Facturación (Interno)',
    os_billingContactEmailDesc: 'Recibir notificaciones internas cuando se reciban pagos',
    os_reminderPortalInfo: 'Los correos de recordatorio incluyen un enlace directo al portal de pago del cliente. Los clientes pueden pagar en línea sin iniciar sesión.',
    // Billing Contact Tab
    os_billingTitle: 'Contacto de Facturación',
    os_billingSubtitle: 'Información de contacto financiero separada para procesamiento de pagos y facturación',
    os_billingWarning: 'Los datos de contacto de facturación se usan para el procesamiento de pagos y comunicaciones financieras. Mantén esta información precisa y actualizada.',
    os_billingContactName: 'Nombre del Contacto de Facturación',
    os_billingEmail: 'Correo de Facturación',
    os_billingPhone: 'Teléfono de Facturación',
    os_billingAddress: 'Dirección de Facturación',
    os_billingAddressPlaceholder: 'Igual que la dirección de la empresa si está en blanco',
    os_paymentInfo: 'Información de Pago',
    os_paymentInstructions: 'Instrucciones de Pago',
    os_paymentInstructionsPlaceholder: 'Por favor haga cheques a nombre de... Se aceptan pagos ACH...',
    os_bankName: 'Nombre del Banco',
    os_accountLast4: 'Cuenta (últimos 4)',
    os_routingLast4: 'Enrutamiento (últimos 4)',
    os_defaultPaymentTerms: 'Términos de Pago Predeterminados',
    // Ticket Defaults Tab
    os_ticketDefaultsTitle: 'Configuración Predeterminada de Tickets',
    os_ticketDefaultsSubtitle: 'Configura horas de SLA, reglas de asignación automática y comportamiento predeterminado de tickets',
    os_slaConfig: 'Configuración de SLA',
    os_slaCritical: 'SLA de Prioridad Crítica (horas)',
    os_slaHigh: 'SLA de Prioridad Alta (horas)',
    os_slaMedium: 'SLA de Prioridad Media (horas)',
    os_slaLow: 'SLA de Prioridad Baja (horas)',
    os_autoAssignment: 'Reglas de Asignación Automática',
    os_autoAssignEnabled: 'Habilitado',
    os_addNewRule: 'Agregar Nueva Regla',
    os_conditionPlaceholder: 'Condición (ej., prioridad=crítica)',
    os_assignToPlaceholder: 'Asignar a (ej., líder de equipo)',
    os_addRule: 'Agregar Regla',
    os_errRuleRequired: 'Se requieren tanto la condición como el destino de asignación.',
    os_when: 'Cuando',
    os_assignTo: 'Asignar a',
    os_defaultValues: 'Valores Predeterminados',
    os_defaultPriority: 'Prioridad Predeterminada',
    os_priorityLow: 'Baja',
    os_priorityMedium: 'Media',
    os_priorityHigh: 'Alta',
    os_priorityCritical: 'Crítica',
    os_defaultDuration: 'Duración Estimada Predeterminada (horas)',
    // Service Types Tab
    os_serviceTypesTitle: 'Tipos de Servicio',
    os_serviceTypesSubtitle: 'Define y categoriza los tipos de servicios que ofrece tu organización.',
    os_addServiceType: 'Agregar Nuevo Tipo de Servicio',
    os_serviceTypeName: 'Nombre',
    os_serviceTypeNameRequired: 'Nombre *',
    os_serviceTypeCategory: 'Categoría',
    os_serviceTypeDescription: 'Descripción',
    os_serviceTypeAdd: 'Agregar',
    os_serviceTypeAdding: 'Agregando...',
    os_serviceTypeSave: 'Guardar',
    os_serviceTypeSaving: 'Guardando...',
    os_serviceTypeCancel: 'Cancelar',
    os_serviceTypeDelete: 'Eliminar',
    os_serviceTypeEdit: 'Editar',
    os_serviceTypeActive: 'Activo',
    os_serviceTypeInactive: 'Inactivo',
    os_serviceTypeSearch: 'Buscar tipos de servicio...',
    os_serviceTypeAllCategories: 'Todas las Categorías',
    os_serviceTypeNewCategory: '+ Nueva categoría...',
    os_serviceTypeEnterCategory: 'Ingresa el nombre de la categoría',
    os_serviceTypeNoResults: 'No se encontraron tipos de servicio.',
    os_serviceTypeErrNameRequired: 'El nombre del tipo de servicio es obligatorio.',
    // Invitations Tab
    os_invitationsTitle: 'Gestión de Invitaciones',
    os_invitationsSubtitle: 'Invita usuarios a esta organización y gestiona las invitaciones pendientes.',
    os_totalMembers: 'Total de Miembros',
    os_pendingInvites: 'Invitaciones Pendientes',
    os_activeMembers: 'Miembros Activos',
    os_sendInvitation: 'Enviar Nueva Invitación',
    os_inviteEmailPlaceholder: 'usuario@ejemplo.com',
    os_inviteRoleOrgAdmin: 'Admin de Org',
    os_inviteRoleManager: 'Gerente',
    os_invitationRoleTechnician: 'Técnico',
    os_inviteRoleSalesperson: 'Vendedor',
    os_inviteRoleViewer: 'Observador',
    os_invite: 'Invitar',
    os_inviting: 'Invitando...',
    os_filterAll: 'Todos',
    os_filterPending: 'Pendiente',
    os_filterAccepted: 'Aceptado',
    os_filterExpired: 'Expirado',
    os_filterRevoked: 'Revocado',
    os_memberColumn: 'Miembro',
    os_roleColumn: 'Rol',
    os_invitedColumn: 'Invitado',
    os_statusColumn: 'Estado',
    os_actionsColumn: 'Acciones',
    os_resend: 'Reenviar',
    os_revoke: 'Revocar',
    os_noMembersFound: 'No se encontraron miembros.',
    os_errDuplicateInvite: 'Este correo ya tiene una invitación activa o membresía.',
    os_errInviteFailed: 'Error al enviar la invitación.',

    // ── Permission Audit Dashboard ──
    pad_title: 'Panel de Auditoría de Permisos',
    pad_subtitlePlatform: 'Monitoreo de permisos en toda la plataforma para todas las organizaciones',
    pad_subtitleOrg: 'Monitoreo de permisos de la organización y análisis de seguridad',
    pad_accessRestricted: 'Acceso Restringido',
    pad_accessRestrictedDesc: 'El Panel de Auditoría de Permisos es accesible solo para Administradores de Org y Superadministradores.',
    pad_filters: 'Filtros',
    pad_refresh: 'Actualizar',
    pad_export: 'Exportar',
    pad_clearAll: 'Limpiar todo',
    // Metrics
    pad_metricDenials: 'Denegaciones de Permisos',
    pad_metricFlagged: 'Eventos Marcados',
    pad_metricCritical: 'Alertas Críticas',
    pad_metricRoleChanges: 'Cambios de Rol',
    pad_metricTotalEvents: 'Total de Eventos',
    pad_metricUsersAffected: 'Usuarios Afectados',
    // Tabs
    pad_tabTimeline: 'Línea de Tiempo de Auditoría',
    pad_tabDenials: 'Denegaciones de Permisos',
    pad_tabMatrix: 'Matriz de Acceso',
    pad_tabAnalytics: 'Análisis',
    // Filter Panel
    pad_filterOrg: 'Organización',
    pad_filterAllOrgs: 'Todas las Organizaciones',
    pad_filterUser: 'Usuario',
    pad_filterAllUsers: 'Todos los Usuarios',
    pad_filterRole: 'Rol',
    pad_filterAllRoles: 'Todos los Roles',
    pad_filterScreen: 'Pantalla',
    pad_filterAllScreens: 'Todas las Pantallas',
    pad_filterEventType: 'Tipo de Evento',
    pad_filterAllEventTypes: 'Todos los Tipos de Evento',
    pad_filterSeverity: 'Severidad',
    pad_filterAll: 'Todos',
    pad_filterCritical: 'Crítico',
    pad_filterWarning: 'Advertencia',
    pad_filterInfo: 'Info',
    pad_filterDateRange: 'Rango de Fechas',
    pad_filterFlaggedOnly: 'Solo Marcados',
    // Event types
    pad_eventRoleChange: 'Cambio de Rol',
    pad_eventPermissionGrant: 'Permiso Otorgado',
    pad_eventPermissionRevoke: 'Permiso Revocado',
    pad_eventAccessAttempt: 'Intento de Acceso',
    pad_eventLoginAnomaly: 'Anomalía de Acceso',
    // Timeline
    pad_auditTimeline: 'Línea de Tiempo de Auditoría',
    pad_events: 'eventos',
    pad_noEvents: 'No se encontraron eventos de auditoría',
    pad_noEventsHint: 'Intenta ajustar tus filtros',
    pad_previous: 'Anterior',
    pad_next: 'Siguiente',
    pad_pageOf: 'Página {page} de {total}',
    pad_flagged: 'Marcado',
    pad_performedBy: 'Realizado Por',
    pad_action: 'Acción',
    pad_ipAddress: 'Dirección IP',
    pad_before: 'Antes',
    pad_after: 'Después',
    pad_details: 'Detalles',
    pad_severityCritical: 'Crítico',
    pad_severityWarning: 'Advertencia',
    pad_severityInfo: 'Info',
    // Denials
    pad_permissionDenials: 'Denegaciones de Permisos',
    pad_records: 'registros',
    pad_noDenials: 'Sin denegaciones de permisos',
    pad_allAuthorized: 'Todos los intentos de acceso fueron autorizados',
    // Matrix
    pad_userAccessMatrix: 'Matriz de Acceso de Usuarios',
    // Anomaly Detection
    pad_anomalyDetection: 'Detección de Anomalías',
    pad_noAnomalies: 'No se detectaron anomalías',
    pad_normalPatterns: 'Todos los patrones de acceso parecen normales',
    // Role Changes
    pad_recentRoleChanges: 'Cambios de Rol Recientes',
    pad_noRoleChanges: 'Sin cambios de rol en este período',

    // ── Security & Access Control ──
    sac_title: 'Seguridad y Control de Acceso',
    sac_subtitle: 'Configura permisos de acceso al menú basados en roles',
    sac_accessRestricted: 'Acceso Restringido',
    sac_accessRestrictedDesc: 'Seguridad y Control de Acceso requiere acceso de Administrador de Org.',
    sac_advancedConfig: 'Configuración Avanzada',
    sac_roles: 'Roles',
    sac_newRole: 'Nuevo Rol',
    sac_roleNamePlaceholder: 'Nombre del rol *',
    sac_roleDescPlaceholder: 'Descripción (opcional)',
    sac_create: 'Crear',
    sac_cancel: 'Cancelar',
    sac_creating: 'Creando...',
    sac_noRoles: 'No hay roles configurados aún.',
    sac_createFirstRole: 'Crea tu primer rol',
    sac_selectRolePrompt: 'Selecciona o crea un rol para configurar permisos',
    sac_colScreen: 'Pantalla',
    sac_colView: 'Ver',
    sac_colCreate: 'Crear',
    sac_colEdit: 'Editar',
    sac_colDelete: 'Eliminar',
    sac_errLoadFailed: 'Error al cargar',
    sac_errSaveFailed: 'Error al guardar',
    sac_roleCreatedSuccess: 'Rol "{name}" creado exitosamente',
    sac_errCreateRole: 'Error al crear el rol',
    sac_errOrgNotFound: 'Organización no encontrada. Por favor actualiza e intenta de nuevo.',
    // Role Selector
    sac_orgRoles: 'Roles de la Organización',
    sac_rolesConfigured: '{n} roles configurados',
    sac_access: 'acceso',
    sac_noRolesFound: 'No se encontraron roles. Crea roles en Gestión de Usuarios y Roles.',
    // Access Summary Bar
    sac_fullAccess: 'Acceso Completo',
    sac_broadAccess: 'Acceso Amplio',
    sac_moderateAccess: 'Acceso Moderado',
    sac_limitedAccess: 'Acceso Limitado',
    sac_noAccess: 'Sin Acceso',
    sac_ofMenus: 'de {total} menús',
    sac_customRole: 'Rol personalizado',
    // Role Access Grid
    sac_accessible: 'accesible',
    sac_allOn: 'Todo Activado',
    sac_allOff: 'Todo Desactivado',
    sac_partial: 'Parcial',
    sac_allowed: 'Permitido',
    sac_denied: 'Denegado',
    sac_saved: 'Guardado',
    // Menu categories
    sac_catSales: 'Ventas',
    sac_catOperations: 'Operaciones',
    sac_catFinance: 'Finanzas',
    sac_catDataManagement: 'Gestión de Datos',
    sac_catSettings: 'Configuración',

    // ── Audit Log ──
    al_title: 'Registro de Auditoría',
    al_subtitle: 'Historial completo de todas las actividades del sistema, cambios y acciones de usuarios.',
    al_accessRestricted: 'Acceso Restringido',
    al_accessRestrictedDesc: 'No tienes permiso para ver el Registro de Auditoría.',
    al_compliance: 'Cumplimiento',
    al_filters: 'Filtros',
    al_exportPDF: 'PDF',
    al_exportCSV: 'Exportar CSV',
    al_activityTimeline: 'Línea de Tiempo de Actividad',
    al_events: 'eventos',
    al_live: 'En Vivo',
    al_loading: 'Cargando registros de auditoría...',
    al_noEvents: 'No se encontraron eventos de auditoría',
    al_noEventsHint: 'La actividad aparecerá aquí a medida que se realicen acciones.',
    al_noEventsFiltered: 'Intenta ajustar tus filtros.',
    al_clearFilters: 'Limpiar todos los filtros',
    al_newEntries: '{n} nueva entrada — haz clic para actualizar',
    al_newEntriesPlural: '{n} nuevas entradas — haz clic para actualizar',
    al_refresh: 'Actualizar',
    al_clear: 'Limpiar',
    // Stats Bar
    al_statTotalEvents: 'Total de Eventos',
    al_statErrors: 'Errores',
    al_statWarnings: 'Advertencias',
    al_statFailures: 'Fallos',
    // Filter Panel
    al_searchPlaceholder: 'Buscar usuario, acción, recurso...',
    al_filterUser: 'Usuario',
    al_filterAllUsers: 'Todos los Usuarios',
    al_filterAction: 'Acción',
    al_filterAllActions: 'Todas las Acciones',
    al_filterEntityType: 'Tipo de Entidad',
    al_filterAllEntities: 'Todas las Entidades',
    al_filterSeverity: 'Severidad',
    al_filterAllSeverity: 'Toda la Severidad',
    al_filterSeverityInfo: 'Info',
    al_filterSeverityWarning: 'Advertencia',
    al_filterSeverityError: 'Error',
    al_filterSeverityCritical: 'Crítico',
    al_filterStatus: 'Estado',
    al_filterAllStatus: 'Todos los Estados',
    al_filterStatusSuccess: 'Exitoso',
    al_filterStatusFailure: 'Fallido',
    al_filterStatusPending: 'Pendiente',
    al_filterDateFrom: 'Fecha Desde',
    al_filterDateTo: 'Fecha Hasta',
    // Entry Card
    al_on: 'en',
    al_timestamp: 'Marca de Tiempo',
    al_user: 'Usuario',
    al_resourceType: 'Tipo de Recurso',
    al_ipAddress: 'Dirección IP',
    al_changeDetails: 'Detalles del Cambio',
    al_viewIn: 'Ver en Gestión de',
    al_justNow: 'ahora mismo',
    al_minutesAgo: 'hace {n}m',
    al_hoursAgo: 'hace {n}h',
    al_daysAgo: 'hace {n}d',
    al_showingOf: 'Mostrando {from}–{to} de {total}',
    al_pageOf: '{page} / {total}',

    // ── Login Page ──
    login_signIn: 'Iniciar sesión en tu cuenta',
    login_enterCredentials: 'Ingresa tus credenciales para acceder a tu cuenta.',
    login_emailAddress: 'Dirección de Correo',
    login_emailPlaceholder: 'tu@empresa.com',
    login_password: 'Contraseña',
    login_forgotPassword: '¿Olvidaste tu contraseña?',
    login_signingIn: 'Iniciando sesión...',
    login_signInBtn: 'Iniciar Sesión',
    login_requestAccess: 'Solicitar Acceso',
    login_trialCta: 'Haz clic aquí para comenzar con tu propia organización, una prueba gratuita de 30 días y prueba con tus propios datos',
    login_copyright: '© {year} ServiceJockey. Todos los derechos reservados.',
    login_emailNotConfirmed: 'Tu dirección de correo no ha sido confirmada. Contacta a tu administrador o intenta de nuevo en breve — el sistema confirmará tu cuenta automáticamente.',
    login_invalidCredentials: 'Correo o contraseña inválidos.',
    login_unexpectedError: 'Ocurrió un error inesperado. Por favor intenta de nuevo.',
    // Request Access Modal
    login_requestTitle: 'Solicitar Acceso',
    login_requestSubtitle: 'Completa el formulario y nos pondremos en contacto contigo pronto.',
    login_companyName: 'Nombre de la Empresa',
    login_companyPlaceholder: 'Empresa Ejemplo',
    login_numberOfUsers: 'Número de Usuarios',
    login_numberOfUsersPlaceholder: 'ej. 10',
    login_phoneNumber: 'Número de Teléfono',
    login_phonePlaceholder: '+1 (555) 000-0000',
    login_sending: 'Enviando...',
    login_submitRequest: 'Enviar Solicitud',
    login_requestSent: '¡Solicitud Enviada!',
    login_requestSentDesc: '¡Gracias! Hemos recibido tu solicitud de acceso y nos pondremos en contacto pronto.',
    login_close: 'Cerrar',

    // ── Forgot Password Page ──
    fp_title: '¿Olvidaste tu contraseña?',
    fp_subtitle: 'Ingresa tu dirección de correo y te enviaremos un enlace para restablecer tu contraseña.',
    fp_emailAddress: 'Dirección de Correo',
    fp_emailPlaceholder: 'tu@empresa.com',
    fp_sending: 'Enviando...',
    fp_sendResetLink: 'Enviar Enlace de Restablecimiento',
    fp_checkInbox: 'Revisa tu bandeja de entrada',
    fp_sentTo: 'Enviamos un enlace de restablecimiento de contraseña a',
    fp_didntReceive: '¿No lo recibiste? Revisa tu carpeta de spam o',
    fp_tryAgain: 'intenta de nuevo',
    fp_backToSignIn: 'Volver a Iniciar Sesión',

    // ── Reset Password Page ──
    rp_setNewPassword: 'Establecer una nueva contraseña',
    rp_chooseStrong: 'Elige una contraseña segura para tu cuenta.',
    rp_newPassword: 'Nueva Contraseña',
    rp_minChars: 'Mín. 8 caracteres',
    rp_confirmNewPassword: 'Confirmar Nueva Contraseña',
    rp_reEnter: 'Vuelve a ingresar tu contraseña',
    rp_updating: 'Actualizando...',
    rp_updatePassword: 'Actualizar Contraseña',
    rp_passwordUpdated: '¡Contraseña actualizada!',
    rp_passwordUpdatedDesc: 'Tu contraseña ha sido restablecida exitosamente. Redirigiendo al inicio de sesión…',
    rp_invalidLink: 'Enlace inválido o expirado',
    rp_invalidLinkDesc: 'Este enlace de restablecimiento de contraseña es inválido o ha expirado. Por favor solicita uno nuevo.',
    rp_requestNewLink: 'Solicitar Nuevo Enlace',
    rp_backToSignIn: 'Volver a Iniciar Sesión',
    rp_errTooShort: 'La contraseña debe tener al menos 8 caracteres.',
    rp_errNoMatch: 'Las contraseñas no coinciden.',
    rp_errUpdateFailed: 'Error al actualizar la contraseña. Por favor intenta de nuevo.',
    rp_errUnexpected: 'Ocurrió un error inesperado. Por favor intenta de nuevo.',

    // ── Org Settings Branding Tab ──
    os_brandingSettings: 'Configuración de Marca',
    os_brandingSettingsDesc: 'Personaliza los colores de tu marca e identidad visual en todas las pantallas',
    os_primaryBrandColor: 'Color Principal de Marca',
    os_livePreviewLabel: 'Vista Previa en Vivo',
    os_documentHeaderPreview: 'Vista Previa del Encabezado del Documento',

    // ── Org Settings Service Types Tab ──
    os_stLoading: 'Cargando tipos de servicio...',
    os_stNoResults: 'No se encontraron tipos de servicio.',
    os_stAddNew: 'Agregar Nuevo Tipo de Servicio',
    os_stName: 'Nombre *',
    os_stCategory: 'Categoría',
    os_stDescription: 'Descripción',
    os_stAdd: 'Agregar',
    os_stAdding: 'Agregando...',
    os_stSave: 'Guardar',
    os_stSaving: 'Guardando...',
    os_stCancel: 'Cancelar',
    os_stDelete: 'Eliminar',
    os_stEdit: 'Editar',
    os_stActive: 'Activo',
    os_stInactive: 'Inactivo',
    os_stSearch: 'Buscar tipos de servicio...',
    os_stAllCategories: 'Todas las Categorías',
    os_stNewCategory: '+ Nueva categoría...',
    os_stEnterCategory: 'Ingresa el nombre de la categoría',
    os_stErrNameRequired: 'El nombre del tipo de servicio es obligatorio.',

    // ── Org Settings Invitations Tab ──
    os_invLoading: 'Cargando miembros...',
    os_invSendNew: 'Enviar Nueva Invitación',
    os_invEmailPlaceholder: 'usuario@ejemplo.com',
    os_invRoleOrgAdmin: 'Admin de Org',
    os_invRoleManager: 'Gerente',
    os_invRoleTechnician: 'Técnico',
    os_invRoleSalesperson: 'Vendedor',
    os_invRoleViewer: 'Observador',
    os_invInvite: 'Invitar',
    os_invInviting: 'Invitando...',
    os_invFilterAll: 'todos',
    os_invFilterPending: 'pendiente',
    os_invFilterAccepted: 'aceptado',
    os_invFilterExpired: 'expirado',
    os_invFilterRevoked: 'revocado',
    os_invMemberCol: 'Miembro',
    os_invRoleCol: 'Rol',
    os_invInvitedCol: 'Invitado',
    os_invStatusCol: 'Estado',
    os_invActionsCol: 'Acciones',
    os_invResend: 'Reenviar',
    os_invRevoke: 'Revocar',
    os_invNoMembers: 'No se encontraron miembros.',
    os_invErrDuplicate: 'Este correo ya tiene una invitación activa o membresía.',
    os_invErrFailed: 'Error al enviar la invitación.',
    os_invSuccess: 'Invitación enviada a {email}',
    os_invTotalMembers: 'Total de Miembros',
    os_invPendingInvites: 'Invitaciones Pendientes',
    os_invActiveMembers: 'Miembros Activos',

    // ── Settings Hub ──
    sh_title: 'Centro de Configuración',
    sh_subtitle: 'Gestiona la configuración de la organización, usuarios, facturación, seguridad y controles administrativos',
    sh_open: 'Abrir',

    // ── My Dashboard ──
    md_title: 'Mi Panel',
    md_welcome: 'Bienvenido, {name}',
    md_loading: 'Cargando tu panel...',
    md_noPersonnelTitle: 'No se Encontró Registro de Personal',
    md_noPersonnelDesc: 'Tu cuenta no está vinculada a un registro de personal. Contacta a tu gerente para configurar tu perfil.',
    md_goToMainDashboard: 'Ir al Panel Principal',
    md_myQuotes: 'Mis Cotizaciones',
    md_totalQuotes: 'Total de cotizaciones',
    md_scheduled: 'Programado',
    md_upcomingTickets: 'Tickets próximos',
    md_pending: 'Pendiente',
    md_awaitingResponse: 'En espera de respuesta',
    md_converted: 'Convertido',
    md_toTickets: 'A tickets',
    md_totalTickets: 'Total de Tickets',
    md_allTickets: 'Todos los tickets',
    md_open: 'Abierto',
    md_activeTickets: 'Tickets activos',
    md_today: 'Hoy',
    md_scheduledToday: 'Programados hoy',
    md_inProgress: 'En Progreso',
    md_activeJobs: 'Trabajos activos',
    md_assigned: 'Asignados',
    md_sevenDay: '7 Días',
    md_upcomingJobs: 'Trabajos próximos',
    md_myAssignedTickets: 'Mis Tickets Asignados',
    md_viewAllTickets: 'Ver Todos los Tickets',
    md_mySchedule: 'Mi Horario (7 días)',
    md_calendar: 'Calendario',
    md_myScheduledJobs: 'Mis Trabajos Programados',
    md_viewAll: 'Ver Todos',
    md_quickActions: 'Acciones Rápidas',
    md_upcomingSchedule: 'Próximo Calendario',
    md_escalationAlerts: 'Alertas de Escalada',
    md_escalations: '{n} escalada',
    md_escalationsPlural: '{n} escaladas',
    md_alerts: 'Alertas ({n})',
    md_notScheduled: 'Sin programar',
    md_noUpcomingJobs: 'No hay trabajos programados próximos.',
    md_noQuotesFound: 'No se encontraron cotizaciones. ¡Crea tu primera cotización!',
    md_createQuote: 'Crear Cotización',
    md_noTicketsAssigned: 'No hay tickets asignados actualmente.',
    md_yourQuotesSchedule: 'Tus cotizaciones y trabajos programados',
    md_allTicketsOverview: 'Todos los tickets y resumen del equipo',
    md_yourAssignedTickets: 'Tus tickets asignados y horario',

    // ── Org Admin First Login Checklist ──
    flc_title: 'Lista de Verificación del Primer Acceso',
    flc_subtitle: 'Completa estos pasos para preparar tu organización',
    flc_progress: '{done} de {total} pasos completados',
    flc_allDone: '¡Todo listo! Tu organización está configurada.',
    flc_dismiss: 'Cerrar Lista',
    flc_goToDashboard: 'Ir al Panel',
    flc_categoryDataVerification: 'Verificación de Datos',
    flc_categoryDataVerificationDesc: 'Confirma que los detalles de tu organización son correctos',
    flc_categoryRoleConfiguration: 'Configuración de Roles',
    flc_categoryRoleConfigurationDesc: 'Configura la estructura de tu equipo y permisos',
    flc_categoryDemoReview: 'Revisión de Datos Demo',
    flc_categoryDemoReviewDesc: 'Explora los datos de muestra y planifica tu lanzamiento',
    flc_priorityRequired: 'Requerido',
    flc_priorityRecommended: 'Recomendado',
    flc_priorityOptional: 'Opcional',
    flc_markComplete: 'Marcar completo',
    flc_markIncomplete: 'Marcar incompleto',
    flc_open: 'Abrir',

    // ── Subscription & Billing ──
    sb_title: 'Suscripción y Facturación',
    sb_subtitle: 'Gestiona tu plan de suscripción, datos de facturación y métodos de pago',
    sb_tabPlan: 'Plan',
    sb_tabBilling: 'Facturación',
    sb_tabMembers: 'Miembros',
    sb_tabWebhooks: 'Webhooks',
    sb_currentPlan: 'Plan Actual',
    sb_trialDaysLeft: '{n} días restantes en prueba',
    sb_trialExpired: 'Prueba Expirada',
    sb_suspended: 'Cuenta Suspendida',
    sb_active: 'Activo',
    sb_trialing: 'Prueba',
    sb_pastDue: 'Vencido',
    sb_cancelled: 'Cancelado',
    sb_selectPlan: 'Seleccionar Plan',
    sb_monthly: 'Mensual',
    sb_annual: 'Anual',
    sb_save20: 'Ahorra 20%',
    sb_perUserMonth: '/usuario/mes',
    sb_mostPopular: 'Más Popular',
    sb_unlimited: 'Ilimitado',
    sb_fullSuite: 'Suite Completa',
    sb_users: 'usuarios',
    sb_userCount: 'Cantidad de Usuarios',
    sb_monthlyTotal: 'Total Mensual',
    sb_savePlan: 'Guardar Plan',
    sb_saving: 'Guardando...',
    sb_saved: '¡Guardado!',
    sb_checkout: 'Suscribirse Ahora',
    sb_checkingOut: 'Cargando...',
    sb_billingName: 'Nombre de Facturación',
    sb_billingEmail: 'Correo de Facturación',
    sb_billingAddress: 'Dirección de Facturación',
    sb_saveChanges: 'Guardar Cambios',
    sb_activeMembers: 'Miembros Activos',
    sb_totalMembers: 'Total de Miembros',
    sb_syncBilling: 'Sincronizar Facturación',
    sb_syncing: 'Sincronizando...',
    sb_checkoutSuccess: '¡Suscripción activada exitosamente!',
    sb_accessRestricted: 'Acceso Restringido',
    sb_accessRestrictedDesc: 'Suscripción y Facturación solo es accesible para Administradores de Org.',

    // ── User & Role Management ──
    urm_title: 'Gestión de Usuarios y Roles',
    urm_subtitle: 'Gestiona los usuarios, roles y permisos de tu organización',
    urm_inviteUser: 'Invitar Usuario',
    urm_totalUsers: 'Total de Usuarios',
    urm_active: 'Activos',
    urm_inactive: 'Inactivos',
    urm_customRoles: 'Roles Personalizados',
    urm_tabUsers: 'Usuarios',
    urm_tabRoles: 'Roles y Permisos',
    urm_tabPreview: 'Vista Previa de Rol',
    urm_tabInheritance: 'Herencia',
    urm_tabAudit: 'Registro de Cambios de Rol',
    urm_tabPermissionAudit: 'Cambios de Permisos',
    urm_searchUsers: 'Buscar usuarios...',
    urm_allRoles: 'Todos los Roles',
    urm_roleAdmin: 'Administrador',
    urm_roleManager: 'Gerente',
    urm_roleTechnician: 'Técnico',
    urm_roleSalesperson: 'Vendedor',
    urm_roleViewer: 'Observador',
    urm_allStatuses: 'Todos los Estados',
    urm_statusActive: 'Activo',
    urm_statusInactive: 'Inactivo',
    urm_statusPending: 'Invitación Pendiente',
    urm_statusNotInvited: 'No Invitado',

    // ── Member Dashboard ──
    mdb_greeting_morning: 'Buenos días',
    mdb_greeting_afternoon: 'Buenas tardes',
    mdb_greeting_evening: 'Buenas noches',
    mdb_happeningToday: 'Esto es lo que está pasando hoy',
    mdb_addQuote: 'Agregar Cotización',
    mdb_createTicket: 'Crear Nuevo Ticket',
    mdb_openTickets: 'Tickets Abiertos',
    mdb_pendingRequests: 'Solicitudes Pendientes',
    mdb_quickAccess: 'Acceso Rápido',
    mdb_quickAccessDesc: 'Pantallas disponibles según los permisos de tu rol',
    mdb_noAccess: 'Sin pantallas disponibles',
    mdb_noAccessDesc: 'Contacta a tu administrador para solicitar acceso a pantallas específicas.',
  },
};

const LanguageContext = createContext({});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('sj_language') || 'en';
  });

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('sj_language', lang);
  }, []);

  const t = useCallback((key, vars) => {
    const dict = translations?.[language] || translations?.en;
    let str = dict?.[key] || translations?.en?.[key] || key;
    if (vars) {
      Object.entries(vars)?.forEach(([k, v]) => {
        str = str?.replace(`{${k}}`, v);
      });
    }
    return str;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
